import { usePrintShortcut } from './print/usePrintShortcut';
import { usePrintInsets } from './print/usePrintInsets';
import { PrintMapPadding } from './print/PrintMapPadding';
import { BrowserPrintNotice } from './print/BrowserPrintNotice';
import { useMapCatalogue } from './MapCatalogueProvider';
import { usePrintArtwork } from './print/usePrintArtwork';
import { Fragment, useId, useRef, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { useMap, type MapViewport } from '@wehunt/map';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { clearDraft, draftIsEmpty, loadDraft, rebaseDraft, saveDraft, type MapEditorDraft } from '../lib/mapEditorDraft';
import type { CopyableTeamMap, GeoArea, GeoLine, GeoLineKind, GeoLineStyle, MapObject, MapPinImage, PinFolder, Team, TeamMap } from '../lib/types';
import { pinDefaultColor, pinTypeVisibility } from '../lib/mapPinTypes';
import { usePinTypeCatalog } from './PinTypeCatalogProvider';
import { DEFAULT_CUSTOM_PIN_SYMBOL, type PinTypeEntry } from '../lib/pinTypeCatalog';
import { PinSymbolDialog } from './PinSymbolDialog';
import { classifyPropertyResponse, propertyOuterCount, propertyRings, propertyTraceUrl, resolvePropertyTap, type PropertyFeature } from './mapEditor/propertyTapResult';
import { geometryEditable } from './mapEditor/editorVertexTarget';
import { InlineNameEditor } from './mapEditor/InlineNameEditor';
import { MapLibreEditorCanvas } from './mapEditor/MapLibreEditorCanvas';
import {
  polylinePoints,
  arrowHeadPath,
  polylineMidpoint,
  lineLengthMeters,
  formatLineLength,
  lineStrokeWidthSvg,
  LINE_BORDER_FACTOR,
  lineDashArray,
  LINE_PICKER_COLORS,
  LINE_DEFAULT_COLOR,
  LINE_DEFAULT_WIDTH,
  LINE_MIN_WIDTH,
  LINE_MAX_WIDTH,
  LINE_WIDTH_TICKS,
  stepLineWidth,
  lineWidthFromFraction,
  lineWidthFraction,
  formatLineWidth,
} from '../lib/mapLines';
import { Icon, type IconName } from './Icon';
import { HudCard, HudHeader } from './HudCard';
import { MapPinSprite, pinSrcFor, useDPRScale } from './MapPin';
import { HiddenPinTypesChip, HIDDEN_PIN_TYPES_CHIP_STACK } from './HiddenPinTypesChip';
import { MapAppearancePanel } from './MapAppearancePanel';
import { PinFolderTree, type PinCollectionOps } from './PinFolderTree';
import { SurfaceIconButton } from './SurfaceIconButton';
import { MapToolActions } from './MapToolActions';
import { MapLayersPicker, type BaseMapInfo, type OverlayInfo } from './MapLayersPicker';
import {
  DEFAULT_MAP_APPEARANCE,
  MAP_APPEARANCE_STORAGE_KEY,
  clampAppearance,
  type MapAppearance,
} from '../lib/mapAppearance';
import { useLocalStorage } from '../lib/useLocalStorage';
import { pinLabelColors } from '../lib/mapAppearance';

// Re-exported: this used to live here, and a test plus outside callers import it
// from this module. The implementation moved to lib/mapAppearance, where the read
// only map surfaces can reach it without pulling in the whole editor.
export { pinLabelColors };
import { polygonCentroid } from '../lib/geo';
import {
  polygonCentroidOfRings,
  GROUND_HIGHLIGHT_COLOR,
  resolveHuntBorderColor,
  SUBAREA_STROKE,
  FORBIDDEN_STROKE,
} from '../lib/mapBorders';
import { fitViewToPoints, groundFramePoints, EMPTY_MAP_VIEW } from '../lib/mapSeedView';
import { GpxImportFlow } from './GpxImportFlow';
import { GpxExportPanel } from './GpxExportPanel';
import { PrintPreviewPanel } from './PrintPreviewPanel';
import { MapPrintSection } from './MapPrintSection';
import { buildMapPrintRequest } from '../lib/mapPrintPayload';
import { safePrintUrl } from '../lib/mapPrintClient';
import { usePrintPinSizes } from './print/usePrintPinSizes';
import { useMapPrintJobs } from '../lib/useMapPrintJobs';
import { baseMapBlocksPrint, groundCenterForPrint, printPropertyBorders } from '../lib/mapPrintRegion';
import { PrintFrameLayers } from './print/PrintFrameLayers';
import { MapProjectionProbe } from './print/MapProjectionProbe';
import { usePrintFrame } from './print/usePrintFrame';
import { useCustomPrintSheet, useSheetFromCamera } from './print/useCustomPrintSheet';
import type { PrintProbeLookup } from '../lib/printProbe';
import { Switch } from './Switch';
import { ConfirmDeleteModal, type ConfirmDeleteObjectType } from './ConfirmDeleteModal';
import { normalizeAreaCode, isValidAreaCode, type OmaRiistaImportResult } from '../lib/omaRiista';
import { normalizeObjektnummer, isValidObjektnummer, type SveaskogImportResult } from '../lib/sveaskog';
import { MIN_RING_POINTS } from '../lib/polygonUnion';
import { groupAreasForDisplay, type AreaDisplayItem } from '../lib/areaGrouping';
import type { ReviiriConnectResult, ReviiriClubsResult, ReviiriImportResult } from '../lib/reviiri';
import { ReviiriImportFlow } from './ReviiriImportFlow';

type Pt = [number, number];
type GeoPt = { lat: number; lon: number };

/** The container the editor's opening view is fitted against, before the real one is measured. */
const EDITOR_SEED_VIEWPORT = { w: 1100, h: 600 };

/** Where the editor's map opens: framed on the border, or on a ground's bounding
 *  box when its polygon is offloaded and only the box came down, or the regional
 *  overview when there is neither. Pins never set the frame — they are content
 *  drawn on the ground, not a definition of where it is, and one can sit far from
 *  the ground. The ring-else-bbox choice is shared with the overview map via
 *  {@link groundFramePoints}. */
export function seedEditorView(boundaryAreas: readonly GeoArea[]): { center: GeoPt; zoom: number } {
  return (
    fitViewToPoints(groundFramePoints(boundaryAreas), EDITOR_SEED_VIEWPORT.w, EDITOR_SEED_VIEWPORT.h, {
      minZ: 5,
      maxZ: 14,
      // A border collapsed to a point says where to look but not how far out.
      noExtentZoom: 12,
    }) ?? EMPTY_MAP_VIEW
  );
}
// `areaSource`/`areaGroup` mirror how mobile tags property parcels: each parcel
// picked from the cadastral overlay is saved as its own area, all sharing one
// generated `areaGroup` and marked `areaSource: "property"`, so the parcels
// round-trip as faithful individual outlines rather than one merged polygon.
/**
 * `color` is the STORED per-area colour, and absent means the record carries none.
 *
 * Absent is the normal state, not a gap to fill. A hunt border's colour comes from the
 * team, and a sub-area or forbidden area is stroked in its layer's fixed colour
 * whatever the record says — so nothing here needs a value, and inventing one has a
 * cost on the phone: a stored per-ground colour holds that ground OUT of the app's
 * union (the app tests only for the field's presence), so a colour web invented and
 * never painted was quietly quilting the app's map.
 */
type Border = { id: string; name: string; color?: string; rings: GeoPt[][]; outerCount?: number; areaSource?: string; areaGroup?: string; coordinateSource?: string };

const BORDER_COLORS = ["#00D6E9", "#10A911", "#3E68FF", "#4FE400", "#8A4D38", "#BE0000", "#FF9945", "#FFB900"];

/** How long the transient property-tap notice stays on screen (ms). */
const PROPERTY_NOTICE_TIMEOUT_MS = 4000;

/** Same contract as {@link Border}: absent `color` means the record carries none. */
type Area = { id: string; name: string; color?: string; rings: GeoPt[][]; outerCount?: number; areaSource?: string; areaGroup?: string; coordinateSource?: string };

/** Editor layers that a copied map merges into. */
export interface MapCopyTarget {
  borders: Border[];
  subAreas: Area[];
  forbiddenAreas: Area[];
  objects: MapObject[];
  lines: GeoLine[];
}

/** How many steps back one editing session keeps. Generous; a snapshot is cheap
 *  (references into immutable state), and the history dies with the tool. */
const HISTORY_LIMIT = 50;

/**
 * CLUB-576: what the Delete row says, per object type. Each language writes its
 * own seven strings rather than interpolating a noun into "Delete {type}" —
 * German puts the verb last and French needs the article's gender, so a template
 * would read as machine translation in half the locales.
 */
const DELETE_LABEL_KEY = {
  border: "toolActions.deleteBorder",
  subarea: "toolActions.deleteSubArea",
  forbidden: "toolActions.deleteForbidden",
  line: "toolActions.deleteLine",
  arrow: "toolActions.deleteArrow",
  trail: "toolActions.deleteTrail",
} as const;

/** The colour the editor's canvas strokes a sub-area / forbidden area with. */
const LAYER_STROKE = { subarea: SUBAREA_STROKE, forbidden: FORBIDDEN_STROKE } as const;

/**
 * CLUB-576: the accent for an object being edited — the colour it is DRAWN with
 * on the map, which is the only thing a user can connect the panel to.
 *
 * A line is stroked in its own stored colour, so that colour is its accent. A hunt
 * border resolves through the shared precedence, because the team's colour is what it
 * is actually drawn in. Sub-areas and forbidden areas ignore the record entirely: the
 * canvas strokes them a fixed magenta and red whatever it says — reading the record
 * there is how a sub-area panel came to be headed blue while its shape on the map was
 * magenta.
 */
function drawnColour(
  kind: "border" | "subarea" | "forbidden" | "line",
  target: { color?: string } | null,
  teamColor?: string,
): string {
  // A hunt border is drawn by the shared precedence — the record's own colour, then
  // the team's, then the default — so a panel accent cannot disagree with the shape on
  // the map. Reading the record alone is what headed a panel in one colour while its
  // border was drawn in another.
  if (kind === "border") return resolveHuntBorderColor({ areaColor: target?.color, teamColor });
  if (kind === "line") return target?.color ?? LINE_DEFAULT_COLOR;
  return LAYER_STROKE[kind];
}

/**
 * Whether a border/sub-area/forbidden polygon can be selected by a click.
 *
 * Selection is blocked ONLY while the user is actively drawing a shape,
 * editing a polygon's vertices, or placing a pin. It is deliberately NOT
 * gated on which layer tab is active: clicking any polygon selects it and the
 * parent `onPolygonSelect` switches to that polygon's own tab. Mirrors how a
 * border is tapped on mobile (a point hit-test on the hunting-ground map,
 * independent of any mode).
 *
 * Borders used to add an `activeTab === "border"` gate that the sub-area and
 * forbidden layers never had, so a border was unselectable from any other tab
 * — the reported "property borders cannot be selected" bug. All three layers
 * now share this one rule.
 */
export function isPolygonSelectable(opts: {
  drawing: boolean;
  vertexEditing: boolean;
  placeMode: boolean;
}): boolean {
  return !opts.drawing && !opts.vertexEditing && !opts.placeMode;
}


/**
 * CLUB-188: append a copied hunting-ground map onto the current editor layers as
 * fresh, independent objects (new ids + cloned geometry). Additive — the copied
 * boundary/sub-areas/forbidden/pins/lines are added alongside anything already
 * drawn, and because the ids are new they save as new records in the target (a
 * one-time copy, not a live link — editing the copy never touches the source).
 *
 * CLUB-528: lines and arrows are copied, but trails are not. A trail is a
 * recorded GPS track — a record of where someone actually walked on the
 * *source* ground — and copying it onto another team's map would turn that
 * record into fiction. Lines and arrows are hand-drawn annotations (drive
 * directions, boundaries between posts) and are the whole point of copying a
 * map.
 *
 * A GPX import goes through here too, and is the one source whose trails are
 * kept (`keepTrails`): the file is the user's own recording of this ground, so
 * its untyped tracks and routes are exactly the trails it means to add.
 */
export function appendCopiedMap(
  prev: MapCopyTarget,
  src: CopyableTeamMap,
  genId: (prefix: string) => string,
  { keepTrails = false }: { keepTrails?: boolean } = {},
): MapCopyTarget {
  const clonePoly = (poly: GeoPt[]): GeoPt[] => poly.map((p) => ({ lat: p.lat, lon: p.lon }));
  /**
   * One copied area becomes one record PER OUTER RING, sharing a group id.
   *
   * A copy always creates, and a created ground is stored inline — one flat ring per
   * record, which the phone also reads as one ring. So a multi-part source is still
   * split into one record per outer ring here, even though the create endpoint itself
   * has moved on: it now takes a ring list plus an `outerRingCount` and stores several
   * rings together as a single WKB blob (`coordinateSource: "storage"`).
   *
   * Splitting is not a workaround, it is how the platform already stores a multi-part
   * ground: one record per parcel sharing an `areaGroup`, which every renderer then
   * unions back into one shape. So the copy round-trips through the same shape the
   * source came from.
   *
   * HOLES ARE STILL NOT CARRIED, even though the create endpoint could now accept them
   * on the wire. What blocks it now is what accepting them would commit the copy to: a
   * multi-ring record only reaches the API by becoming storage-backed, and a
   * storage-backed ground is immutable on the update path — it refuses to reshape a
   * blob back into an inline ring or the reverse. A copied boundary or sub-area is
   * exactly the kind of record the receiving team expects to keep editing (drag a
   * vertex, redraw a corner), so trading that editability away for a carried hole is
   * the wrong trade here, and the split-with-dropped-holes shape stays. On the
   * FORBIDDEN layer the trade reads the other way — see below — which is why carrying
   * holes on that layer is the open follow-up, not a rejected idea.
   *
   * WHICH WAY the loss cuts depends on the layer, and it is not the same direction on
   * all three. On a boundary or a sub-area, dropping a hole makes excluded ground part
   * of the area: a copied ground with a lake arrives with the lake huntable. On the
   * FORBIDDEN layer the sign flips — a hole there is a huntable island inside a
   * no-hunting zone, so dropping it marks legal ground as forbidden. That is the more
   * consequential direction, because a hunter reading the copied map is told they may
   * not hunt where they may. Both are stated because the first version of this comment
   * gave only the boundary reading while the helper runs on all three layers.
   */
  const toAreas = (prefix: string, fallbackName: string) =>
    (a: GeoArea): Area[] => {
      const outers = a.rings.slice(0, a.outerCount ?? a.rings.length);
      const usable = outers.filter((ring) => ring.length >= MIN_RING_POINTS);
      // Nothing drawable: the area is dropped.
      //
      // This used to keep one record carrying the source's raw ring list "so the name
      // survives", which could not work: the create endpoint rejects a body whose
      // effective ring has fewer than three coordinates — a multi-ring body is accepted
      // now, but a ring with under three points still is not — so such a record drew a
      // 400, and a 400 is not retried, so the whole copy save rejected after sibling
      // writes had already landed. That is precisely the failure this split exists to
      // prevent, reintroduced by the branch meant to avoid it.
      //
      // A ground with no ring of three points has no geometry to copy and would render
      // as nothing on every surface, so what was being preserved was a name attached to
      // an invisible area. Losing that is worth a copy that completes.
      if (usable.length === 0) return [];
      // One shared group so the parts re-merge on every surface. An existing group id
      // is kept — a copied property selection stays one selection — and a fresh one is
      // minted only when the source had none and there is more than one part to bind.
      const areaGroup =
        a.areaGroup ?? (usable.length > 1 ? "g" + Math.random().toString(36).slice(2, 9) : undefined);
      return usable.map((ring, i) => ({
        id: genId(prefix),
        name: a.name || (usable.length > 1 ? `${fallbackName} ${i + 1}` : fallbackName),
        // A colourless area stays colourless. The fallbackColor parameter is deleted
        // rather than defaulted: a parameter that can carry a colour is what makes the
        // stamp re-addable, and every layer already draws correctly without one.
        color: a.color,
        rings: [clonePoly(ring)],
        outerCount: 1,
        // Preserve property provenance + grouping when copying a map, matching the
        // load and save paths (a copied parcel stays a grouped "property" ground).
        areaSource: a.areaSource,
        areaGroup,
      }));
    };
  return {
    borders: [...prev.borders, ...src.boundaryAreas.flatMap(toAreas("b", "Border"))],
    subAreas: [...prev.subAreas, ...src.subAreaPolygons.flatMap(toAreas("sa", "Sub-area"))],
    forbiddenAreas: [
      ...prev.forbiddenAreas,
      ...src.forbiddenPolygons.flatMap(toAreas("fa", "Forbidden area")),
    ],
    objects: [
      ...prev.objects,
      // A copied pin keeps its note and its pictures. The pictures still name the
      // source pin's stored objects, so the pin records where it came from and
      // the save duplicates them under fresh ids before creating it (two pins
      // sharing one object would let either delete the other's picture).
      ...src.objects.map((o) => ({
        ...o,
        id: genId("p"),
        ...(o.photos?.length ? { copiedFromPinId: o.id } : {}),
      })),
    ],
    lines: [
      ...prev.lines,
      ...src.lines
        .filter((l) => keepTrails || l.kind !== "trail")
        .map((l) => ({ ...l, id: genId("l"), points: clonePoly(l.points) })),
    ],
  };
}
type EditState = TeamMap & { borders: Border[]; subAreas: Area[]; forbiddenAreas: Area[]; lines: GeoLine[] };

/**
 * `state`'s editor-shaped layers (`borders`/`subAreas`/`forbiddenAreas`), as
 * the wire shape both the save endpoint and the print server read
 * (`boundaryAreas`/`subAreaPolygons`/`forbiddenPolygons`). One mapping, used
 * by `handleSave` and by the print request builder, so the two can never
 * independently drift on what a stored/printed area carries.
 *
 * The whole ring list goes back, not the first ring: for a stored outline
 * this is the echo that lets the API preserve it, and truncating it here is
 * exactly the write that used to destroy every part but one. `color` is
 * carried too — omitting it made the payload unable to ECHO a colour only
 * the app can author, and the API merged that absence as an explicit null
 * over the stored value.
 */
// No explicit return-type annotation: the object literal's own inferred type
// (every field a concrete array, never `| undefined`) is what lets this
// satisfy `onSave`'s narrower parameter type below AND `TeamMap` — annotating
// the return as `TeamMap` would widen `subAreaPolygons`/`forbiddenPolygons`
// back to that interface's optional field type and break the former.
function editStateToTeamMap(state: EditState) {
  const toArea = (a: Border | Area) => ({
    id: a.id,
    name: a.name,
    color: a.color,
    rings: a.rings,
    outerCount: a.outerCount,
    areaSource: a.areaSource,
    areaGroup: a.areaGroup,
    coordinateSource: a.coordinateSource,
  });
  // The editor-only layer arrays are destructured OUT of the spread: they
  // duplicate the ring data already mapped into the wire fields below, and a
  // consumer that serialized the whole object would silently double the
  // payload with fields the API never reads.
  const { borders, subAreas, forbiddenAreas, ...teamMap } = state;
  return {
    ...teamMap,
    boundaryAreas: borders.map(toArea),
    subAreaPolygons: subAreas.map(toArea),
    forbiddenPolygons: forbiddenAreas.map(toArea),
    objects: teamMap.objects,
    lines: teamMap.lines ?? [], // CLUB-187
  };
}

/**
 * CLUB-576: everything an editing tool can change in one step. All four are held
 * immutably (always replaced, never mutated), so a snapshot is just the current
 * references — no cloning needed.
 */
type EditSnapshot = {
  state: EditState;
  drawPoints: GeoPt[];
  selectedParcels: Set<string>;
  sessionIds: string[];
  dirty: boolean;
};

type LayerId = "border" | "pins" | "subareas" | "forbidden" | "lines" | "arrows" | "trails";
type BorderMode = "idle" | "draw" | "properties";
type AreaMode = "idle" | "draw" | "properties";
type LineMode = "idle" | "draw"; // CLUB-187
type VertexEdit = { kind: "border" | "subarea" | "forbidden" | "line"; id: string } | null;
type PlaceMode = { type: string; label: string; multi?: boolean; customSymbol?: string } | null;
// `ids` is set only for a property-group delete: it removes every member
// parcel of the group in one action (the single-item path leaves it undefined
// and deletes just `id`).
type PendingDelete = { type: ConfirmDeleteObjectType; id: string; name?: string; ids?: string[] } | null;
// The line draft carries the app's full style set (name/colour/style/width)
// so the draw HUD can edit them before the line is committed.
type LineDraft = { id: string; kind: GeoLineKind; name: string; color: string; width: number; style: GeoLineStyle };

const VERTEX_KIND_TO_DELETE_TYPE: Record<string, ConfirmDeleteObjectType> = {
  border: 'border',
  subarea: 'subArea',
  forbidden: 'forbiddenArea',
};

// Web Mercator Y fraction for a latitude.

const LAYER_TABS: { id: LayerId; label: string; icon: IconName }[] = [
  { id: "border", label: "Border", icon: "shield" },
  { id: "pins", label: "Map pins", icon: "pin" },
  { id: "subareas", label: "Sub-areas", icon: "subarea" },
  { id: "forbidden", label: "Forbidden", icon: "forbidden-area" },
  { id: "lines", label: "Lines", icon: "edit" },
  { id: "arrows", label: "Arrows", icon: "arrow-right" },
  // CLUB-528: trails are recorded (GPX import / a recorded drive), never
  // drawn — see the `kind !== "trail"` guard on LinesPanel's Add button.
  { id: "trails", label: "Trails", icon: "edit" },
];

const PIN_COLORS = [
  "#10A911", "#3E68FF", "#FF681F", "#B60B1E",
  "#8B572A", "#3a85d6", "#a81e8c", "#4a4a4a",
  "#FFD700", "#3a6a3a", "#c0531f", "#4D4052",
];

const IMPORT_SOURCES = [
  { id: "sveaskog", label: "Sveaskog", sub: "Swedish state forest properties", glyph: "S", color: "#3a8b46" },
  { id: "omariista", label: "Oma riista", sub: "Finnish hunters' service", glyph: "O", color: "#a3651b" },
  { id: "reviiri", label: "Reviiri", sub: "Sign in to import your boundary", glyph: "R", color: "#a81e8c" },
  { id: "gpx", label: "GPX import", sub: "Upload a .gpx file from your device", glyph: "↗", color: "#3a85d6" },
  { id: "copy", label: "Copy hunting ground map", sub: "Reuse a map from another team", glyph: "⎘", color: "#6b7268" },
];

/** Where the editor's map is looking: the centre and the tile zoom. */
export interface MapEditorView {
  center: { lat: number; lon: number }
  zoom: number
}

const EMPTY_BASE_MAPS: BaseMapInfo[] = [];
const EMPTY_OVERLAY_MAPS: OverlayInfo[] = [];

export interface MapEditorPageProps {
  team: Team
  onBack: () => void
  /**
   * The view to open on, instead of the one fitted to the border. The editor
   * keeps its centre and zoom in state seeded once per mount, so a caller that
   * remounts it (to get freshly saved server state into its snapshot state)
   * uses this to put the user back where they were working. Leave it unset to
   * open on the fitted view — which is what a caller wants after an import,
   * where there is new geometry to frame.
   */
  initialView?: MapEditorView
  /** Reports the current view as the user pans and zooms, so a caller can hand
      it back as `initialView` on a later mount. */
  onViewChange?: (view: MapEditorView) => void
  onSave?: (state: {
    boundaryAreas: GeoArea[]
    subAreaPolygons: GeoArea[]
    forbiddenPolygons: GeoArea[]
    objects: MapObject[]
    lines: GeoLine[]
    /**
     * CLUB-676: the ground's pin folders. The editor always sends these, so a
     * Save cannot drop work done in the collection panel.
     *
     * Optional so that adding it did not break every existing caller — in
     * production folders are written separately from the map anyway, and a host
     * that already handles them that way can ignore this half of the payload.
     */
    folders?: PinFolder[]
  }) => Promise<void>
  /** CLUB-188: teams whose map can be copied into this one (excludes the current
      team). When present + non-empty, Import Map → "Copy hunting ground map" is
      enabled. */
  copySources?: { id: string; name: string }[]
  /** Fetch a source team's copyable map. Resolve with its layers; reject to show
      the failure in the copy panel. */
  onLoadCopySource?: (teamId: string) => Promise<CopyableTeamMap>
  /** CLUB-190: import an Oma Riista hunting area by its 10-char code. When
      present, Import Map → "Oma Riista" is enabled — regardless of where the map
      is centred, since a hunter may import a Finnish area from anywhere.
      Resolve with the outcome; reject for a generic failure. */
  onImportOmaRiista?: (areaCode: string) => Promise<OmaRiistaImportResult>
  /** Called once the Oma Riista panel is DISMISSED after an import that actually
      changed the ground — the caller's cue to pull the new boundary in.
      Deliberately not fired when the import resolves: the caller refreshes by
      remounting this editor, which would tear the panel down before the user
      could read its confirmation (they reported the panel simply vanishing). */
  onOmaRiistaImported?: () => void
  /** CLUB-189: import a Sveaskog boundary by objektnummer. Its presence enables
      Import Map → "Sveaskog"; without it the entry is omitted from the list,
      the same way onImportOmaRiista gates "Oma Riista". */
  onImportSveaskog?: (objektnummer: string) => Promise<SveaskogImportResult>
  /** Called once the Sveaskog panel is dismissed after a successful import, so the
      host can refresh the ground. Deferred to dismissal for the same reason as
      onOmaRiistaImported: refreshing on resolve remounts the editor and destroys
      the panel before its confirmation can be read. */
  onSveaskogImported?: () => void
  /** CLUB-191: connect the signed-in user's Reviiri account. Together with
      onLoadReviiriClubs and onImportReviiri, its presence enables Import Map →
      "Reviiri". All three are required — a partially wired panel could sign a user in
      and then have nothing to show them. */
  onReviiriConnect?: (email: string, password: string) => Promise<ReviiriConnectResult>
  /** Lists the connected Reviiri account's clubs and maps. */
  onLoadReviiriClubs?: () => Promise<ReviiriClubsResult>
  /** Imports one club-prefixed Reviiri map id ("{clubId}/{mapId}") as a hunting ground. */
  onImportReviiri?: (mapId: string) => Promise<ReviiriImportResult>
  /** Called once the Reviiri panel is DISMISSED after an import that changed the
      ground — same deferral as onOmaRiistaImported, and for the same reason: the
      caller refreshes by remounting this editor, which would tear the panel down
      before its confirmation could be read. */
  onReviiriImported?: () => void
  /** Returns a fresh Firebase ID token for the Tanger-backed `/api/maps` +
      `/api/properties` calls. The backend forwards it as the Tanger `firebase`
      login, which is what unlocks the property (Fastigheter) overlay + the
      tap-to-select property lookup — an anonymous request only gets the base
      maps. Omit in contexts without auth (e.g. the dev/prototype server serves
      those endpoints unauthenticated). */
  getAuthToken?: () => Promise<string | null>
  /**
   * The print server's probe for a point — which CRS a sheet centred there is
   * composed in and what prints there. Supplied by the host, because reaching
   * the Estate API is a domain concern and this library is presentation-only.
   */
  getPrintProbe?: PrintProbeLookup
}

export function MapEditorPage({ team, onBack, initialView, onViewChange, onSave, copySources, onLoadCopySource, onImportOmaRiista, onOmaRiistaImported, onImportSveaskog, onSveaskogImported, onReviiriConnect, onLoadReviiriClubs, onImportReviiri, onReviiriImported, getAuthToken, getPrintProbe }: MapEditorPageProps) {
  const { t, i18n } = useTranslation('map');

  const [state, setState] = useState<EditState>(() => {
    const m = JSON.parse(JSON.stringify(team.map)) as TeamMap;
    return {
      ...m,
      borders: (m.boundaryAreas ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        // The stored colour, with NO default. Defaulting here is how a previously
        // colourless border acquired one the moment a user edited its geometry: the
        // default came in on load and went back out in the save payload. The default
        // a border is DRAWN with lives in resolveHuntBorderColor, where it paints
        // without being persisted.
        color: a.color,
        rings: a.rings,
        outerCount: a.outerCount,
        areaSource: a.areaSource,
        areaGroup: a.areaGroup,
        coordinateSource: a.coordinateSource,
      })),
      subAreas: (m.subAreaPolygons ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        // The stored colour, not the layer's stroke. Discarding it here is what made
        // the save payload unable to echo a colour only the app can author, and the
        // canvas strokes a sub-area in its own magenta regardless of this value.
        color: a.color,
        rings: a.rings,
        outerCount: a.outerCount,
        areaSource: a.areaSource,
        areaGroup: a.areaGroup,
        coordinateSource: a.coordinateSource,
      })),
      forbiddenAreas: (m.forbiddenPolygons ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        // As for sub-areas above: the stored colour, not the layer's stroke.
        color: a.color,
        rings: a.rings,
        outerCount: a.outerCount,
        areaSource: a.areaSource,
        areaGroup: a.areaGroup,
        coordinateSource: a.coordinateSource,
      })),
      lines: m.lines ?? [], // CLUB-187
    };
  });
  const [activeTab, setActiveTab] = useState<LayerId>("border");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Transient feedback for a property tap that resolved to no selectable parcel
  // (e.g. over water) or failed — the Tanger proxy can't distinguish the two,
  // and both used to be a silent no-op.
  const [propertyNotice, setPropertyNotice] = useState<string | null>(null)
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)

  // ── Unsaved work survives a lost tab ──────────────────────────────────────
  // Edits live in memory until Save, so a closed tab, a reload or a crash lost
  // them all. They are now also kept as a local draft (see mapEditorDraft.ts),
  // which the next opening of this map offers to restore. `baseRef` is the map
  // as last loaded or saved: a restore replays only the changes made since it.
  const baseRef = useRef<EditState>(state);
  const [pendingDraft, setPendingDraft] = useState<MapEditorDraft<EditState> | null>(() => {
    const draft = loadDraft<EditState>(team.id);
    if (draft && draftIsEmpty(state, draft)) {
      clearDraft(team.id);
      return null;
    }
    return draft;
  });
  const latestRef = useRef({ state, dirty: false, discarded: false });
  // True while a Save is in flight. The route answers a successful save by
  // re-keying this editor (that is how saved server state reaches it), and it
  // does so BEFORE onSave resolves: the old editor unmounts while still dirty
  // and the new one mounts reading the draft. So the draft is cleared when the
  // save starts, nothing writes it while the save runs, and a failed save puts
  // it back. Without this, every save was followed by an offer to restore the
  // changes that had just been saved.
  const savingRef = useRef(false);
  // Debounced: a drag or a typed name writes once, not on every change. Held
  // off while a stored draft awaits the user's answer, so it is not overwritten.
  useEffect(() => {
    latestRef.current = { ...latestRef.current, state, dirty };
    if (pendingDraft) return;
    if (!dirty) {
      clearDraft(team.id);
      return;
    }
    const timer = window.setTimeout(() => {
      if (!savingRef.current) saveDraft(team.id, baseRef.current, state);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [state, dirty, pendingDraft, team.id]);
  useEffect(() => {
    // Whatever the debounce has not written yet is written now: on leaving the
    // page (with the browser's own "leave site?" warning) and on leaving the
    // editor some other way, such as the sidebar.
    const flush = () => {
      const { state: s, dirty: d, discarded } = latestRef.current;
      if (d && !discarded) saveDraft(team.id, baseRef.current, s);
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!latestRef.current.dirty || latestRef.current.discarded) return;
      flush();
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Unmounted mid-save is the success path's re-key (see savingRef): the
      // save owns the draft until it settles.
      if (!savingRef.current) flush();
    };
  }, [team.id]);
  const restoreDraft = () => {
    if (!pendingDraft) return;
    setState((current) => rebaseDraft(current, pendingDraft));
    setDirty(true);
    setPendingDraft(null);
  };
  const discardDraft = () => {
    clearDraft(team.id);
    setPendingDraft(null);
  };
  // The opening view frames everything the editor draws — the border *and* the
  // pins. The border alone is not a stand-in for it: a ground can be one
  // property boundary tens of metres across while its pins sit tens of
  // kilometres out, and a view fitted to the border then opens with every pin
  // off screen. This is the page a hunter opens to find those pins, so an
  // off-screen pin here reads as a pin that was never saved.
  //
  // Seeded against an assumed 1100x600 container: this runs before the real one
  // has been measured, and the editor is a full-width page.
  const openingView = useState(
    () => initialView ?? seedEditorView(team.map.boundaryAreas ?? []),
  )[0];

  // Report the view outward so a caller can restore it on a later mount. The
  // canvas owns the live camera (MapLibre) from mount onward, so this now
  // reports ITS camera via `onViewChange`/`handleCanvasViewChange` below —
  // once on load and after every pan/zoom — rather than an effect keyed off
  // mapCenter/tileZoom state, which no longer changes after mount. Ref-held
  // so panning does not need this callback's identity to stay stable.
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;

  // The live camera, held three ways because its readers want different things
  // from it — and all three are the same numbers, so they are set together.
  //
  // `liveZoom` is STATE: the zoom buttons' disabled state renders off it.
  //
  // `liveViewRef` is a REF holding the whole view, because its readers ask for
  // it at the moment they run rather than during render — the print request
  // builder on a click, and the seed `printView` takes when the print panel
  // opens. Re-rendering the page on every pan to serve a read that happens on
  // a click would fight the pan for nothing.
  //
  // `printView` is that same view as STATE, because the print frame's corners
  // are DERIVED from the centre: the frame drawn on the canvas can only follow
  // a pan if the page re-renders (this mirrors `TeamMapPrintView`'s own `view`
  // state, and costs one render per gesture, not per frame — the canvas
  // reports on move end only). That render only buys anything while the frame
  // is on screen, so it is kept up to date only while the panel is open, and
  // seeded from the ref each time it opens.
  const [liveZoom, setLiveZoom] = useState(openingView.zoom);
  const liveViewRef = useRef<MapViewport>({ center: openingView.center, zoom: openingView.zoom });
  const [printView, setPrintView] = useState<MapViewport>({ center: openingView.center, zoom: openingView.zoom });
  // `handleCanvasViewChange` is deliberately identity-stable, so it cannot
  // read `printOpen` state; this ref is how it knows.
  const printOpenRef = useRef(false);
  const handleCanvasViewChange = useCallback((view: MapViewport) => {
    liveViewRef.current = view;
    setLiveZoom(view.zoom);
    if (printOpenRef.current) setPrintView(view);
    onViewChangeRef.current?.(view);
  }, []);

  // Bridges the live maplibre-gl map instance out of `MapLibreEditorCanvas`
  // (via its own `onMap` prop) so the page's zoom buttons, keyboard handlers
  // and "center on" list actions can drive it directly — the canvas owns the
  // camera from mount and no longer takes it as a controlled prop.
  const mapRef = useRef<ReturnType<typeof useMap>>(null);
  const handleMapBridge = useCallback((map: ReturnType<typeof useMap>) => {
    mapRef.current = map;
  }, []);
  const centerMapOn = useCallback((c: { lat: number; lon: number }) => {
    mapRef.current?.jumpTo({ center: [c.lon, c.lat] });
  }, []);

  const mapCatalogue = useMapCatalogue();
  const baseMaps = mapCatalogue.data?.baseMaps ?? EMPTY_BASE_MAPS;
  const overlayMaps = mapCatalogue.data?.overlayMaps ?? EMPTY_OVERLAY_MAPS;
  const [selectedMapId,   setSelectedMapId]   = useState<string | null>(null);
  const [enabledOverlays, setEnabledOverlays] = useState<Set<string>>(new Set());
  const [layersOpen, setLayersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [appearanceRaw, setAppearance] = useLocalStorage<MapAppearance>(
    MAP_APPEARANCE_STORAGE_KEY,
    DEFAULT_MAP_APPEARANCE,
  );
  const catalog = usePinTypeCatalog();
  const appearance = clampAppearance(appearanceRaw, catalog.knownPredicate);

  // Auth header for the Tanger-backed endpoints (/api/maps, /api/properties):
  // the backend forwards the token as the Tanger `firebase` login, which is
  // what unlocks the property overlay + tap-to-select. Empty when no token
  // getter is supplied (dev/prototype servers serve these unauthenticated).
  // Ref-backed so `authHeaders` stays referentially stable across renders.
  const getAuthTokenRef = useRef(getAuthToken);
  getAuthTokenRef.current = getAuthToken;
  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = getAuthTokenRef.current ? await getAuthTokenRef.current() : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Resolve the selected entry during render so the tile source and print
  // flavour always describe the same catalogue version, including account and
  // language changes. Keep the user's choice when that id is still available.
  const selectedBaseMap = baseMaps.find((map) => map.id === selectedMapId) ?? baseMaps[0];
  const tileInfo = useMemo(() => {
    if (selectedBaseMap) {
      return { url: selectedBaseMap.url, generation: selectedBaseMap.generation, minZ: selectedBaseMap.minZ, maxZ: selectedBaseMap.maxZ, widths: selectedBaseMap.widths ?? [] };
    }
    const data = mapCatalogue.data;
    return data?.url ? { url: data.url, generation: data.generation ?? 1, minZ: data.minZ ?? 1, maxZ: data.maxZ ?? 19, widths: data.widths ?? [] } : null;
  }, [selectedBaseMap, mapCatalogue.data]);

  // The tile catalogue's own advertised bounds, for the zoom buttons'
  // disabled state. MapLibre's own hard camera limits are fixed inside
  // `MapLibreEditorCanvas` (1..19, independent of the catalogue) — this is
  // narrower, and about not inviting a zoom past where the base map actually
  // has imagery, the same floor/ceiling the old canvas offered.
  const tileZoomMin = tileInfo?.minZ ?? 1;
  const tileZoomMax = tileInfo?.maxZ ?? 19;
  const zoomIn = () => mapRef.current?.zoomIn();
  const zoomOut = () => mapRef.current?.zoomOut();

  // Import source picker + GPX flow
  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [gpxOpen, setGpxOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [omaRiistaOpen, setOmaRiistaOpen] = useState(false);
  // Set when an Oma Riista import actually changed the ground, cleared when the
  // caller has been told. A ref rather than state: nothing renders from it, and it
  // must survive the re-render that showing the confirmation causes.
  const omaRiistaChangedRef = useRef(false);
  // Whether an Oma Riista session is currently open — set in onPick below,
  // cleared here. Distinguishes, when an in-flight import resolves, between
  // "some panel is open to defer the confirmation to" (set the changed flag,
  // closeOmaRiista drains it on whichever close fires) and "the panel that
  // started this import is already closed and nothing reopened it" (no future
  // close exists to drain the ref, so the onImport wrapper below must deliver
  // the refresh immediately instead). Mirrors sveaskogOpenRef.
  const omaRiistaOpenRef = useRef(false);
  // The only place the Oma Riista panel actually closes: its own header X, its
  // footer Cancel/Done, and closeImportFlows below all end up here, so a
  // pending "changed" flag reaches the caller the same way whichever one
  // fired. Closing mid-import must not swallow an import that succeeded — the
  // header stays live during a submit for the same reason it does on the
  // Sveaskog panel: nothing in the fetch chain times out and these panels have
  // no backdrop or Escape, so removing the exit would trap the user on a hung
  // request. Draining here is what makes using that exit safe.
  const closeOmaRiista = () => {
    omaRiistaOpenRef.current = false;
    setOmaRiistaOpen(false);
    if (omaRiistaChangedRef.current) {
      omaRiistaChangedRef.current = false;
      onOmaRiistaImported?.();
    }
  };
  const [sveaskogOpen, setSveaskogOpen] = useState(false);
  // Set when a Sveaskog import actually changed the ground, cleared once the
  // caller has been told. A ref rather than state, for the same reason as
  // omaRiistaChangedRef above.
  const sveaskogChangedRef = useRef(false);
  // Whether a Sveaskog session is currently open — set in onPick below,
  // cleared here. Distinguishes, when an in-flight import resolves, between
  // "some panel is open to defer the confirmation to" (set the changed flag,
  // closeSveaskog drains it on whichever close fires) and "the panel that
  // started this import is already closed and nothing reopened it" (no future
  // close exists to drain the ref, so the onImport wrapper below must deliver
  // the refresh immediately instead). Mirrors reviiriOpenRef.
  const sveaskogOpenRef = useRef(false);
  // The only place the Sveaskog panel actually closes: its own header X, its
  // footer Cancel/Done, and closeImportFlows below all end up here, so a
  // pending "changed" flag reaches the caller the same way whichever one
  // fired. Closing mid-import must not swallow an import that succeeded —
  // see SveaskogImportFlow's own doc comment for why the header stays live
  // during a submit.
  const closeSveaskog = () => {
    sveaskogOpenRef.current = false;
    setSveaskogOpen(false);
    if (sveaskogChangedRef.current) {
      sveaskogChangedRef.current = false;
      onSveaskogImported?.();
    }
  };
  const [reviiriOpen, setReviiriOpen] = useState(false);
  // Set when a Reviiri import actually changed the ground, cleared once the
  // caller has been told. A ref rather than state, for the same reason as
  // omaRiistaChangedRef above.
  const reviiriChangedRef = useRef(false);
  // Kept in a ref as well so an async import can see whether a newer panel session
  // opened while it was awaiting the backend, rather than reading a stale render value.
  const reviiriOpenRef = useRef(false);
  // CLUB-191: bumped by every close of the Reviiri panel — see closeReviiri
  // below, the one place that happens. The import request is owned by the
  // onImport wrapper's own closure (MapEditorPage's, not ReviiriImportFlow's),
  // so it runs to completion even after the panel that started it has
  // closed — e.g. via the header X, which (unlike the footer Cancel button)
  // isn't stage-guarded against an in-flight import. The wrapper captures the
  // session id it started under. If that id has moved by the time the request
  // resolves, the result is either delivered immediately (nothing is open) or
  // deferred to the newer panel's close so it cannot remount that live session.
  const reviiriSessionRef = useRef(0);
  const reviiriEnabled = !!(onReviiriConnect && onLoadReviiriClubs && onImportReviiri);
  // The only place the Reviiri panel actually closes: its own header X,
  // footer Cancel/Done, and closeImportFlows below (switching to another map
  // action while the panel is open) all end up here, so a maybe-pending
  // "changed" flag reaches the caller the same way regardless of which one
  // fired.
  const closeReviiri = () => {
    reviiriOpenRef.current = false;
    setReviiriOpen(false);
    reviiriSessionRef.current++;
    if (reviiriChangedRef.current) {
      reviiriChangedRef.current = false;
      onReviiriImported?.();
    }
  };
  const closeImportFlows = () => { setImportPanelOpen(false); setGpxOpen(false); setCopyOpen(false); closeOmaRiista(); closeSveaskog(); closeReviiri(); };

  // Export panel
  const [exportOpen, setExportOpen] = useState(false);

  // Print preview panel — server-rendered PDF only (ratified product call,
  // 2026-09-01, the same shape TeamMapPrintView/TeamDetailPage already
  // ship). Browser print could not survive the swap onto MapLibreEditorCanvas
  // — that canvas has no printing/printScale/printTileSize raster-capture
  // props the old FullscreenMapCanvas had — so it is converted here rather
  // than landing an intermediate commit with a broken Print button.
  const [printOpen, setPrintOpen] = useState(false);
  const printCanvasRef = useRef<HTMLDivElement>(null);
  const printPadding = usePrintInsets(printCanvasRef, printOpen);
  usePrintShortcut(() => setPrintOpen(true), !printOpen);
  // Opening the panel is what puts the print frame's centre back in step with
  // the camera: `printView` stopped following it when the panel last closed,
  // so without this seed a hunter who panned away in between would open onto a
  // frame drawn around where they used to be.
  useEffect(() => {
    printOpenRef.current = printOpen;
    if (printOpen) setPrintView(liveViewRef.current);
  }, [printOpen]);
  // Paper, orientation, scale and the two custom controls — the same choice
  // `TeamMapPrintView` offers, so the rules live in one hook rather than in a
  // copy each (see `useCustomPrintSheet`).
  const printSheet = useCustomPrintSheet();
  const {
    format: printFormat,
    orientation: printOrientation,
    scale: pdfScale,
    page: printCustomPage,
  } = printSheet;
  const [omittedPinCount, setOmittedPinCount] = useState(0);

  // The editor's LIVE (possibly unsaved) layers, shaped as a TeamMap — the
  // same field mapping `handleSave` sends the server (see
  // `editStateToTeamMap` below), reused here so the print request and the
  // save can never independently drift on how editor state maps to the wire
  // shape.
  const printMap = useMemo<TeamMap>(() => editStateToTeamMap(state), [state]);
  // Where the ground is, for framing the sheet around — read from the LIVE
  // state rather than the team's persisted `team.map`: a hunter who just
  // imported or moved a boundary in this session expects the print entry to
  // answer for what they are looking at, not the last saved snapshot.
  const groundCenter = useMemo(() => groundCenterForPrint(printMap), [printMap]);
  // The editor's entire print-entry gate: no token getter, or a ground with no
  // centre to frame a sheet around, means both Print entry points (the toolbar
  // button and the overflow-menu row) stay hidden rather than opening a panel
  // that cannot print — mirrors `TeamDetailPage`'s `MapTab`. WHERE the ground
  // sits is no longer part of it; see that comment for why the region boxes
  // went.
  const canPrint = getAuthToken !== undefined && groundCenter !== null;
  const printJobs = useMapPrintJobs({ enabled: canPrint, getAuthToken });

  // The print canvas's own measured size, for `zoomToFitFrame`'s pixel
  // arithmetic — `MapLibreEditorCanvas` has no `cssSize` of its own (unlike
  // `TeamMapView`, which reports one with every view change), so this page
  // measures the container it renders the canvas into and hands the size to
  // `usePrintFrame` itself.
  const [printContainerPx, setPrintContainerPx] = useState({ w: 1100, h: 600 });
  useEffect(() => {
    const el = printCanvasRef.current;
    if (!el) return;
    const measure = (w: number, h: number) => {
      if (w > 0 && h > 0) setPrintContainerPx((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    const rect = el.getBoundingClientRect();
    measure(rect.width, rect.height);
    const obs = new ResizeObserver(([e]) => measure(e.contentRect.width, e.contentRect.height));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // The print sheet the server will compose, plus the camera moves that keep
  // it in view — the same chain `TeamMapPrintView` runs (see `usePrintFrame`).
  // Gated on `printOpen`: this page stays mounted the whole time the hunter is
  // drawing, so with the panel closed there is nothing to frame, nothing to
  // ask Estate about, and no fit to remember.
  const {
    corners: printFrameCornersValue,
    projector: printProjector,
    probe: printProbe,
    printUnavailable,
    projectorPending: printProjectorPending,
    printError: printSheetError,
    retry: retryPrintSheet,
    focus: printFocus,
    projectRef: printProjectRef,
    unprojectRef: printUnprojectRef,
  } = usePrintFrame({
    enabled: printOpen,
    padding: printPadding,
    center: printView.center,
    zoom: printView.zoom,
    containerPx: printContainerPx,
    format: printFormat,
    orientation: printOrientation,
    scale: pdfScale,
    page: printCustomPage,
    sheetFollowsCamera: printSheet.followsCamera,
    // The one sheet the camera cannot be made to match: below 100 mm the
    // page stops shrinking with the view, so the map zooms out to it.
    refitToken: printSheet.zoomOutToken,
    getPrintProbe,
  });

  // The other direction: whichever custom control is active is recomputed from
  // what the map is showing, while the frame hook is told not to fit back to
  // it. That pair of rules is what keeps the sheet and the camera from chasing
  // each other.
  useSheetFromCamera({
    sheet: printSheet,
    enabled: printOpen,
    center: printView.center,
    zoom: printView.zoom,
    containerPx: printContainerPx,
    padding: printPadding,
    projector: printProjector,
    unprojectRef: printUnprojectRef,
  });

  // Which pins the editor is choosing not to draw. Per-viewer display
  // state, not an edit — it never enters `state`, never marks the map dirty and
  // never reaches the save payload. Hiding a pin to get it out of the way while
  // you work on the one underneath is not a change to the ground.
  //
  // Declared here, above the print request, because the sheet leaves these out
  // too: hiding stands in the list is how a team prints only the ones in use.
  const [hiddenPinIds, setHiddenPinIds] = useState<ReadonlySet<string>>(new Set());
  const [collectionHidden, setCollectionHidden] = useState(false);
  const hiddenOnCanvas = useMemo(() => {
    if (!collectionHidden) return hiddenPinIds;
    return new Set([...state.objects.map((o) => o.id), ...(state.lines ?? []).map((l) => l.id)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionHidden, hiddenPinIds, state.objects, state.lines]);

  // Declared below `usePrintFrame` rather than above it: the request is built
  // against the frame's own projector, so it cannot be assembled before the
  // frame exists.
  const printPinSizes = usePrintPinSizes(appearance);
  const [borderMode, setBorderMode] = useState<BorderMode>("idle");
  const [areaMode, setAreaMode] = useState<AreaMode>("idle");
  const [lineMode, setLineMode] = useState<LineMode>("idle");
  const [vertexEdit, setVertexEdit] = useState<VertexEdit>(null);
  const [placeMode, setPlaceMode] = useState<PlaceMode>(null);
  const printEditPending = borderMode === 'draw' || areaMode === 'draw' || lineMode === 'draw' || vertexEdit !== null || placeMode !== null;
  const printArtwork = usePrintArtwork(printMap, appearance, hiddenOnCanvas, printOpen);
  const printRequest = useMemo(() => {
    if (!printProjector || !printFrameCornersValue || printProbe?.status !== 'SUCCESS' || printSheetError || printArtwork.pending || printEditPending || mapCatalogue.pending || mapCatalogue.error) return null;
    // The live view's centre, so the sheet is centred on what the user framed.
    const center = liveViewRef.current.center;
    return buildMapPrintRequest({
      map: printMap,
      center,
      scale: pdfScale,
      format: printFormat,
      orientation: printOrientation,
      symbolSvgs: printArtwork.svgs,
      symbolDesignSvgs: printArtwork.designSvgs,
      // The print panel's sliders, seeded from the Pin size setting.
      pinSizes: printPinSizes.sizes,
      // The whole object, not the two or three scalars off it: the payload
      // builder reads the border treatment and every line-thickness term too.
      appearance,
      // The custom sheet's own millimetres, which win over `format` on the
      // wire's `page_width`/`page_height` — undefined while a named format is
      // selected.
      customPage: printCustomPage,
      // The base map the editor's own layer picker has selected — the editor keeps
      // this state itself rather than through `useTeamMapPresentation`.
      baseMapId: selectedBaseMap?.id ?? null,
      // Printed when the preview shows them and the server draws them here.
      propertyBorders: printPropertyBorders(printProbe, enabledOverlays),
      // The sheet the frame on screen is drawn from, so the request is clipped
      // and simplified against exactly the rectangle the user composed — not a
      // second projector loaded here that could answer differently. The request
      // stays unavailable until the current probe and projector are ready.
      sheet: printProjector,
      // What the pin list is keeping off the editor map stays off the sheet.
      hiddenObjectIds: hiddenOnCanvas,
      activeOverlays: overlayMaps.filter((o) => enabledOverlays.has(o.id)),
      capabilities: printProbe.capabilities ?? [],
      printableFlavors: printProbe.flavors,
    });
  }, [printMap, pdfScale, printFormat, printOrientation, printCustomPage, appearance, printPinSizes.sizes, selectedBaseMap, printProbe, enabledOverlays, printProjector, printFrameCornersValue, printSheetError, hiddenOnCanvas, printArtwork.pending, printArtwork.svgs, printArtwork.designSvgs, printEditPending, overlayMaps, mapCatalogue.pending, mapCatalogue.error]);

  const handleCreatePdf = useCallback(async () => {
    const built = printRequest;
    if (built === null || built.pointCount > 1000000) return;
    // Reported, never swallowed: a print that quietly drops stands is worse
    // than one that says how many it could not draw.
    setOmittedPinCount(built.omittedPins.length);
    await printJobs.start({
      payload: built.request,
      scale: pdfScale,
      pageLabel: printSheet.pageLabel,
      teamId: team.id,
      teamName: team.name,
    });
  }, [printRequest, printJobs, pdfScale, printSheet.pageLabel, team.id, team.name]);

  // Border drawing
  const [drawPoints, setDrawPoints] = useState<GeoPt[]>([]);
  const [borderDraft, setBorderDraft] = useState<{ id: string; name: string; editingId?: string; color?: string } | null>(null);

  // Area drawing (subareas / forbidden)
  const [areaDraft, setAreaDraft] = useState<{ id: string; name: string; color?: string; editingId?: string } | null>(null);

  // CLUB-187: line / arrow drawing
  const [lineDraft, setLineDraft] = useState<LineDraft | null>(null);
  const lines = state.lines ?? [];

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);

  // Vertex editing (any saved polygon)

  // Vertex handles are offered only for geometry the editor can send back.
  //
  // `geometryEditable` guarded the DRAW paths, but nothing guarded the three
  // places that OPEN vertex editing: a click on the polygon itself, and the
  // pencil on the areas list. So a storage-backed or multi-ring outline opened
  // its handles, and the first drag wrote `rings: [newPoints]` - four parcels of
  // a five-parcel import gone from editor state while outerCount still said
  // five. The save then diffed one ring against five, the endpoint refused it,
  // and the whole map save failed with it, unrelated pin and line edits
  // included. Cancel recovered; Done did not.
  //
  // Selecting such a border is still useful, so the tab switch stays and only
  // the handles are withheld: the list card names it a stored outline and offers
  // rename, recolour and delete, all of which carry the geometry back untouched.
  // The map-click path has only a kind and an id, where the list rows already hold the
  // record. This resolves the one so every caller can use the SAME predicate,
  // `geometryEditable`, rather than each entry point growing its own spelling of the
  // rule that decides whether an outline may be reshaped.
  const recordFor = (kind: "border" | "subarea" | "forbidden", id: string) =>
    (kind === "border"
      ? state.borders
      : kind === "subarea"
        ? state.subAreas
        : state.forbiddenAreas
    ).find((a) => a.id === id);

  // CLUB-576: the target exactly as it stood when the edit opened, so Cancel can
  // put it back. The vertex history alone is not enough — the edit HUDs also
  // rename, recolour and (for lines) restyle, and none of that enters it.
  const [vertexEditOrigin, setVertexEditOrigin] = useState<Border | Area | GeoLine | null>(null);

  // Pins
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pinDrawerOpen, setPinDrawerOpen] = useState(false);
  // CLUB-676: whether the pin collection is open. It takes the right panel
  // slot for the whole layer, so while it is open a selected pin highlights on
  // the map and checks its row — it does NOT swap the panel for the edit card,
  // which would close the list you were reading out from under you.
  const [collectionOpen, setCollectionOpen] = useState(false);
  // The GPX import/export panels take a flat category list rather than the
  // catalog's own group tree — they predate the catalog and remap unknown
  // waypoint types by scanning it, which a nested shape doesn't help with.
  const pinCategories = useMemo(
    () => catalog.leaves.map((e) => ({ type: e.id, label: catalog.label(e.id), color: pinDefaultColor(e.id), group: '' })),
    [catalog],
  );
  const [sessionIds, setSessionIds] = useState<string[]>([]);
  // CLUB-576: `pinEditOrigin` is the pin as it was when the card opened — what
  // Cancel restores. Undo is separate: see the edit history below.
  const [pinEditOrigin, setPinEditOrigin] = useState<MapObject | null>(null);
  // Which field the last recorded step touched, so a run of changes to the SAME
  // field is one step: typing a name is one Undo, not one per keystroke, and a
  // drag across the map is one Undo, not one per mouse-move.
  const pinEditStepRef = useRef<string | null>(null);

  // CLUB-576: whether the map was already unsaved when the current editing tool
  // opened. Cancel restores this, so backing out of a tool that changed nothing
  // in the end never leaves a phantom "unsaved changes" prompt behind.
  const dirtyBeforeToolRef = useRef(false);

  /**
   * CLUB-576: one Undo/Redo history for the open editing tool.
   *
   * Undo used to be reimplemented per tool — a slice for draw points, a set
   * subtraction for parcels, an id list for placed pins, a polygon stack for
   * vertices, an object stack for pin fields. Five mechanisms, five disabled
   * states, and no way to offer Redo without writing five more.
   *
   * They are now one stack of snapshots. Every tool records the editable state
   * as it stood BEFORE a step; Undo swaps the current state onto the redo stack
   * and applies the snapshot, and Redo does the reverse. Redo therefore costs
   * nothing extra, which is the whole reason for the shape.
   *
   * Scope is deliberately the open tool: the history is cleared whenever the
   * active tool changes (see the effect below). A snapshot carries no mode
   * flags, so undoing past a tool's own lifetime could restore draft points
   * with no draw HUD to own them.
   */
  const [undoStack, setUndoStack] = useState<EditSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<EditSnapshot[]>([]);

  // Wheel/pinch zoom and pan are MapLibre's own native gestures now — the old
  // hand-rolled cursor-anchored wheel handler and its mirror refs are gone
  // along with the SVG projection engine they computed against.

  // Keyboard zoom: + / = to zoom in, - to zoom out. Ignores inputs/textareas.
  // Drives the bridged live map directly (see `mapRef`/`handleMapBridge`
  // above) rather than page zoom state — MapLibre owns the camera past mount.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === "+" || (e.key === "=" && !e.shiftKey)) {
        e.preventDefault();
        mapRef.current?.zoomIn();
      } else if (e.key === "-") {
        e.preventDefault();
        mapRef.current?.zoomOut();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []); // stable — reads the live map via a ref

  // Mirror of the live history handlers, so the keyboard binding can install
  // once and still reach the current pair.
  const historyRef = useRef<{ undo: (() => void) | null; redo: (() => void) | null }>({
    undo: null,
    redo: null,
  });

  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const togglePanel = () => setPanelCollapsed((v) => !v);

  const areaKey: "subAreas" | "forbiddenAreas" | null =
    activeTab === "subareas" ? "subAreas" : activeTab === "forbidden" ? "forbiddenAreas" : null;
  // Fixed token used to generate persisted object names (data — not translated).
  const areaNameToken = activeTab === "subareas" ? "Sub-area" : "Forbidden area";
  // Distinct id namespace per area type — sub-areas and forbidden areas must not
  // draw new ids from the same pool, so a sub and a forbidden area can never
  // collide (matches the "sa"/"fa" prefixes the copy/import flow uses).
  const areaIdPrefix = activeTab === "subareas" ? "sa" : "fa";
  // Translated display strings for the UI.
  const areaTitle = activeTab === "subareas" ? t('area.subAreaTitle') : t('area.forbiddenTitle');
  const areaPlural = activeTab === "subareas" ? t('area.subAreaPlural') : t('area.forbiddenPlural');
  const areaAccent = activeTab === "subareas" ? LAYER_STROKE.subarea : LAYER_STROKE.forbidden;

  // Group parcels for display ONCE per state change. groupAreasForDisplay runs
  // the polygon-clipping union, so memoizing keeps a pan/zoom re-render (now
  // `liveZoom` state, since MapLibre owns the camera) from re-unioning every
  // border/sub-area/forbidden group. Both the map render and the list cards
  // read these same arrays.
  // A stored area of fewer than three points encloses nothing, so it is dropped
  // here rather than at each of the three render sites. Left in, it reached the
  // renderer as an empty polygon and its centre-on resolved through a centroid
  // that used to answer the origin - so selecting it jumped the editor to the
  // Gulf of Guinea. The read-only surfaces get the same guard from groundShapes.
  const drawable = <T extends { rings: GeoPt[][] }>(areas: T[]) =>
    areas.filter((a) => a.rings.some((r) => r.length >= MIN_RING_POINTS));
  const borderDisplay = useMemo(() => groupAreasForDisplay(drawable(state.borders)), [state.borders]);
  const subAreaDisplay = useMemo(() => groupAreasForDisplay(drawable(state.subAreas)), [state.subAreas]);
  const forbiddenDisplay = useMemo(
    () => groupAreasForDisplay(drawable(state.forbiddenAreas)),
    [state.forbiddenAreas],
  );
  const areaDisplay = areaKey === "subAreas" ? subAreaDisplay : areaKey === "forbiddenAreas" ? forbiddenDisplay : [];

  // The canvas's own overlaySources memo (MapLibreEditorCanvas) is keyed on
  // this array's IDENTITY — MapView's reconciliation effect underneath it
  // keys on content, not identity, so a fresh array here is not a
  // correctness bug, but there is no reason to hand a new one down on every
  // render (every camera tick, now that onViewChange drives page state) when
  // nothing about the enabled overlay set changed.
  const overlayLayers = useMemo(
    () =>
      overlayMaps
        .filter((o) => enabledOverlays.has(o.id) && o.url)
        .map((o) => ({ url: o.url, generation: o.generation })),
    [overlayMaps, enabledOverlays],
  );

  const selected = state.objects.find((o) => o.id === selectedId);
  const placed = sessionIds.map((id) => state.objects.find((o) => o.id === id)).filter(Boolean) as MapObject[];

  // CLUB-667: hiding the selected pin's type drops the selection with it.
  // Leaving it selected would leave the inspector editing — and Delete
  // removing — a pin that is nowhere on screen.
  useEffect(() => {
    if (selected && appearance.hiddenPinTypes.includes(selected.type)) setSelectedId(null);
  }, [selected, appearance.hiddenPinTypes]);

  // ─── Mutators ─────────────────────────────────────────────────────────
  const updateObj = (id: string, patch: Partial<MapObject>) => {
    setState((m) => ({ ...m, objects: m.objects.map((o) => (o.id === id ? { ...o, ...patch } : o)) }));
    setDirty(true);
  };
  const removeObj = (id: string) => {
    setState((m) => ({ ...m, objects: m.objects.filter((o) => o.id !== id) }));
    setSessionIds((s) => s.filter((x) => x !== id));
    setSelectedId(null);
    setDirty(true);
  };
  // ─── CLUB-676: the pin collection's edits ──────────────────────────────
  // These run against the editor's WORKING COPY, not straight to the server,
  // because that is what the surface around them promises: everything else in
  // here is provisional until Save, and a folder rename that committed on its
  // own while a Cancel was still on the table would be the one edit the button
  // could not take back. They record an undo step for the same reason.
  //
  // (In production the folder writes are separate calls from the map save. A
  // host that wants them immediate can drive them itself; this keeps the
  // editor's own Save/Cancel honest either way.)
  const nextFolderId = () => "pf" + Math.random().toString(36).slice(2, 8);
  const createFolder = (name: string) => {
    recordStep();
    setState((m) => ({ ...m, pinFolders: [...(m.pinFolders ?? []), { id: nextFolderId(), name }] }));
    setDirty(true);
  };
  const renameFolder = (folderId: string, name: string) => {
    recordStep();
    // In place: the array's order IS the display order, so remove-and-append
    // would quietly send a renamed folder to the bottom of the list.
    setState((m) => ({
      ...m,
      pinFolders: (m.pinFolders ?? []).map((f) => (f.id === folderId ? { ...f, name } : f)),
    }));
    setDirty(true);
  };
  const deleteFolder = (folderId: string, { deletePins }: { deletePins: boolean }) => {
    recordStep();
    setState((m) => ({
      ...m,
      pinFolders: (m.pinFolders ?? []).filter((f) => f.id !== folderId),
      objects: deletePins
        ? m.objects.filter((o) => o.folderId !== folderId)
        : m.objects.map((o) => (o.folderId === folderId ? { ...o, folderId: undefined } : o)),
      // Filter BEFORE map: filtering after would compare against a folderId
      // the map step had already cleared, so `deletePins` could never match a
      // line and "delete folder and its lines" silently kept every one of them.
      lines: (m.lines ?? [])
        .filter((l) => !(deletePins && l.folderId === folderId))
        .map((l) => (l.folderId === folderId ? { ...l, folderId: undefined } : l)),
    }));
    setDirty(true);
  };
  const filePins = (ids: readonly string[], folderId: string | null) => {
    recordStep();
    const set = new Set(ids);
    setState((m) => ({
      ...m,
      objects: m.objects.map((o) => (set.has(o.id) ? { ...o, folderId: folderId ?? undefined } : o)),
      lines: (m.lines ?? []).map((l) => (set.has(l.id) ? { ...l, folderId: folderId ?? undefined } : l)),
    }));
    setDirty(true);
  };
  const copyPins = (ids: readonly string[], folderId: string | null) => {
    recordStep();
    const set = new Set(ids);
    setState((m) => ({
      ...m,
      objects: [
        ...m.objects,
        ...m.objects
          .filter((o) => set.has(o.id))
          .map((o) => ({ ...o, id: "o" + Math.random().toString(36).slice(2, 8), folderId: folderId ?? undefined })),
      ],
    }));
    setDirty(true);
  };
  const deletePins = (ids: readonly string[]) => {
    recordStep();
    const set = new Set(ids);
    setState((m) => ({
      ...m,
      objects: m.objects.filter((o) => !set.has(o.id)),
      lines: (m.lines ?? []).filter((l) => !set.has(l.id)),
    }));
    setSessionIds((s) => s.filter((x) => !set.has(x)));
    if (selectedId && set.has(selectedId)) setSelectedId(null);
    setDirty(true);
  };

  const addObj = (lat: number, lon: number) => {
    if (!placeMode) return;
    recordStep();
    const id = "o" + Math.random().toString(36).slice(2, 8);
    const obj: MapObject = {
      id,
      lat,
      lon,
      type: placeMode.type,
      // The STORED pin name is the type's label in the user's language, which
      // is what mobile writes for a pin saved without a name (its edit page
      // seeds `name` with the registry's localized type name). From then on it
      // is the user's own text and is never retranslated; only a pin with no
      // name at all falls back to the type label at display time. The running
      // number is a deliberate web-only addition: the editor drops several pins
      // of one type in a row, and identical names make them impossible to tell
      // apart in the folder list.
      label: `${placeMode.label} ${state.objects.filter((o) => o.type === placeMode.type).length + 1}`,
      ...(placeMode.customSymbol ? { customSymbol: placeMode.customSymbol } : {}),
    };
    setState((m) => ({ ...m, objects: [...m.objects, obj] }));
    // CLUB-667: you cannot place a pin you then cannot see. Dropping one whose
    // type is filtered out shows that type again rather than saving a pin that
    // renders nowhere — the exact shape of failure the filter's own chip warns
    // about, and the one CLUB-639 was about.
    if (appearance.hiddenPinTypes.includes(placeMode.type)) {
      setAppearance({
        ...appearance,
        hiddenPinTypes: appearance.hiddenPinTypes.filter((t) => t !== placeMode.type),
      });
    }
    setSessionIds((s) => [...s, id]);
    setDirty(true);
    if (!placeMode.multi) {
      setPlaceMode(null);
      setPinDrawerOpen(true);
    }
  };
  // CLUB-576: every pin edit goes through here so Undo has something to reverse.
  // A run of changes to the SAME field is one step: typing a name is one Undo,
  // not one per keystroke, and a drag across the map is one, not one per move.
  const patchPin = (id: string, patch: Partial<MapObject>) => {
    const step = Object.keys(patch).sort().join(',');
    if (pinEditStepRef.current !== step) {
      recordStep();
      pinEditStepRef.current = step;
    }
    updateObj(id, patch);
  };
  const cancelPinEdit = () => {
    const original = pinEditOrigin;
    if (original) {
      setState((m) => ({ ...m, objects: m.objects.map((o) => (o.id === original.id ? original : o)) }));
      setDirty(dirtyBeforeToolRef.current);
    }
    pinEditStepRef.current = null;
    setSelectedId(null);
  };
  // CLUB-576: pin placement's old "Back" button kept every pin dropped in the
  // session — the one thing Cancel must not do. Cancel lifts all of them and
  // hands back the pin picker; Undo lifts them one at a time via the history.
  const cancelPlacement = () => {
    const dropped = new Set(sessionIds);
    if (dropped.size > 0) {
      setState((m) => ({ ...m, objects: m.objects.filter((o) => !dropped.has(o.id)) }));
      setDirty(dirtyBeforeToolRef.current);
    }
    setSessionIds([]);
    setSelectedId(null);
    setPlaceMode(null);
    setPinDrawerOpen(true);
    // The picker reopening leaves the session key unchanged, so the reset effect
    // will not fire — and a snapshot from mid-session would put back a pin that
    // Cancel is meant to discard.
    resetHistory();
  };
  const finishPlacement = () => {
    setPlaceMode(null);
    setSessionIds([]);
    resetHistory();
  };

  // CLUB-188: merge a copied hunting-ground map into the current one as fresh,
  // editable objects. Non-destructive (appends to existing layers) and a one-time
  // copy (new ids → saved as new records, never linked back to the source).
  const applyCopiedMap = (src: CopyableTeamMap, keepTrails = false) => {
    const genId = (p: string) => p + Math.random().toString(36).slice(2, 8);
    setState((m) => ({ ...m, ...appendCopiedMap(m, src, genId, { keepTrails }) }));
    setDirty(true);
  };

  const addDrawPoint = (lat: number, lon: number) => {
    recordStep();
    setDrawPoints((p) => [...p, { lat, lon }]);
  };

  // Borders
  const startNewBorder = () => {
    closeImportFlows();
    const next = state.borders.length + 1;
    // No palette pick. A new border used to be stamped with BORDER_COLORS[n % 8],
    // which meant EVERY hand-drawn border acquired a stored colour by insertion
    // index — a colour the app never paints, and whose mere presence holds the ground
    // out of the app's union. The draft is drawn in the team's colour instead, which
    // is what the finished border will be drawn in too.
    setBorderDraft({ id: "b" + Math.random().toString(36).slice(2, 7), name: `Border ${next}` });
    setBorderMode("draw");
    setDrawPoints([]);
  };
  const startEditBorder = (b: Border) => {
    if (!geometryEditable(b)) return;
    closeImportFlows();
    setBorderDraft({ id: b.id, name: b.name, editingId: b.id, color: b.color });
    setDrawPoints(b.rings[0]);
    setBorderMode("draw");
  };
  const finishDraw = () => {
    if (drawPoints.length >= 3 && borderDraft) {
      // Carries no colour: see startNewBorder. A redraw keeps whatever the record
      // already had, which is why the draft carries the stored value through.
      const finalBorder: Border = { id: borderDraft.editingId ?? borderDraft.id, name: borderDraft.name, color: borderDraft.color, rings: [drawPoints] };
      setState((m) => {
        if (borderDraft.editingId) {
          return { ...m, borders: m.borders.map((b) => (b.id === borderDraft.editingId ? finalBorder : b)) };
        }
        return { ...m, borders: [...m.borders, finalBorder] };
      });
      setDirty(true);
    }
    setBorderMode("idle");
    setBorderDraft(null);
    setDrawPoints([]);
  };
  const cancelDraw = () => {
    setBorderMode("idle");
    setBorderDraft(null);
    setDrawPoints([]);
  };

  // CLUB-187: lines & arrows. Defaults mirror the app (kMapLineDefault*).
  const startNewLine = (kind: GeoLineKind) => {
    closeImportFlows();
    setVertexEdit(null);
    setLineDraft({
      id: "l" + Math.random().toString(36).slice(2, 7),
      kind,
      name: "",
      color: LINE_DEFAULT_COLOR,
      width: LINE_DEFAULT_WIDTH,
      style: "solid",
    });
    setLineMode("draw");
    setDrawPoints([]);
  };
  const finishLine = () => {
    if (drawPoints.length >= 2 && lineDraft) {
      const finalLine: GeoLine = {
        id: lineDraft.id,
        kind: lineDraft.kind,
        name: lineDraft.name.trim() || undefined,
        color: lineDraft.color,
        width: lineDraft.width,
        style: lineDraft.style,
        points: drawPoints,
      };
      setState((m) => ({ ...m, lines: [...(m.lines ?? []), finalLine] }));
      setDirty(true);
    }
    setLineMode("idle");
    setLineDraft(null);
    setDrawPoints([]);
  };
  const cancelLine = () => {
    setLineMode("idle");
    setLineDraft(null);
    setDrawPoints([]);
  };
  const deleteLine = (id: string) => {
    setState((m) => ({ ...m, lines: (m.lines ?? []).filter((l) => l.id !== id) }));
    setDirty(true);
  };
  const updateLine = (id: string, patch: Partial<GeoLine>) => {
    setState((m) => ({ ...m, lines: (m.lines ?? []).map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
    setDirty(true);
  };
  // CLUB-187: leaving the Lines/Arrows tab abandons an in-progress line draw so
  // its points don't bleed into another layer's canvas.
  useEffect(() => {
    setLineMode("idle");
    setLineDraft(null);
  }, [activeTab]);
  const removeBorder = (id: string) => {
    setState((m) => ({ ...m, borders: m.borders.filter((b) => b.id !== id) }));
    setDirty(true);
  };
  /**
   * Recolour ONE border — the one being edited — and no other. Saved with the rest of
   * the map, like a rename.
   *
   * The parcels of one property selection share an `areaGroup` and are presented as a
   * single border, so they are recoloured together: recolouring one parcel alone would
   * split it off from the rest of the shape it is drawn as part of.
   */
  const recolorBorder = (id: string, color: string) => {
    setState((m) => {
      const group = m.borders.find((b) => b.id === id)?.areaGroup;
      return {
        ...m,
        borders: m.borders.map((b) => (b.id === id || (group && b.areaGroup === group) ? { ...b, color } : b)),
      };
    });
    setDirty(true);
  };

  // Border properties (cadastral parcel selection)
  const [selectedParcels, setSelectedParcels] = useState<Set<string>>(() => new Set());
  const [mapProperties, setMapProperties] = useState<PropertyFeature[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  // Ids of the parcels SEEDED into this selection from already-saved property
  // areas (their border/area record already exists in state). The finish-diff
  // uses this to tell a still-present saved parcel (leave its record alone)
  // from one the user deselected (delete its record) and from a fresh in-session
  // pick (build a new record) — so a saved parcel is never duplicated.
  const seededPropertyIdsRef = useRef<Set<string>>(new Set());

  // Represents an already-saved property area as a tappable parcel: its stored
  // id is the durable key (a fresh tap on the same ground gets a different,
  // ephemeral Tanger id), its outline is the first stored ring, and its point
  // is that ring's centroid. It carries the whole stored ring set (outer plus
  // holes) so a re-tap inside a stored hole resolves as a NEW pick rather than
  // a deselect of the parcel around it, and the layer draws the hole.
  const savedAreasAsProperties = (
    areas: readonly { id: string; name: string; rings: GeoPt[][]; outerCount?: number; areaSource?: string }[],
  ): PropertyFeature[] =>
    areas
      .filter((a) => a.areaSource === 'property' && (a.rings[0]?.length ?? 0) >= MIN_RING_POINTS)
      .flatMap((a) => {
        const ring = a.rings[0];
        const c = polygonCentroid(ring);
        if (!c) return [];
        return [{
          id: a.id,
          designation: a.name,
          lat: c.lat,
          lon: c.lon,
          polygon: ring,
          rings: a.rings,
          outerCount: a.outerCount ?? a.rings.length,
        }];
      });

  // Enters "Select properties" mode for the active flow (border or area).
  // Tanger only exposes per-point info — there is no bbox vector source — so we
  // don't preload the whole overlay. What we DO seed is the flow's already-saved
  // property parcels, so a parcel stored on a previous visit (on web or mobile)
  // shows up selected on re-entry: without this, entering reset the selection to
  // empty and a re-tap on a saved parcel added a duplicate instead of removing it.
  const enterPropertySelection = (
    savedAreas: readonly { id: string; name: string; rings: GeoPt[][]; outerCount?: number; areaSource?: string }[],
  ) => {
    closeImportFlows();
    const seeded = savedAreasAsProperties(savedAreas);
    setMapProperties(seeded);
    setSelectedParcels(new Set(seeded.map((p) => p.id)));
    seededPropertyIdsRef.current = new Set(seeded.map((p) => p.id));

    const propLayer = overlayMaps.find((o) =>
      /fastighe|property|cadastr|boundary|fastigh/i.test(o.label)
    );
    if (propLayer) {
      setEnabledOverlays((prev) => new Set([...prev, propLayer.id]));
    }
  };

  // Auto-dismiss the property-tap notice so it doesn't linger after the user
  // has moved on. Re-armed each time the message changes.
  useEffect(() => {
    if (!propertyNotice) return;
    const timer = setTimeout(() => setPropertyNotice(null), PROPERTY_NOTICE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [propertyNotice]);

  // Resolve a tapped point to a real cadastral property and toggle its
  // selection. Used while either borderMode or areaMode is "properties".
  // The `?trace=` endpoint returns the exact parcel polygon in a single call,
  // so the resulting border/area hugs the actual fastighet boundary.
  const identifyPropertyAt = (lat: number, lon: number) => {
    setPropertiesLoading(true);
    setPropertyNotice(null);
    authHeaders()
      .then((headers) =>
        fetch(propertyTraceUrl(lat, lon), { headers })
      )
      .then((r) => {
        if (!r.ok) throw new Error(`properties request failed: ${r.status}`);
        return r.json();
      })
      .then((d: { property: PropertyFeature | null } | null | undefined) => {
        const outcome = classifyPropertyResponse(d);
        switch (outcome.kind) {
          case 'selected': {
            const hit = outcome.property;
            // Decide against the parcels currently selected — matching a saved
            // parcel by geometry, not just by id (see resolvePropertyTap).
            const selected = mapProperties.filter((p) => selectedParcels.has(p.id));
            const decision = resolvePropertyTap(hit, selected);
            if (decision.action === 'remove') {
              const removeId = decision.id;
              setSelectedParcels((prev) => {
                const next = new Set(prev);
                next.delete(removeId);
                return next;
              });
              // A seeded (saved) parcel stays in mapProperties so it can be
              // re-selected without another tap resolving it, but it must leave
              // the selection. A non-seeded session pick is dropped entirely.
              if (!seededPropertyIdsRef.current.has(removeId)) {
                setMapProperties((prev) => prev.filter((p) => p.id !== removeId));
              }
              setPropertyNotice(null);
              return;
            }
            setMapProperties((prev) =>
              prev.some((p) => p.id === hit.id) ? prev : [...prev, hit]
            );
            setSelectedParcels((prev) => {
              const next = new Set(prev);
              next.add(hit.id);
              return next;
            });
            setPropertyNotice(null);
            return;
          }
          case 'empty':
            setPropertyNotice(t('properties.noParcel'));
            return;
          case 'error':
            setPropertyNotice(t('properties.identifyError'));
            return;
        }
      })
      .catch(() => setPropertyNotice(t('properties.identifyError')))
      .finally(() => setPropertiesLoading(false));
  };

  // The picks that finishing property selection turns into areas: the tapped
  // parcels that resolved to a usable outline (an outer ring of ≥3 points). Each
  // becomes its OWN area — mirroring how mobile stores property parcels — rather
  // than being merged into one polygon, and carries every ring the service sent:
  // the outer first, then the holes.
  const selectedParcelsWithPolygon = (): { id: string; designation: string; rings: GeoPt[][]; outerCount: number }[] =>
    mapProperties
      .filter((p) => selectedParcels.has(p.id) && !!p.polygon && p.polygon.length >= MIN_RING_POINTS)
      .map((p) => ({ id: p.id, designation: p.designation, rings: propertyRings(p), outerCount: propertyOuterCount(p) }));

  // A fresh id shared by every area produced in one finish action, so the
  // parcels stay grouped (matches mobile's per-selection areaGroup).
  const newAreaGroupId = () => "g" + Math.random().toString(36).slice(2, 9);

  const startSelectProperties = () => {
    enterPropertySelection(state.borders);
    setBorderMode("properties");
  };
  const cancelProperties = () => {
    resetPropertySelection();
    setBorderMode("idle");
  };
  // Clears the transient selection state after a finish or cancel — never strand
  // a stale selection, a seeded parcel list, or the seeded-id set into the next
  // entry (which re-seeds from scratch).
  const resetPropertySelection = () => {
    setSelectedParcels(new Set());
    setMapProperties([]);
    seededPropertyIdsRef.current = new Set();
  };
  // One area per selected parcel, sharing a fresh areaGroup and tagged
  // areaSource:"property" (mobile-faithful). Returns null when no selected
  // parcel resolved to a usable outline. Border and Area are the same shape.
  // Seeded parcels are EXCLUDED: their record already exists, so this only ever
  // builds records for fresh in-session picks (finishers reconcile the rest).
  const buildPropertyAreas = (
    idPrefix: string, nameToken: string, existingCount: number,
  ): Border[] | null => {
    const seeded = seededPropertyIdsRef.current;
    const picks = selectedParcelsWithPolygon().filter((p) => !seeded.has(p.id));
    if (picks.length === 0) return null;
    const areaGroup = newAreaGroupId();
    return picks.map((pick, i) => ({
      id: idPrefix + Math.random().toString(36).slice(2, 7),
      name: pick.designation || `${nameToken} ${existingCount + i + 1}`,
      // No colour. The `color` PARAMETER is deleted rather than defaulted, because a
      // parameter that can carry one is what makes the stamp re-addable: this is the
      // path that stamped the domain default on every cadastral parcel of every
      // property selection,
      // and a stored colour is what keeps those parcels out of the app's union — so
      // the stamp was invisible AND it quilted the app's map.
      rings: pick.rings,
      outerCount: pick.outerCount,
      areaSource: "property",
      areaGroup,
    }));
  };

  // Which seeded (already-saved) parcels the user deselected during this
  // session — their records must be deleted on save. Read against a selection
  // snapshot so the finisher and the setState updater agree on what left.
  const deselectedSeededIds = (selected: ReadonlySet<string>): Set<string> =>
    new Set([...seededPropertyIdsRef.current].filter((id) => !selected.has(id)));

  const finishProperties = () => {
    const created = buildPropertyAreas("b", "Border", state.borders.length);
    // A seeded parcel still selected keeps its existing record untouched; one
    // deselected is dropped so saveTeamMap deletes it (diff-by-id per type).
    const removed = deselectedSeededIds(selectedParcels);
    if (created || removed.size > 0) {
      setState((m) => ({
        ...m,
        borders: [...m.borders.filter((b) => !removed.has(b.id)), ...(created ?? [])],
      }));
      setDirty(true);
    }
    // Always reset, even when nothing changed (e.g. only parcels without a
    // usable outline were tapped) — never strand a stale selection.
    resetPropertySelection();
    setBorderMode("idle");
  };

  const startSelectAreaProperties = () => {
    if (!areaKey) return;
    enterPropertySelection(state[areaKey]);
    setAreaMode("properties");
  };
  const cancelAreaProperties = () => {
    resetPropertySelection();
    setAreaMode("idle");
  };
  const finishAreaProperties = () => {
    if (!areaKey) { resetPropertySelection(); setAreaMode("idle"); return; }
    const created = buildPropertyAreas(areaIdPrefix, areaNameToken, state[areaKey].length);
    const removed = deselectedSeededIds(selectedParcels);
    if (created || removed.size > 0) {
      setState((m) => ({
        ...m,
        [areaKey]: [...m[areaKey].filter((a) => !removed.has(a.id)), ...(created ?? [])],
      }));
      setDirty(true);
    }
    resetPropertySelection();
    setAreaMode("idle");
  };
  // Whether finishing property selection would commit anything: a parcel is
  // selected (build/keep it), OR a seeded saved parcel was deselected (delete
  // it). The second case is why Done can't simply key on "count > 0" — removing
  // the only saved parcel drives the count to zero yet still needs committing.
  const propertySelectionHasChange =
    selectedParcels.size > 0 ||
    [...seededPropertyIdsRef.current].some((id) => !selectedParcels.has(id));
  const toggleParcel = (id: string) => {
    recordStep();
    setSelectedParcels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Areas (sub + forbidden)
  const startNewArea = () => {
    closeImportFlows();
    if (!areaKey) return;
    const next = state[areaKey].length + 1;
    // No colour: the accent is what the in-progress outline is DRAWN with, resolved
    // at render time, not a value to persist.
    setAreaDraft({ id: areaIdPrefix + Math.random().toString(36).slice(2, 7), name: `${areaNameToken} ${next}` });
    setAreaMode("draw");
    setDrawPoints([]);
  };
  const startEditArea = (a: Area) => {
    if (!geometryEditable(a)) return;
    closeImportFlows();
    setAreaDraft({ id: a.id, name: a.name, color: a.color, editingId: a.id });
    setDrawPoints(a.rings[0]);
    setAreaMode("draw");
  };
  const finishArea = () => {
    if (drawPoints.length >= 3 && areaDraft && areaKey) {
      // The draft's colour is the accent the panel and the in-progress outline are
      // drawn with, not a value to persist: the canvas strokes a sub-area magenta and
      // a forbidden area red whatever the record says. Writing the accent here is how
      // web would come to invent a stored colour for every area it touches — and a
      // stored per-ground colour holds that ground out of the app's union, so the
      // invented value would be invisible and topology-changing at the same time.
      // A redraw of an existing area keeps whatever it already had, which is what
      // startEditArea carries through.
      const finalArea: Area = { id: areaDraft.editingId ?? areaDraft.id, name: areaDraft.name, color: areaDraft.color, rings: [drawPoints] };
      setState((m) => {
        if (areaDraft.editingId) {
          return { ...m, [areaKey]: m[areaKey].map((a) => (a.id === areaDraft.editingId ? finalArea : a)) };
        }
        return { ...m, [areaKey]: [...m[areaKey], finalArea] };
      });
      setDirty(true);
    }
    setAreaMode("idle");
    setAreaDraft(null);
    setDrawPoints([]);
  };
  const cancelArea = () => {
    setAreaMode("idle");
    setAreaDraft(null);
    setDrawPoints([]);
  };
  // Vertex editing. Polygons carry their points in `polygon`; lines in
  // `points` (open path, min 2). The shared helpers below normalise on that.
  const getVertexTarget = (): Border | Area | GeoLine | null => {
    if (!vertexEdit) return null;
    if (vertexEdit.kind === "border") return state.borders.find((b) => b.id === vertexEdit.id) ?? null;
    if (vertexEdit.kind === "subarea") return state.subAreas.find((a) => a.id === vertexEdit.id) ?? null;
    if (vertexEdit.kind === "line") return (state.lines ?? []).find((l) => l.id === vertexEdit.id) ?? null;
    return state.forbiddenAreas.find((a) => a.id === vertexEdit.id) ?? null;
  };
  const vertexTargetPoints = (t: Border | Area | GeoLine): GeoPt[] =>
    "rings" in t ? (t.rings[0] ?? []) : t.points;
  const updateVertexTarget = (newPoints: GeoPt[]) => {
    if (!vertexEdit) return;
    if (vertexEdit.kind === "line") {
      setState((m) => ({
        ...m,
        lines: (m.lines ?? []).map((l) => (l.id === vertexEdit.id ? { ...l, points: newPoints } : l)),
      }));
      setDirty(true);
      return;
    }
    const key = vertexEdit.kind === "border" ? "borders" : vertexEdit.kind === "subarea" ? "subAreas" : "forbiddenAreas";
    setState((m) => ({
      ...m,
      [key]: m[key].map((x) => (x.id === vertexEdit.id ? { ...x, rings: [newPoints] } : x)),
    }));
    setDirty(true);
  };
  // CLUB-576: opening a pin for editing records it, the same way entering a
  // vertex edit does — Cancel needs a floor to restore to.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    pinEditStepRef.current = null;
    const pin = selectedId ? state.objects.find((o) => o.id === selectedId) : undefined;
    setPinEditOrigin(pin ?? null);
    if (pin) dirtyBeforeToolRef.current = dirty;
  }, [selectedId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!vertexEdit) {
      setVertexEditOrigin(null);
      return;
    }
    setVertexEditOrigin(getVertexTarget());
    dirtyBeforeToolRef.current = dirty;
  }, [vertexEdit?.id, vertexEdit?.kind]);
  // CLUB-576: Cancel exits the edit persisting nothing — the record goes back to
  // what it was when the tool opened, however many vertex moves, renames or
  // recolours happened in between.
  const cancelVertexEdit = () => {
    const original = vertexEditOrigin;
    if (vertexEdit && original) {
      const { id, kind } = vertexEdit;
      if (kind === "line") {
        setState((m) => ({
          ...m,
          lines: (m.lines ?? []).map((l) => (l.id === id ? (original as GeoLine) : l)),
        }));
      } else {
        const key = kind === "border" ? "borders" : kind === "subarea" ? "subAreas" : "forbiddenAreas";
        setState((m) => ({
          ...m,
          [key]: m[key].map((x) => (x.id === id ? (original as Border) : x)),
        }));
      }
      setDirty(dirtyBeforeToolRef.current);
    }
    setVertexEdit(null);
  };
  const snapshot = (): EditSnapshot => ({ state, drawPoints, selectedParcels, sessionIds, dirty });
  const applySnapshot = (s: EditSnapshot) => {
    // The team's border colour is carried FORWARD, never restored.
    //
    // It lives on `EditState` because every border on the canvas is painted from it,
    // but unlike everything else there it is applied to the server on click rather
    // than at Save. So restoring a snapshot taken before a recolour repainted every
    // border in the old colour while the server held the new one - and since the
    // colour is outside the map's save cycle, pressing Save would not have put it
    // back. Undo has to reverse only what Undo can reverse.
    setState((m) => ({ ...s.state, borderColor: m.borderColor }));
    setDrawPoints(s.drawPoints);
    setSelectedParcels(s.selectedParcels);
    setSessionIds(s.sessionIds);
    setDirty(s.dirty);
    // Ends any run of same-field pin edits. Without this, editing that field
    // again after an Undo would be coalesced into the step that was just
    // reversed: it would record nothing (so it could not be undone) and leave
    // the redo branch it diverges from still live, so Redo would discard it.
    pinEditStepRef.current = null;
  };
  /**
   * Records the state as it stands, immediately BEFORE a step changes it. Also
   * drops any redo branch: once the user acts again, the steps they had undone
   * are no longer reachable — standard undo/redo, and it keeps the two stacks
   * from describing two different futures.
   */
  const recordStep = () => {
    setUndoStack((h) => [...h.slice(-HISTORY_LIMIT + 1), snapshot()]);
    setRedoStack([]);
  };
  const undoStep = () => {
    const previous = undoStack[undoStack.length - 1];
    if (!previous) return;
    setRedoStack((r) => [...r, snapshot()]);
    setUndoStack((h) => h.slice(0, -1));
    applySnapshot(previous);
  };
  const redoStep = () => {
    const next = redoStack[redoStack.length - 1];
    if (!next) return;
    setUndoStack((h) => [...h, snapshot()]);
    setRedoStack((r) => r.slice(0, -1));
    applySnapshot(next);
  };
  const resetHistory = () => {
    setUndoStack([]);
    setRedoStack([]);
  };
  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;
  /**
   * Armed by mousedown on a handle, spent by the first movement. Pressing a
   * handle is not yet a change: recording on mousedown put an empty step in the
   * history — so the Undo/Redo pair appeared having changed nothing, and a
   * double-click to delete a point recorded three steps (two mousedowns plus the
   * delete), leaving two Undo presses that did nothing visible.
   */
  const vertexDragUnrecordedRef = useRef(false);
  const beginVertexDrag = () => {
    vertexDragUnrecordedRef.current = true;
  };
  const moveVertex = (i: number, lat: number, lon: number) => {
    const t = getVertexTarget();
    if (!t) return;
    if (vertexDragUnrecordedRef.current) {
      recordStep();
      vertexDragUnrecordedRef.current = false;
    }
    updateVertexTarget(vertexTargetPoints(t).map((p, idx) => (idx === i ? { lat, lon } : p)));
  };
  const deleteVertex = (i: number) => {
    const t = getVertexTarget();
    if (!t) return;
    const pts = vertexTargetPoints(t);
    const minPoints = vertexEdit?.kind === "line" ? 2 : 3;
    if (pts.length <= minPoints) return;
    updateVertexTarget(pts.filter((_, idx) => idx !== i));
  };
  const insertVertex = (afterIdx: number, lat: number, lon: number) => {
    const t = getVertexTarget();
    if (!t) return;
    const np = [...vertexTargetPoints(t)];
    np.splice(afterIdx + 1, 0, { lat, lon });
    updateVertexTarget(np);
  };
  // Which tool is open. The history belongs to that tool, so this string is what
  // resets it: opening, closing or switching tool starts a fresh one.
  //
  // Placement is keyed on the SESSION, not on the armed category: with "Place
  // multiple" off, dropping a pin clears placeMode and reopens the picker, which
  // keyed on the category would have wiped the history in the same commit that
  // recorded the placement — a change made, with no Undo ever offered.
  const openToolKey = [
    borderMode,
    areaMode,
    lineMode,
    placeMode || pinDrawerOpen ? "placing" : "",
    vertexEdit ? `${vertexEdit.kind}:${vertexEdit.id}` : "",
    activeTab === "pins" && !pinDrawerOpen ? selectedId ?? "" : "",
  ].join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    resetHistory();
  }, [openToolKey]);

  historyRef.current = { undo: canUndo ? undoStep : null, redo: canRedo ? redoStep : null };

  // ⌘Z undoes, ⌘⇧Z redoes. Read through the ref so the binding installs once,
  // and skipped inside text fields, where the browser's own undo belongs.
  useEffect(() => {
    const onHistoryKey = (e: KeyboardEvent) => {
      if (e.key !== "z" && e.key !== "Z") return;
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      const run = e.shiftKey ? historyRef.current.redo : historyRef.current.undo;
      if (!run) return;
      e.preventDefault();
      run();
    };
    document.addEventListener("keydown", onHistoryKey);
    return () => document.removeEventListener("keydown", onHistoryKey);
  }, []); // stable — reads the live handlers via the ref

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const { type, id, ids } = pendingDelete;
    // A property group carries every member parcel's id; a single item deletes
    // just `id`. `targetIds` unifies both so the per-type removals below run
    // once per affected record.
    const targetIds = ids && ids.length > 0 ? ids : [id];
    const targetIdSet = new Set(targetIds);
    switch (type) {
      case 'border':
        targetIds.forEach(removeBorder);
        break;
      case 'pin':
        removeObj(id);
        break;
      case 'subArea':
        setState((m) => ({ ...m, subAreas: m.subAreas.filter((a) => !targetIdSet.has(a.id)) }));
        setDirty(true);
        break;
      case 'forbiddenArea':
        setState((m) => ({ ...m, forbiddenAreas: m.forbiddenAreas.filter((a) => !targetIdSet.has(a.id)) }));
        setDirty(true);
        break;
      case 'line':
      case 'arrow':
      case 'trail':
        deleteLine(id);
        break;
    }
    if (vertexEdit && targetIdSet.has(vertexEdit.id)) setVertexEdit(null);
    setPendingDelete(null);
  };

  const onTabChange = (id: LayerId) => {
    setActiveTab(id);
    setBorderMode("idle");
    setBorderDraft(null);
    setDrawPoints([]);
    setAreaMode("idle");
    setAreaDraft(null);
    setSelectedId(null);
    setPlaceMode(null);
    setVertexEdit(null);
    setGpxOpen(false);
    setImportPanelOpen(false);
  };

  const exitToTeam = () => {
    if (dirty) {
      setShowUnsavedDialog(true)
    } else {
      onBack()
    }
  }

  const handleSave = async () => {
    if (!onSave) {
      setDirty(false)
      return
    }
    setSaving(true)
    setSaveError(null)
    savingRef.current = true
    clearDraft(team.id)
    try {
      // The save endpoint reads `folders`; the `TeamMap` the print server and
      // the preview read carries the same list as `pinFolders`. The editor
      // ALWAYS sends it, so a Save cannot drop work done in the collection
      // panel. (CLUB-676)
      await onSave({ ...editStateToTeamMap(state), folders: state.pinFolders ?? [] })
      baseRef.current = state
      setDirty(false)
    } catch {
      // The edits are still unsaved: keep the safety net.
      saveDraft(team.id, baseRef.current, state)
      setSaveError(t('save.error'))
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  return (
    <div
      data-map-editor="root"
      style={{
        position: "relative",
        background: "#0f1110",
        borderRadius: 12,
        overflow: "hidden",
        height: "100%",
        minHeight: 600,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TopBar
        team={team}
        mapName={state.name}
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onCancel={exitToTeam}
        onRename={(name) => {
          setState((m) => ({ ...m, name }));
          setDirty(true);
        }}
        onOpenImport={() => {
          setImportPanelOpen(true);
        }}
        onOpenExport={() => setExportOpen(true)}
        onOpenPrint={() => setPrintOpen(true)}
        canPrint={canPrint}
      />

      <LayerTabBar
        active={activeTab}
        onChange={onTabChange}
        counts={{
          pins: state.objects.length,
          border: state.borders.length,
          subareas: state.subAreas.length,
          forbidden: state.forbiddenAreas.length,
          lines: lines.filter((l) => l.kind === "line").length,
          arrows: lines.filter((l) => l.kind === "arrow").length,
          // CLUB-528: trails must be counted on their own tab — filtering by
          // exact kind here (rather than an "arrow ? arrows : lines" fallback)
          // is what keeps a trail from being silently folded into the Lines count.
          trails: lines.filter((l) => l.kind === "trail").length,
        }}
      />

      <div ref={printCanvasRef} data-map-print="canvas" style={{ flex: 1, position: "relative", overflow: "hidden", background: "#1a1e1c" }}>
        <MapLibreEditorCanvas
          printMap={printOpen ? printMap : undefined}
          state={state}
          hiddenObjectIds={hiddenOnCanvas}
          borderDisplay={borderDisplay}
          subAreaDisplay={subAreaDisplay}
          forbiddenDisplay={forbiddenDisplay}
          mapCenter={openingView.center}
          tileZoom={openingView.zoom}
          tileZoomMax={tileZoomMax}
          tileInfo={tileInfo}
          onViewChange={handleCanvasViewChange}
          onMap={handleMapBridge}
          activeTab={activeTab}
          drawing={borderMode === "draw" || areaMode === "draw" || lineMode === "draw"}
          drawPoints={drawPoints}
          // A border in progress is drawn in the colour it will BE once finished, which
          // is the team's - not a literal repeat of the domain default.
          drawAccent={lineMode === "draw" ? (lineDraft?.color ?? LINE_DEFAULT_COLOR) : areaMode === "draw" ? areaDraft?.color || GROUND_HIGHLIGHT_COLOR : resolveHuntBorderColor({ areaColor: borderDraft?.color, teamColor: state.borderColor })}
          drawShape={lineMode === "draw" ? (lineDraft?.kind === "arrow" ? "arrow" : "line") : "polygon"}
          // Only an area draft is washed: a border is saved and drawn as lines alone.
          drawFill={areaMode === "draw"}
          lineDraft={lineMode === "draw" ? lineDraft : null}
          onFinishLineDraw={finishLine}
          onCancelLineDraw={cancelLine}
          onLineSelect={(id) => {
            if (borderMode !== "idle" || areaMode !== "idle" || lineMode === "draw" || placeMode) return;
            closeImportFlows();
            const l = lines.find((x) => x.id === id);
            if (!l) return;
            // CLUB-528: explicit per-kind routing — the old "arrow ? arrows :
            // lines" fallback silently sent a clicked trail to the Lines tab.
            const targetTab: LayerId =
              l.kind === "arrow" ? "arrows" : l.kind === "trail" ? "trails" : "lines";
            if (activeTab !== targetTab) {
              setActiveTab(targetTab);
              setSelectedId(null);
              setPinDrawerOpen(false);
              setDrawPoints([]);
            }
            setVertexEdit({ kind: "line", id });
          }}
          onAddDrawPoint={addDrawPoint}
          selectedId={selectedId}
          onSelectObj={(id) => {
            if (placeMode) return;
            if (id !== null && activeTab !== "pins") {
              // Pin clicked while a different tab is active — switch to pins tab
              setBorderMode("idle");
              setBorderDraft(null);
              setDrawPoints([]);
              setAreaMode("idle");
              setAreaDraft(null);
              setVertexEdit(null);
              setPinDrawerOpen(false);
              setActiveTab("pins");
            }
            setSelectedId(id);
          }}
          onMoveObj={(id, lat, lon) => {
            // Each drag is its own Undo step. patchPin merges a run of edits to
            // the same field into one step (typing a name), which would fold a
            // second drag of the pin into the first.
            pinEditStepRef.current = null;
            patchPin(id, { lat, lon });
          }}
          onAddObj={addObj}
          placeMode={placeMode}
          borderDraft={borderDraft}
          areaDraft={areaDraft}
          vertexEdit={vertexEdit}
          onVertexMove={moveVertex}
          onVertexDragStart={beginVertexDrag}
          onVertexInsert={(after, lat, lon) => { recordStep(); insertVertex(after, lat, lon); }}
          onVertexDelete={(i) => { recordStep(); deleteVertex(i); }}
          onPolygonSelect={(kind, id) => {
            if (borderMode !== "idle" || areaMode !== "idle" || placeMode) return;
            closeImportFlows();
            const targetTab: LayerId = kind === "border" ? "border" : kind === "subarea" ? "subareas" : "forbidden";
            if (activeTab !== targetTab) {
              // Polygon clicked while a different tab is active — switch to its tab
              setActiveTab(targetTab);
              setSelectedId(null);
              setPinDrawerOpen(false);
              setDrawPoints([]);
            }
            const clicked = recordFor(kind, id);
            if (!clicked || !geometryEditable(clicked)) return;
            setVertexEdit({ kind, id });
          }}
          appearance={appearance}
          showParcels={borderMode === "properties" || areaMode === "properties"}
          mapProperties={mapProperties}
          propertiesLoading={propertiesLoading}
          selectedParcels={selectedParcels}
          onToggleParcel={toggleParcel}
          onIdentifyProperty={
            borderMode === "properties" || areaMode === "properties"
              ? identifyPropertyAt
              : undefined
          }
          overlayLayers={overlayLayers}
          focus={printOpen ? printFocus : null}
          // Null whenever print mode is off (`usePrintFrame` is gated on
          // `printOpen`), so the editor's pins are never dimmed while drawing.
          dimOutside={printFrameCornersValue}
          // Pins and names at the size the print panel's sliders will print
          // them, so moving a slider shows on the map and not only on paper.
          printPinSizes={printOpen ? { sizes: printPinSizes.sizes, scale: pdfScale } : undefined}
        >
          <PrintMapPadding padding={printPadding} />
          {printOpen && (
            <>
              {/* Added to the map when the panel opens, and MapLibre paints in
                  the order layers were added — so the frame sits above every
                  ground and overlay layer that was already there. A raster
                  overlay toggled on later cannot get above it: `MapView`
                  inserts every raster layer BEFORE the first `gj-` layer
                  (`vectorAnchorLayerId`), which the frame's own layers are.
                  What can is another GEOJSON layer mounted after the frame —
                  a border/area draft or a vertex-edit session started while
                  the panel is open — since that one is appended on top.
                  Closing and reopening the panel re-adds the frame last and
                  puts it back above; keeping it there continuously would mean
                  re-adding every frame layer whenever any sibling layer
                  mounts. */}
              <PrintFrameLayers corners={printFrameCornersValue} />
              <MapProjectionProbe projectRef={printProjectRef} unprojectRef={printUnprojectRef} />
            </>
          )}
        </MapLibreEditorCanvas>

        <MapControls
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          canZoomIn={liveZoom < tileZoomMax}
          canZoomOut={liveZoom > tileZoomMin}
          layersOpen={layersOpen}
          onToggleLayers={() => { setLayersOpen((v) => !v); setSettingsOpen(false); }}
          settingsOpen={settingsOpen}
          onToggleSettings={() => { setSettingsOpen((v) => !v); setLayersOpen(false); }}
        />

        {/* CLUB-576: Undo/Redo sit on the map, bottom-right, and appear only once
            a step has actually been taken — an always-present pair would claim
            map real estate to say "nothing to undo". */}
        {(canUndo || canRedo) && (
          <MapHistoryControls
            onUndo={undoStep}
            canUndo={canUndo}
            onRedo={redoStep}
            canRedo={canRedo}
          />
        )}

        {layersOpen && (
          <MapLayersPicker
            baseMaps={baseMaps}
            overlayMaps={overlayMaps}
            selectedMapId={selectedBaseMap?.id ?? null}
            enabledOverlays={enabledOverlays}
            onSelectMap={(m) => {
              setSelectedMapId(m.id);
            }}
            onToggleOverlay={(id) =>
              setEnabledOverlays((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
              })
            }
            onClose={() => setLayersOpen(false)}
            topOffset={appearance.hiddenPinTypes.length > 0 ? HIDDEN_PIN_TYPES_CHIP_STACK : 0}
          />
        )}

        {settingsOpen && (
          <MapAppearancePanel
            value={appearance}
            onChange={setAppearance}
            onClose={() => setSettingsOpen(false)}
            topOffset={appearance.hiddenPinTypes.length > 0 ? HIDDEN_PIN_TYPES_CHIP_STACK : 0}
            // The editor draws the standard border treatment whatever the
            // setting says, like the app's planning map; offering the picker
            // here would be a control that changes nothing on this surface.
            borderStyles={false}
          />
        )}

        {/*
          CLUB-667: the same chip the read-only surfaces carry. It matters more
          here than anywhere: this is the page where a hidden pin is one you
          cannot select, move or delete, so the way back has to be on screen
          rather than three clicks into a panel.
        */}
        <HiddenPinTypesChip
          hiddenPinTypes={appearance.hiddenPinTypes}
          withBackground={appearance.pinBackground}
          onShowAll={() => setAppearance({ ...appearance, hiddenPinTypes: [] })}
          onShowType={(type) =>
            setAppearance({
              ...appearance,
              hiddenPinTypes: appearance.hiddenPinTypes.filter((t) => t !== type),
            })
          }
          onOpenList={() => {
            setLayersOpen(false);
            setSettingsOpen(false);
          }}
        />

        {activeTab === "border" && borderMode === "idle" && !vertexEdit && (
          <BordersListCard
            displayItems={borderDisplay}
            teamColor={state.borderColor}
            onNew={startNewBorder}
            onSelectProps={startSelectProperties}
            onEdit={(b) => { if (!geometryEditable(b)) return; setVertexEdit({ kind: "border", id: b.id }); }}
            onRedraw={(b) => startEditBorder(b)}
            onRemove={(item) => {
              setPendingDelete({
                type: 'border',
                id: item.members[0].id,
                ids: item.isGroup ? item.members.map((m) => m.id) : undefined,
                name: item.name,
              });
            }}
            onCenterOn={(item) => { const c = displayItemCenter(item); if (c) centerMapOn(c); }}
            onRecolor={(item, c) => recolorBorder(item.members[0].id, c)}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {activeTab === "border" && borderMode === "draw" && (
          <BorderDrawHud
            count={drawPoints.length}
            name={borderDraft?.name ?? t('border.tag')}
            isEditing={!!borderDraft?.editingId}
            color={resolveHuntBorderColor({ areaColor: borderDraft?.color, teamColor: state.borderColor })}
            onRename={(name) => setBorderDraft((d) => (d ? { ...d, name } : d))}
            // The draft's own colour: finishDraw copies it onto the border, so only the
            // border being drawn takes it.
            onRecolor={(c) => setBorderDraft((d) => (d ? { ...d, color: c } : d))}
            onCancel={cancelDraw}
            onFinish={finishDraw}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {activeTab === "border" && borderMode === "properties" && (
          <BorderPropertiesHud
            count={selectedParcels.size}
            canFinish={propertySelectionHasChange}
            onCancel={cancelProperties}
            onDone={finishProperties}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {importPanelOpen && !gpxOpen && !copyOpen && !omaRiistaOpen && !sveaskogOpen && !reviiriOpen && (
          <ImportMapPanel
            copyEnabled={!!onLoadCopySource}
            omaRiistaEnabled={!!onImportOmaRiista}
            sveaskogEnabled={!!onImportSveaskog}
            reviiriEnabled={reviiriEnabled}
            onClose={() => setImportPanelOpen(false)}
            onPick={(id) => {
              if (id === "gpx") {
                setImportPanelOpen(false);
                setGpxOpen(true);
              } else if (id === "copy") {
                setImportPanelOpen(false);
                setCopyOpen(true);
              } else if (id === "omariista") {
                setImportPanelOpen(false);
                omaRiistaOpenRef.current = true;
                setOmaRiistaOpen(true);
              } else if (id === "sveaskog") {
                setImportPanelOpen(false);
                sveaskogOpenRef.current = true;
                setSveaskogOpen(true);
              } else if (id === "reviiri") {
                setImportPanelOpen(false);
                // A fresh session, distinct from anything a still-pending
                // import from a previous open might resolve into later
                // (CLUB-191) — see reviiriSessionRef above.
                reviiriSessionRef.current++;
                reviiriOpenRef.current = true;
                setReviiriOpen(true);
              } else {
                // Unreachable today — every IMPORT_SOURCES id above has its own
                // branch. Kept as a defensive fallback for a source added
                // without a branch, and deliberately a no-op close rather than
                // setDirty(true): marking the map dirty here is the exact bug
                // this ticket reported (picking Sveaskog fell into this branch
                // and told the user the map changed when nothing did).
                setImportPanelOpen(false);
              }
            }}
          />
        )}

        {gpxOpen && (
          <GpxImportFlow
            pinCategories={pinCategories}
            hasExistingBorder={state.borders.length > 0}
            onClose={() => setGpxOpen(false)}
            onApply={(src) => applyCopiedMap(src, true)}
          />
        )}

        {copyOpen && onLoadCopySource && (
          <CopyMapFlow
            sources={copySources ?? []}
            onLoad={onLoadCopySource}
            onApply={applyCopiedMap}
            onClose={() => setCopyOpen(false)}
          />
        )}

        {omaRiistaOpen && onImportOmaRiista && (
          <OmaRiistaImportFlow
            onImport={async (areaCode) => {
              const result = await onImportOmaRiista(areaCode);
              if (result.status === "created" || result.status === "refreshed") {
                if (omaRiistaOpenRef.current) {
                  // A panel (the same one, or one reopened since) is still
                  // open — remember the change but don't tell the caller yet:
                  // its refresh remounts this editor and would destroy the
                  // panel before the confirmation could be read. The next
                  // closeOmaRiista, whenever it fires, drains this flag.
                  omaRiistaChangedRef.current = true;
                } else {
                  // The panel that started this import already closed (e.g.
                  // via the header X mid-request) and nothing reopened it, so
                  // no future close exists to drain a pending flag — deliver
                  // the refresh immediately instead of losing it.
                  onOmaRiistaImported?.();
                }
              }
              return result;
            }}
            onClose={closeOmaRiista}
          />
        )}

        {sveaskogOpen && onImportSveaskog && (
          <SveaskogImportFlow
            onImport={async (objektnummer) => {
              const result = await onImportSveaskog(objektnummer);
              if (result.status === "created" || result.status === "refreshed") {
                if (sveaskogOpenRef.current) {
                  // A panel (the same one, or one reopened since) is still
                  // open — remember the change but don't tell the caller yet:
                  // its refresh remounts this editor and would destroy the
                  // panel before the confirmation could be read. The next
                  // closeSveaskog, whenever it fires, drains this flag.
                  sveaskogChangedRef.current = true;
                } else {
                  // The panel that started this import already closed (e.g.
                  // via the header X mid-request) and nothing reopened it, so
                  // no future close exists to drain a pending flag — deliver
                  // the refresh immediately instead of losing it.
                  onSveaskogImported?.();
                }
              }
              return result;
            }}
            onClose={closeSveaskog}
          />
        )}

        {reviiriOpen && onReviiriConnect && onLoadReviiriClubs && onImportReviiri && (
          <ReviiriImportFlow
            onConnect={onReviiriConnect}
            onLoadClubs={onLoadReviiriClubs}
            onImport={async (mapId) => {
              // Read before awaiting: this closure keeps running (see
              // reviiriSessionRef above) even if the panel that requested the
              // import unmounts before the request settles.
              const session = reviiriSessionRef.current;
              const result = await onImportReviiri(mapId);
              if (result.status === "created" || result.status === "refreshed") {
                if (session === reviiriSessionRef.current) {
                  // Still the same, still-open session: defer to closeReviiri
                  // so the "created"/"refreshed" confirmation gets a chance to
                  // be read before the editor remounts.
                  reviiriChangedRef.current = true;
                } else if (reviiriOpenRef.current) {
                  // A newer Reviiri session is now open. Delivering the stale result
                  // immediately would remount the editor and destroy that newer panel;
                  // fold its refresh into the newer session's eventual close instead.
                  reviiriChangedRef.current = true;
                } else {
                  // The originating panel closed and no newer session is active, so no
                  // future close can deliver this successful backend change.
                  onReviiriImported?.();
                }
              }
              return result;
            }}
            onClose={closeReviiri}
          />
        )}

        {exportOpen && (
          <GpxExportPanel
            pinCategories={pinCategories}
            data={{
              mapName: state.name,
              borders:        state.borders,
              subAreas:       state.subAreas,
              forbiddenAreas: state.forbiddenAreas,
              pins: state.objects.map((o) => ({
                id:    o.id,
                label: o.label ?? o.type,
                type:  o.type,
                lat:   o.lat,
                lon:   o.lon,
              })),
            }}
            onClose={() => setExportOpen(false)}
          />
        )}

        {printOpen && <BrowserPrintNotice />}
        {printOpen && (
          <PrintPreviewPanel
            topOffset={
              appearance.hiddenPinTypes.length > 0 ? HIDDEN_PIN_TYPES_CHIP_STACK : 0
            }
            format={printFormat}
            orientation={printOrientation}
            onFormatChange={printSheet.onFormatChange}
            onOrientationChange={printSheet.onOrientationChange}
            customPage={printCustomPage}
            onCustomPageChange={printSheet.onCustomPageChange}
            customPageActive={printSheet.customPageActive}
            onActivateCustomPage={printSheet.onActivateCustomPage}
            aspect={printSheet.aspect}
            onAspectChange={printSheet.onAspectChange}
            pinSizes={printPinSizes.sizes}
            pinSizeRange={printPinSizes.pinRange}
            onPinSizesChange={printPinSizes.onSizesChange}
            onClose={() => setPrintOpen(false)}
            // No browser print here: the map is a MapLibre canvas, which a
            // print stylesheet cannot re-rasterise for paper. MapPrintSection's
            // own "Create PDF" is the only way to print.
            serverPrintSection={
              canPrint ? (
                <MapPrintSection
                  scale={pdfScale}
                  onScaleChange={printSheet.onScaleChange}
                  format={printFormat}
                  orientation={printOrientation}
                  // Without it a custom sheet's coverage, wait estimate and
                  // limit check would all be computed for the named format
                  // behind it.
                  page={printCustomPage}
                  customScale={printSheet.customScale}
                  onCustomScaleChange={printSheet.onCustomScaleChange}
                  customScaleActive={printSheet.customScaleActive}
                  onActivateCustomScale={printSheet.onActivateCustomScale}
                  jobs={printJobs.jobs}
                  busy={printJobs.busy}
                  // Create stays closed until the sheet's projector is in
                  // hand: the request is clipped to the paper in that
                  // projector's plane, so a click before it lands would post
                  // the whole ground and hit the server's request-body cap
                  // instead of printing the sheet on screen.
                  sheetPending={printProjectorPending || !printFrameCornersValue || printArtwork.pending || mapCatalogue.pending}
                  editPending={printEditPending}
                  skips={printRequest?.skips}
                  pointLimitExceeded={(printRequest?.pointCount ?? 0) > 1000000}
                  sheetError={printSheetError ?? (mapCatalogue.error ? 'catalogue' : null)}
                  onRetrySheet={mapCatalogue.error ? mapCatalogue.retry : retryPrintSheet}
                  // A flavour the probe does not list for this centre would be
                  // accepted and then fail; better to say so before the wait.
                  // Where nothing prints at all, the panel says that instead.

                  printUnavailable={printUnavailable}
                  error={printJobs.error}
                  omittedPinCount={omittedPinCount}
                  hiddenPinTypeCount={appearance.hiddenPinTypes.length}
                  onCreate={handleCreatePdf}
                  onOpen={(job) => {
                    // The result URL is self-authenticating and short-lived, so
                    // it is handed straight to the browser rather than stored
                    // anywhere — but only once proven an https URL.
                    const url = safePrintUrl(job.url);
                    if (url !== null) window.open(url, '_blank', 'noopener');
                  }}
                  onReprint={handleCreatePdf}
                />
              ) : (
                // Defensive only: the toolbar's own Print entry is gated on
                // `canPrint` too (see TopBar below), so this renders only if
                // that gate is bypassed — a direct state change, a test, or a
                // future caller.
                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: COLORS.onSurfaceVariant }}>
                  {t('serverPrint.unavailable')}
                </p>
              )
            }
          />
        )}

        {(activeTab === "subareas" || activeTab === "forbidden") && areaMode === "idle" && !vertexEdit && (
          <AreasListCard
            title={areaPlural}
            singular={areaTitle}
            accent={areaAccent}
            displayItems={areaDisplay}
            onNew={startNewArea}
            onSelectProps={startSelectAreaProperties}
            onEdit={(a) => {
              if (!geometryEditable(a)) return;
              setVertexEdit({ kind: activeTab === "subareas" ? "subarea" : "forbidden", id: a.id });
            }}
            onRedraw={(a) => startEditArea(a)}
            onRename={(item, name) => {
              if (!areaKey) return;
              // Every member of a property selection takes the name, not just the
              // first: the parcels share one areaGroup and are listed, drawn and
              // picked as one area, and mobile renames a selection the same way.
              const ids = new Set(item.members.map((m) => m.id));
              setState((m) => ({
                ...m,
                [areaKey]: m[areaKey].map((x) => (ids.has(x.id) ? { ...x, name } : x)),
              }));
              setDirty(true);
            }}
            onRemove={(item) => {
              setPendingDelete({
                type: activeTab === 'subareas' ? 'subArea' : 'forbiddenArea',
                id: item.members[0].id,
                ids: item.isGroup ? item.members.map((m) => m.id) : undefined,
                name: item.name,
              });
            }}
            onCenterOn={(item) => { const c = displayItemCenter(item); if (c) centerMapOn(c); }}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {(activeTab === "subareas" || activeTab === "forbidden") && areaMode === "properties" && (
          <BorderPropertiesHud
            tag={areaTitle}
            accent={areaAccent}
            description={t('properties.descriptionArea', { area: areaTitle.toLowerCase() })}
            count={selectedParcels.size}
            canFinish={propertySelectionHasChange}
            onCancel={cancelAreaProperties}
            onDone={finishAreaProperties}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {(activeTab === "subareas" || activeTab === "forbidden") && areaMode === "draw" && (
          <AreaDrawHud
            title={areaTitle}
            accent={areaAccent}
            count={drawPoints.length}
            name={areaDraft?.name ?? ""}
            onRename={(name) => setAreaDraft((d) => (d ? { ...d, name } : d))}
            onCancel={cancelArea}
            onFinish={finishArea}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {activeTab === "pins" && !pinDrawerOpen && !placeMode && !selected && !collectionOpen && (
          <PinsActionsCard
            count={state.objects.length}
            folderCount={(team.map.pinFolders ?? []).length}
            onAdd={() => {
              closeImportFlows();
              setPinDrawerOpen(true);
              setSessionIds([]);
              setSelectedId(null);
            }}
            onBrowse={() => {
              closeImportFlows();
              setCollectionOpen(true);
            }}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {activeTab === "pins" && collectionOpen && !pinDrawerOpen && !placeMode && (
          <PinCollectionHud
            folders={state.pinFolders ?? []}
            pins={state.objects}
            lines={state.lines}
            selectedId={selectedId}
            onSelect={(id) => setSelectedId((prev) => (prev === id ? null : id))}
            onFocus={centerMapOn}
            onClose={() => setCollectionOpen(false)}
            ops={{
              onCreateFolder: createFolder,
              onRenameFolder: renameFolder,
              onDeleteFolder: deleteFolder,
              onMovePins: filePins,
              onCopyPins: copyPins,
              onDeletePins: deletePins,
              // Editing a pin is the edit card, which owns the same panel slot —
              // so opening it closes the list rather than stacking on it.
              onEditPin: (id) => {
                setCollectionOpen(false);
                setSelectedId(id);
              },
              onAddPin: () => {
                setCollectionOpen(false);
                closeImportFlows();
                setPinDrawerOpen(true);
                setSessionIds([]);
                setSelectedId(null);
              },
              visibility: {
                hiddenPinIds,
                onSetPinHidden: (id, hide) =>
                  setHiddenPinIds((prev) => {
                    const next = new Set(prev);
                    if (hide) next.add(id);
                    else next.delete(id);
                    return next;
                  }),
                collectionHidden,
                onSetCollectionHidden: setCollectionHidden,
              },
            }}
          />
        )}

        {activeTab === "pins" && selected && !pinDrawerOpen && !placeMode && !collectionOpen && (
          <PinEditCard
            key={selected.id}
            obj={selected}
            onChange={(patch) => patchPin(selected.id, patch)}
            onDelete={() => {
              setPendingDelete({ type: 'pin', id: selected.id, name: selected.label || undefined });
            }}
            onCancel={cancelPinEdit}
            onDone={() => setSelectedId(null)}
            withBackground={appearance.pinBackground}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {activeTab === "pins" && placeMode && (
          <PlacementBanner
            placeMode={placeMode}
            sessionCount={sessionIds.length}
            onDone={finishPlacement}
            onCancel={cancelPlacement}
          />
        )}

        {vertexEdit && vertexEdit.kind !== "line" && (
          <VertexEditHud
            kind={vertexEdit.kind}
            target={getVertexTarget() as Border | Area | null}
            // CLUB-576: the accent is the colour the shape is DRAWN with, not the
            // colour the record stores. Taking it from the record is what headed
            // a sub-area panel blue while its shape on the map was magenta.
            color={drawnColour(vertexEdit.kind, getVertexTarget(), state.borderColor)}
            onRename={(name) => {
              if (!vertexEdit) return;
              const key =
                vertexEdit.kind === "border"
                  ? "borders"
                  : vertexEdit.kind === "subarea"
                  ? "subAreas"
                  : "forbiddenAreas";
              setState((m) => ({
                ...m,
                [key]: m[key].map((x) => (x.id === vertexEdit.id ? { ...x, name } : x)),
              }));
              setDirty(true);
            }}
            onRecolor={vertexEdit.kind === "border" ? (c) => recolorBorder(vertexEdit.id, c) : undefined}
            onRedraw={() => {
              const t = getVertexTarget();
              if (!t) return;
              setVertexEdit(null);
              if (vertexEdit.kind === "border") startEditBorder(t as Border);
              else startEditArea(t as Area);
            }}
            onCancel={cancelVertexEdit}
            onDelete={() => {
              if (!vertexEdit) return;
              const collection =
                vertexEdit.kind === 'border'
                  ? state.borders
                  : vertexEdit.kind === 'subarea'
                  ? state.subAreas
                  : state.forbiddenAreas;
              const target = collection.find((x) => x.id === vertexEdit.id);
              setPendingDelete({
                type: VERTEX_KIND_TO_DELETE_TYPE[vertexEdit.kind],
                id: vertexEdit.id,
                name: target?.name,
              });
            }}
            onDone={() => setVertexEdit(null)}
            collapsed={panelCollapsed}
            onToggleCollapse={togglePanel}
          />
        )}

        {(activeTab === "lines" || activeTab === "arrows" || activeTab === "trails") && (() => {
          // CLUB-528: rendering-only filter — `kindLines` (and `lines` itself,
          // just below) are local to this render and never touch `state.lines`
          // or the array handed to `onSave`; the save path stays kind-agnostic.
          const kind: GeoLineKind =
            activeTab === "arrows" ? "arrow" : activeTab === "trails" ? "trail" : "line";
          const kindLines = lines.filter((l) => l.kind === kind);
          if (lineMode === "draw" && lineDraft?.kind === kind) {
            return (
              <LineDrawHud
                kind={kind}
                draft={lineDraft}
                pointCount={drawPoints.length}
                lengthMeters={lineLengthMeters(drawPoints)}
                onChange={(patch) => setLineDraft((d) => (d ? { ...d, ...patch } : d))}
                onCancel={cancelLine}
                onFinish={finishLine}
                collapsed={panelCollapsed}
                onToggleCollapse={togglePanel}
              />
            );
          }
          const editingLine =
            vertexEdit?.kind === "line" ? kindLines.find((l) => l.id === vertexEdit.id) ?? null : null;
          if (editingLine) {
            return (
              <LineEditHud
                kind={kind}
                line={editingLine}
                lineIndex={kindLines.indexOf(editingLine)}
                onPatch={(patch) => updateLine(editingLine.id, patch)}
                onCancel={cancelVertexEdit}
                onDelete={() => setPendingDelete({ type: kind, id: editingLine.id, name: editingLine.name })}
                onDone={() => setVertexEdit(null)}
                collapsed={panelCollapsed}
                onToggleCollapse={togglePanel}
              />
            );
          }
          return (
            <LinesPanel
              kind={kind}
              lines={kindLines}
              onAdd={() => startNewLine(kind)}
              onEdit={(l) => { closeImportFlows(); setVertexEdit({ kind: "line", id: l.id }); }}
              onDelete={(id) => {
                const l = kindLines.find((x) => x.id === id);
                setPendingDelete({ type: kind, id, name: l?.name });
              }}
              onCenterOn={(l) => { const c = polygonCentroid(l.points); if (c) centerMapOn(c); }}
              collapsed={panelCollapsed}
              onToggleCollapse={togglePanel}
            />
          );
        })()}

      </div>

      {pinDrawerOpen && (
        <PinDrawer
          placedSession={placed}
          onPlaceMode={(pm) => {
            if (pm) closeImportFlows();
            if (pm) dirtyBeforeToolRef.current = dirty;
            setPlaceMode(pm);
            if (pm) setPinDrawerOpen(false);
          }}
          onSavePlaced={(id, patch) => updateObj(id, patch)}
          onRemovePlaced={removeObj}
          withBackground={appearance.pinBackground}
          onClose={() => {
            setPinDrawerOpen(false);
            setPlaceMode(null);
            setSessionIds([]);
          }}
        />
      )}
      {saveError && (
        <div style={{
          position: 'absolute', top: 56, left: '50%', transform: 'translateX(-50%)',
          background: COLORS.errorContainer, color: COLORS.onErrorContainer,
          padding: '8px 16px', borderRadius: 8, fontSize: 13, zIndex: 10,
          whiteSpace: 'nowrap',
        }}>
          {saveError}
        </div>
      )}
      {propertyNotice && (
        <div style={{
          position: 'absolute', top: saveError ? 96 : 56, left: '50%', transform: 'translateX(-50%)',
          background: COLORS.secondaryContainer, color: COLORS.onSecondaryContainer,
          padding: '8px 16px', borderRadius: 8, fontSize: 13, zIndex: 10,
          maxWidth: '80%', textAlign: 'center',
        }}>
          {propertyNotice}
        </div>
      )}
      {pendingDelete && (
        <ConfirmDeleteModal
          objectType={pendingDelete.type}
          objectName={pendingDelete.name}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
      {pendingDraft && (
        <DraftRestoreDialog
          savedAt={new Date(pendingDraft.savedAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
        />
      )}
      {showUnsavedDialog && (
        <UnsavedChangesDialog
          saving={saving}
          onCancel={() => setShowUnsavedDialog(false)}
          onDiscard={() => {
            latestRef.current.discarded = true;
            clearDraft(team.id);
            setDirty(false);
            setShowUnsavedDialog(false);
            onBack();
          }}
          onSave={async () => {
            await handleSave()
            if (!saveError) onBack()
            else setShowUnsavedDialog(false)
          }}
        />
      )}
    </div>
  );
}

/**
 * Offered when the map is opened with a local draft of edits that were never
 * saved (a closed tab, a reload, a crash). Restore puts them back as unsaved
 * edits, to be saved or cancelled like any other; Discard forgets them.
 */
function DraftRestoreDialog({
  savedAt,
  onRestore,
  onDiscard,
}: {
  savedAt: string
  onRestore: () => void
  onDiscard: () => void
}) {
  const { t } = useTranslation('map');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('draft.title')}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          padding: '24px',
          maxWidth: 360,
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <p style={{ margin: '0 0 8px', color: COLORS.onSurface, fontSize: 16, fontWeight: 600 }}>
          {t('draft.title')}
        </p>
        <p style={{ margin: '0 0 20px', color: COLORS.onSurfaceVariant, fontSize: 14 }}>
          {t('draft.body', { time: savedAt })}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="wh-btn wh-btn-secondary" onClick={onDiscard}>
            {t('draft.discard')}
          </button>
          <button className="wh-btn wh-btn-primary" onClick={onRestore}>
            {t('draft.restore')}
          </button>
        </div>
      </div>
    </div>
  );
}

function UnsavedChangesDialog({
  onSave,
  onDiscard,
  onCancel,
  saving,
}: {
  onSave: () => void
  onDiscard: () => void
  onCancel: () => void
  saving: boolean
}) {
  const { t } = useTranslation('map');
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          padding: '24px',
          maxWidth: 320,
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <p style={{ margin: '0 0 8px', color: COLORS.onSurface, fontSize: 16, fontWeight: 600 }}>
          {t('unsaved.title')}
        </p>
        <p style={{ margin: '0 0 20px', color: COLORS.onSurfaceVariant, fontSize: 14 }}>
          {t('unsaved.body')}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="wh-btn wh-btn-text" onClick={onCancel} disabled={saving}>
            {t('unsaved.cancel')}
          </button>
          <button className="wh-btn wh-btn-secondary" onClick={onDiscard} disabled={saving}>
            {t('unsaved.discard')}
          </button>
          <button className="wh-btn wh-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? t('unsaved.saving') : t('unsaved.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Top bar ────────────────────────────────────────────────────────────

/** Width below which the toolbar's actions no longer fit inline. */
const NARROW_TOOLBAR_MAX_PX = 600;

/**
 * True when the viewport is too narrow for the toolbar's actions to sit inline.
 *
 * This used to be a pure CSS `@media (max-width: 600px)` rule that set
 * `display: none` on the actions — which removed Import, Export, Print and
 * Cancel outright rather than relocating them. Import is the only route into the
 * Oma Riista importer, so the whole feature became unreachable on a phone or a
 * half-width window, and losing Cancel stranded the user in the editor. Deciding
 * in JS lets the same actions move into an overflow menu instead of vanishing.
 */
function useNarrowToolbar(): boolean {
  const query = `(max-width: ${NARROW_TOOLBAR_MAX_PX}px)`;
  // Read synchronously on first render so the first paint is already correct —
  // an effect-only read would flash the inline row on a phone.
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    setNarrow(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return narrow;
}

/**
 * The toolbar's actions collapsed behind a single trigger, for viewports where
 * they cannot sit inline. Every action stays reachable — that is the whole point
 * (see `useNarrowToolbar`).
 */
function MapToolOverflowMenu({
  onOpenImport,
  onOpenExport,
  onOpenPrint,
  onCancel,
  canPrint,
}: {
  onOpenImport: () => void;
  onOpenExport: () => void;
  onOpenPrint: () => void;
  onCancel: () => void;
  /** Hides the Print row entirely rather than offering a click that leads
   *  nowhere — mirrors `TopBar`'s own inline Print button. */
  canPrint: boolean;
}) {
  const { t } = useTranslation('map');
  const [open, setOpen] = useState(false);

  const items: { key: string; icon: IconName; label: string; run: () => void }[] = [
    { key: "import", icon: "download", label: t('topBar.import'), run: onOpenImport },
    { key: "export", icon: "upload",   label: t('topBar.export'), run: onOpenExport },
    ...(canPrint ? [{ key: "print", icon: "print" as IconName, label: t('topBar.print'), run: onOpenPrint }] : []),
    { key: "cancel", icon: "x",        label: t('topBar.cancel'), run: onCancel },
  ];

  return (
    <div style={{ position: "relative" }}>
      <button
        className="wh-btn-icon"
        aria-label={t('topBar.moreActions')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="more" size={18} />
      </button>

      {open && (
        <>
          {/* Click-outside backdrop, so the menu closes the way the other panels do. */}
          <div style={{ position: "fixed", inset: 0, zIndex: 20 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: 6,
              minWidth: 184,
              background: COLORS.surfaceContainerLowest,
              borderRadius: 10,
              boxShadow: "0 8px 28px rgba(0,0,0,0.28)",
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              overflow: "hidden",
              zIndex: 21,
            }}
          >
            {items.map((it) => (
              <button
                key={it.key}
                onClick={() => {
                  setOpen(false);
                  it.run();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "11px 14px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 14,
                  color: COLORS.onSurface,
                  textAlign: "left",
                }}
              >
                <Icon name={it.icon} size={16} />
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TopBar({
  team,
  mapName,
  dirty,
  saving,
  onSave,
  onCancel,
  onRename,
  onOpenImport,
  onOpenExport,
  onOpenPrint,
  canPrint,
}: {
  team: Team;
  mapName: string;
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  onCancel: () => void;
  onRename: (name: string) => void;
  onOpenImport: () => void;
  onOpenExport: () => void;
  onOpenPrint: () => void;
  /**
   * A Print button that opens a dialog which cannot actually print — no auth
   * token to exchange a print session with, or a ground with no centre to
   * frame a sheet around — is worse than no button at all, so both Print entry
   * points (this one and the overflow-menu row) stay hidden rather than
   * inviting a click that leads nowhere. Mirrors `TeamDetailPage`'s `MapTab`.
   */
  canPrint: boolean;
}) {
  const { t } = useTranslation('map');
  const [editing, setEditing] = useState(false);
  const narrow = useNarrowToolbar();
  return (
    <div
      data-map-print="hide"
      style={{
        height: 56,
        flexShrink: 0,
        background: COLORS.surfaceContainerLowest,
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
        gap: 12,
        zIndex: 5,
      }}
    >
      <button className="wh-btn-icon" onClick={onCancel} aria-label={t('topBar.back')}>
        <Icon name="chevron-left" size={18} />
      </button>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
        {editing ? (
          <input
            autoFocus
            value={mapName}
            onChange={(e) => onRename(e.target.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setEditing(false);
            }}
            style={{
              fontFamily: COLORS.fontDisplay,
              fontSize: 18,
              fontWeight: 400,
              border: `1px solid ${COLORS.primary}`,
              borderRadius: 6,
              padding: "2px 6px",
              width: 320,
              outline: "none",
            }}
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              textAlign: "left",
              fontFamily: COLORS.fontDisplay,
              fontSize: 18,
              fontWeight: 400,
              color: COLORS.onSurface,
              cursor: "text",
            }}
          >
            {mapName}
          </button>
        )}
        <div style={{ fontSize: 12, color: COLORS.outline }}>{team.name} · {t('topBar.subtitle')}</div>
      </div>

      <div style={{ flex: 1 }} />

      {narrow ? (
        <MapToolOverflowMenu
          onOpenImport={onOpenImport}
          onOpenExport={onOpenExport}
          onOpenPrint={onOpenPrint}
          onCancel={onCancel}
          canPrint={canPrint}
        />
      ) : (
        <>
          <button className="wh-btn wh-btn-outline wh-btn-sm" onClick={onOpenImport}>
            <Icon name="download" size={14} />
            {t('topBar.import')}
          </button>
          <button className="wh-btn wh-btn-outline wh-btn-sm" onClick={onOpenExport}>
            <Icon name="upload" size={14} />
            {t('topBar.export')}
          </button>
          {canPrint && (
            <button className="wh-btn wh-btn-outline wh-btn-sm" onClick={onOpenPrint}>
              <Icon name="print" size={14} />
              {t('topBar.print')}
            </button>
          )}
          <div style={{ width: 1, height: 24, background: COLORS.surfaceContainerHighest }} />
          <button className="wh-btn wh-btn-outline" onClick={onCancel}>
            {t('topBar.cancel')}
          </button>
        </>
      )}
      <button className="wh-btn wh-btn-primary" disabled={!dirty || !!saving} onClick={onSave}>
        <Icon name="check" size={16} />
        {saving ? t('topBar.saving') : dirty ? t('topBar.saveChanges') : t('topBar.saved')}
      </button>
    </div>
  );
}

// ─── Layer tabs ─────────────────────────────────────────────────────────

function LayerTabBar({
  active,
  onChange,
  counts,
}: {
  active: LayerId;
  onChange: (id: LayerId) => void;
  counts: Record<LayerId, number>;
}) {
  const { t: tr } = useTranslation('map');
  return (
    <div
      data-map-print="hide"
      style={{
        flexShrink: 0,
        background: COLORS.surfaceContainerLowest,
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        display: "flex",
        padding: "0 12px",
        gap: 2,
        overflowX: "auto",
      }}
    >
      {LAYER_TABS.map((t) => {
        const isActive = active === t.id;
        const count = counts[t.id];
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 14px",
              border: "none",
              background: "none",
              borderBottom: `2px solid ${isActive ? COLORS.primary : "transparent"}`,
              color: isActive ? COLORS.primary : COLORS.outline,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <Icon name={t.icon} size={16} />
            {tr(`layer.${t.id}`)}
            {count > 0 && (
              <span
                style={{
                  fontSize: 11,
                  padding: "1px 7px",
                  borderRadius: 999,
                  background: isActive ? COLORS.primary : COLORS.surfaceContainer,
                  color: isActive ? COLORS.onPrimary : COLORS.outline,
                  fontWeight: 700,
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Map controls (zoom + layer legend) ─────────────────────────────────

function MapControls({
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  layersOpen,
  onToggleLayers,
  settingsOpen,
  onToggleSettings,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  layersOpen: boolean;
  onToggleLayers: () => void;
  settingsOpen: boolean;
  onToggleSettings: () => void;
}) {
  const { t } = useTranslation('map');
  return (
    <div data-map-print="hide" style={{ position: "absolute", top: 16, right: 16, display: "flex", flexDirection: "column", gap: 6, zIndex: 4 }}>
      <SurfaceIconButton title={t('controls.zoomIn')} onClick={onZoomIn} disabled={!canZoomIn}>
        <Icon name="zoom-in" size={20} />
      </SurfaceIconButton>
      <SurfaceIconButton title={t('controls.zoomOut')} onClick={onZoomOut} disabled={!canZoomOut}>
        <Icon name="zoom-out" size={20} />
      </SurfaceIconButton>
      <SurfaceIconButton title={t('controls.mapLayers')} onClick={onToggleLayers} active={layersOpen}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      </SurfaceIconButton>
      <SurfaceIconButton title={t('controls.appearance')} onClick={onToggleSettings} active={settingsOpen}>
        <Icon name="settings" size={20} />
      </SurfaceIconButton>
    </div>
  );
}

/**
 * CLUB-576: the map's Undo/Redo pair, bottom-right.
 *
 * Labelled rather than icon-only: a bare arrow among the zoom controls read as a
 * view control, and "revert my last change" is worth spelling out. It sits on the
 * map rather than in a panel footer so one implementation serves every editing
 * tool and it keeps working while the panel is collapsed.
 */
function MapHistoryControls({
  onUndo,
  canUndo,
  onRedo,
  canRedo,
}: {
  onUndo: () => void;
  canUndo: boolean;
  onRedo: () => void;
  canRedo: boolean;
}) {
  const { t } = useTranslation('map');
  const button = (disabled: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 40,
    padding: "0 14px",
    border: "none",
    background: "none",
    borderRadius: 999,
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 600,
    color: disabled ? COLORS.outline : COLORS.onSurface,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "default" : "pointer",
  });
  return (
    <div
      data-map-print="hide"
      data-map-history=""
      style={{
        position: "absolute",
        right: 16,
        bottom: 16,
        zIndex: 4,
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: 4,
        borderRadius: 999,
        background: COLORS.surface,
        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
      }}
    >
      <button onClick={onUndo} disabled={!canUndo} style={button(!canUndo)}>
        <Icon name="undo" size={18} />
        {t('toolActions.undo')}
      </button>
      <button onClick={onRedo} disabled={!canRedo} style={button(!canRedo)}>
        <Icon name="redo" size={18} />
        {t('toolActions.redo')}
      </button>
    </div>
  );
}

// ─── Geo helpers ─────────────────────────────────────────────────────────

// Centre-on target for a display item: the centroid of the OUTERS of the shape it
// draws. Holes are excluded because a lake's vertices would pull the centre toward
// ground the item does not cover, and every outer is included so a disjoint group
// frames all of its parcels rather than just the first.
function displayItemCenter<T extends { rings: GeoPt[][] }>(
  item: AreaDisplayItem<T>,
): GeoPt | null {
  return polygonCentroidOfRings(item.rings.slice(0, item.outerCount));
}

// ─── HUD components ─────────────────────────────────────────────────────

function ActionRow({
  icon,
  title,
  sub,
  onClick,
  tone = "primary",
  chevron = true,
}: {
  icon: IconName;
  title: string;
  /** Omitted where the title says it all — see CLUB-576 on the redraw row. */
  sub?: string;
  onClick: () => void;
  /**
   * CLUB-576: `destructive` is how Delete reaches the body. It is the panel's
   * only irreversible action, so it is labelled and full width rather than a
   * bare trash icon that could not say whether it removed the point, the zone,
   * or everything.
   */
  tone?: "primary" | "destructive";
  /** False for an action; true for a drill-in. */
  chevron?: boolean;
}) {
  const destructive = tone === "destructive";
  const hoverBg = destructive ? COLORS.errorContainer : COLORS.primaryOpacity8;
  const baseBg = "transparent";
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 12px",
        // CLUB-576: a full-width row is a big target — keep it at the 44px
        // minimum rather than letting a sub-less row shrink below it.
        minHeight: 44,
        border: "none",
        background: baseBg,
        borderRadius: 10,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
      onMouseLeave={(e) => (e.currentTarget.style.background = baseBg)}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: destructive ? COLORS.errorContainer : COLORS.primaryContainer,
          color: destructive ? COLORS.error : COLORS.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={18} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: destructive ? COLORS.error : COLORS.onSurface }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: COLORS.outline }}>{sub}</div>}
      </div>
      {chevron && <Icon name="chevron-right" size={18} color={COLORS.outline} />}
    </button>
  );
}

function FloatingActionBar({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        position: "absolute",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: 8,
        zIndex: 10,
      }}
    >
      {children}
    </div>
  );
}

function CountChip({ value, color, label, sub }: { value: number; color: string; label: string; sub?: string }) {
  return (
    <div
      style={{
        marginTop: 14,
        padding: "10px 12px",
        background: COLORS.surface,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: color,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {value}
      </span>
      <div style={{ fontSize: 13 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: COLORS.outline }}>{sub}</div>}
      </div>
    </div>
  );
}

function BordersListCard({
  displayItems,
  teamColor,
  onNew,
  onSelectProps,
  onEdit,
  onRedraw,
  onRemove,
  onCenterOn,
  onRecolor,
  collapsed,
  onToggleCollapse,
}: {
  /** The team's border colour, so a row's swatch cannot disagree with the map. */
  teamColor?: string;
  /** Recolour one row's border — every parcel of a property selection at once. */
  onRecolor: (item: AreaDisplayItem<Border>, color: string) => void;
  // Pre-grouped by the parent (memoized): each property selection is ONE item
  // (its parcels share an areaGroup) and each manual border is its own item.
  // Display grouping only — state and the save-diff remain per-parcel.
  displayItems: AreaDisplayItem<Border>[];
  onNew: () => void;
  onSelectProps: () => void;
  onEdit: (b: Border) => void;
  onRedraw: (b: Border) => void;
  onRemove: (item: AreaDisplayItem<Border>) => void;
  onCenterOn: (item: AreaDisplayItem<Border>) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  // The row whose colour palette is open, if any.
  const [colourRow, setColourRow] = useState<string | null>(null);
  // Counts reflect the grouped ROWS the user sees, not the per-parcel array.
  const collapsedTitle = displayItems.length === 0 ? t('border.collapsedCreate') : t('border.collapsedCount', { count: displayItems.length });
  return (
    <HudCard width={340} collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={t('border.tag')} collapsedTitle={collapsedTitle}>
      <div style={{ padding: "16px 18px 8px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {t('border.tag')}
        </div>
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 22,
            fontWeight: 400,
            lineHeight: 1.15,
          }}
        >
          {displayItems.length === 0
            ? t('border.headingEmpty')
            : t('border.headingCount', { count: displayItems.length })}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 6, lineHeight: 1.5 }}>
          {t('border.description')}
        </div>
      </div>
      <div style={{ padding: "4px 8px 8px" }}>
        <ActionRow
          icon="edit"
          title={displayItems.length === 0 ? t('border.drawArea') : t('border.addAnother')}
          sub={t('border.drawSub')}
          onClick={onNew}
        />
        <ActionRow
          icon="map"
          title={t('border.selectProperties')}
          sub={t('border.selectPropertiesSub')}
          onClick={onSelectProps}
        />
      </div>

      {displayItems.length > 0 && (
        <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
          <div
            style={{
              padding: "10px 16px 6px",
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.outline,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t('border.existing')}
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {displayItems.map((item) => {
              // Every row can be recoloured here, because the only other pickers sit
              // in the reshape and redraw panels, which a property selection and a
              // stored (imported) outline never open.
              const rowColour = resolveHuntBorderColor({ areaColor: item.isGroup ? item.color : item.members[0].color, teamColor });
              const colourControl = (
                <BorderRowColourButton
                  color={rowColour}
                  open={colourRow === item.key}
                  onToggle={() => setColourRow((k) => (k === item.key ? null : item.key))}
                />
              );
              const colourPalette = colourRow === item.key && (
                <BorderRowPalette
                  color={rowColour}
                  onPick={(c) => {
                    onRecolor(item, c);
                    setColourRow(null);
                  }}
                />
              );
              // A property group is presented as one area: name + parcel-count badge,
              // with centre-on, colour and remove. Its parcels aren't hand-edited, so
              // no rename or vertex-edit here.
              if (item.isGroup) {
                return (
                  <Fragment key={item.key}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 16px",
                      borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
                    }}
                  >
                    <button
                      title={t('border.centreOn')}
                      onClick={() => onCenterOn(item)}
                      style={{ ...listRowBodyStyle, display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 4,
                          flexShrink: 0,
                          // A group can now carry no colour of its own, and
                          // string-concatenating an absent one renders the literal
                          // "undefined55" as a CSS colour.
                          background: resolveHuntBorderColor({ areaColor: item.color, teamColor }) + "55",
                          border: `2px dashed ${resolveHuntBorderColor({ areaColor: item.color, teamColor })}`,
                        }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 13,
                            fontWeight: 600,
                            color: COLORS.onSurface,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.name}
                        </span>
                        <span style={{ display: "block", fontSize: 11, color: COLORS.outline }}>
                          {t('group.parcels', { count: item.members.length })}
                        </span>
                      </span>
                    </button>
                    {colourControl}
                    <button
                      className="wh-btn-icon"
                      onClick={() => onRemove(item)}
                      title={t('border.delete')}
                      style={{ color: COLORS.error }}
                    >
                      <Icon name="trash" size={20} />
                    </button>
                  </div>
                  {colourPalette}
                  </Fragment>
                );
              }

              const b = item.members[0];
              return (
                <Fragment key={item.key}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 16px",
                    borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
                  }}
                >
                  <button
                    title={t('border.centreOn')}
                    onClick={() => onCenterOn(item)}
                    style={{ ...listRowBodyStyle, display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        flexShrink: 0,
                        // Through the shared precedence, like the group row above. The
                        // old `b.color + "55"` and `2px dashed ${b.color}` were a
                        // NaN-string and a literal "2px dashed undefined" the moment a
                        // border carried no colour of its own — and now most do not.
                        // tsc catches the first; the template literal is legal and
                        // would have shipped.
                        background: resolveHuntBorderColor({ areaColor: b.color, teamColor }) + "55",
                        border: `2px dashed ${resolveHuntBorderColor({ areaColor: b.color, teamColor })}`,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: COLORS.onSurface,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.name}
                      </span>
                      <span style={{ display: "block", fontSize: 11, color: COLORS.outline }}>
                        {geometryEditable(b)
                          ? t('border.points', { count: b.rings[0]?.length ?? 0 })
                          : t('border.storedOutline')}
                      </span>
                    </span>
                  </button>
                  {/* No point editing for an outline the editor cannot send back — see
                      geometryEditable. Renaming, recolouring and deleting stay. */}
                  {geometryEditable(b) && (
                    <button className="wh-btn-icon" onClick={() => onEdit(b)} title={t('border.editPoints')}>
                      <Icon name="edit" size={20} />
                    </button>
                  )}
                  {colourControl}
                  <button
                    className="wh-btn-icon"
                    onClick={() => onRemove(item)}
                    title={t('border.delete')}
                    style={{ color: COLORS.error }}
                  >
                    <Icon name="trash" size={20} />
                  </button>
                </div>
                {colourPalette}
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </HudCard>
  );
}

function BorderDrawHud({
  count,
  name,
  isEditing,
  color,
  onRename,
  onRecolor,
  onCancel,
  onFinish,
  collapsed,
  onToggleCollapse,
}: {
  count: number;
  name: string;
  isEditing: boolean;
  color: string;
  onRename: (n: string) => void;
  /** Absent when the host cannot persist a team colour; the picker is then not shown. */
  onRecolor?: (c: string) => void;
  onCancel: () => void;
  onFinish: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  const canFinish = count >= 3;
  return (
    <HudCard width={320} collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={t('border.tag')} collapsedTitle={name || t('borderDraw.collapsedNew')}>
      <div style={{ padding: "16px 18px 14px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.tertiary,
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {isEditing ? t('borderDraw.editing') : t('borderDraw.drawing')}
        </div>
        <div className="wh-field" style={{ marginBottom: 12 }}>
          <label>{t('borderDraw.name')}</label>
          <input className="wh-input" value={name} onChange={(e) => onRename(e.target.value)} />
        </div>
        {onRecolor && (
        <div className="wh-field" style={{ marginBottom: 12 }}>
          <label>{t('borderDraw.colour')}</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
            {BORDER_COLORS.map((c) => {
              const selected = c.toLowerCase() === color.toLowerCase();
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={t('borderDraw.colourAria', { color: c })}
                  aria-pressed={selected}
                  onClick={() => onRecolor(c)}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: c,
                    cursor: "pointer",
                    padding: 0,
                    border: selected ? `2px solid ${COLORS.onSurface}` : "2px solid transparent",
                    outline: selected ? `1px solid ${c}` : "none",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
                  }}
                />
              );
            })}
          </div>
        </div>
        )}
        <div style={{ fontSize: 13, color: COLORS.outline, lineHeight: 1.5 }}>
          {t('borderDraw.instructions')}
        </div>
        <CountChip
          value={count}
          color={COLORS.tertiary}
          label={count === 0 ? t('borderDraw.noPoints') : t('borderDraw.pointsPlaced', { count })}
          sub={count >= 3 ? t('borderDraw.readyToFinish') : t('borderDraw.needMore', { count: 3 - count })}
        />
      </div>
      <MapToolActions onCancel={onCancel} onDone={onFinish} doneDisabled={!canFinish} />
    </HudCard>
  );
}

/** A list row's colour control: a dot in the border's current colour. */
function BorderRowColourButton({ color, open, onToggle }: { color: string; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('map');
  return (
    <button
      type="button"
      className="wh-btn-icon"
      onClick={onToggle}
      title={t('vertex.borderColor')}
      aria-label={t('vertex.borderColor')}
      aria-expanded={open}
    >
      <span style={{ width: 18, height: 18, borderRadius: "50%", background: color, boxShadow: "0 0 0 1px rgba(0,0,0,0.2)", display: "block" }} />
    </button>
  );
}

/** The palette a list row opens under itself. */
function BorderRowPalette({ color, onPick }: { color: string; onPick: (c: string) => void }) {
  const { t } = useTranslation('map');
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "0 16px 12px 48px" }}>
      {BORDER_COLORS.map((c) => {
        const selected = c.toLowerCase() === color.toLowerCase();
        return (
          <button
            key={c}
            type="button"
            aria-label={t('borderDraw.colourAria', { color: c })}
            aria-pressed={selected}
            onClick={() => onPick(c)}
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: c,
              cursor: "pointer",
              padding: 0,
              border: selected ? `2px solid ${COLORS.onSurface}` : "2px solid transparent",
              outline: selected ? `1px solid ${c}` : "none",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
            }}
          />
        );
      })}
    </div>
  );
}

function BorderPropertiesHud({
  count,
  onCancel,
  onDone,
  collapsed,
  onToggleCollapse,
  tag,
  accent = "#a81e8c",
  description,
  canFinish,
}: {
  count: number;
  onCancel: () => void;
  onDone: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  tag?: string;
  accent?: string;
  description?: string;
  // Whether Done may commit. Defaults to "there is a selection". A caller passes
  // this explicitly when finishing can also DELETE a saved parcel the user
  // deselected — a real change to commit even though nothing is selected.
  canFinish?: boolean;
}) {
  const { t } = useTranslation('map');
  const resolvedTag = tag ?? t('border.tag');
  const resolvedDescription = description ?? t('properties.descriptionBorder');
  return (
    <HudCard width={300} collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={resolvedTag} collapsedTitle={t('properties.collapsedTitle')}>
      <div style={{ padding: "16px 18px 14px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: accent,
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {t('properties.heading')}
        </div>
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1.2,
          }}
        >
          {t('properties.title')}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 6, lineHeight: 1.5 }}>
          {resolvedDescription}
        </div>
        <CountChip
          value={count}
          color={accent}
          label={t('properties.selected', { count })}
        />
      </div>
      <MapToolActions onCancel={onCancel} onDone={onDone} doneDisabled={!(canFinish ?? count > 0)} />
    </HudCard>
  );
}

export function ImportMapPanel({
  onClose,
  onPick,
  copyEnabled,
  omaRiistaEnabled,
  sveaskogEnabled,
  reviiriEnabled,
}: {
  onClose: () => void;
  onPick: (id: string) => void;
  copyEnabled?: boolean;
  omaRiistaEnabled?: boolean;
  sveaskogEnabled?: boolean;
  reviiriEnabled?: boolean;
}) {
  const { t } = useTranslation('map');
  // Reviiri is offered only when the host wired all three handlers. Existing sources
  // retain their established availability rules.
  const sources = IMPORT_SOURCES.filter(
    (s) =>
      (s.id !== "copy" || copyEnabled) &&
      (s.id !== "omariista" || omaRiistaEnabled) &&
      (s.id !== "sveaskog" || sveaskogEnabled) &&
      (s.id !== "reviiri" || reviiriEnabled),
  );
  return (
    <HudCard side="right" sideOffset={60} onToggleCollapse={onClose} collapseIcon="x" collapsedTag={t('border.tag')} collapsedTitle={t('import.collapsedTitle')}>
      <HudHeader title={t('import.title')} subtitle={t('import.subtitle')} />
      <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
        {sources.map((s) => (
          <button
            key={s.id}
            onClick={() => onPick(s.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              textAlign: "left",
              marginBottom: 2,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: s.color,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {s.glyph}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{t(`import.sources.${s.id}`)}</div>
              <div style={{ fontSize: 11, color: COLORS.outline }}>{t(`import.sources.${s.id}Sub`)}</div>
            </div>
            <Icon name="chevron-right" size={14} color={COLORS.outline} />
          </button>
        ))}
      </div>
      <div style={{ padding: 12, borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surface }}>
        <button className="wh-btn wh-btn-outline" onClick={onClose} style={{ width: "100%" }}>
          {t('import.cancel')}
        </button>
      </div>
    </HudCard>
  );
}

type CopyStage = "pick" | "loading" | "confirm" | "error" | "done";

/**
 * CLUB-188 — copy an existing hunting-ground map into the current one. The user
 * picks a source team, reviews a summary of what will be copied, confirms, and
 * the boundary/areas/pins are appended as fresh, editable objects. Load failures
 * are surfaced inline with a retry.
 */
function CopyMapFlow({
  sources,
  onLoad,
  onApply,
  onClose,
}: {
  sources: { id: string; name: string }[];
  onLoad: (teamId: string) => Promise<CopyableTeamMap>;
  onApply: (src: CopyableTeamMap) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('map');
  const [stage, setStage] = useState<CopyStage>("pick");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<CopyableTeamMap | null>(null);
  // Ignore results from a superseded/cancelled load (e.g. panel closed mid-fetch).
  const loadToken = useRef(0);

  const selectedName = sources.find((s) => s.id === selectedId)?.name ?? "";

  const load = (teamId: string) => {
    setSelectedId(teamId);
    setStage("loading");
    const token = ++loadToken.current;
    onLoad(teamId)
      .then((map) => {
        if (token !== loadToken.current) return;
        setLoaded(map);
        setStage("confirm");
      })
      .catch(() => {
        if (token !== loadToken.current) return;
        setStage("error");
      });
  };

  const counts = loaded
    ? [
        { n: loaded.boundaryAreas.length, key: "boundaries" },
        { n: loaded.subAreaPolygons.length, key: "subAreas" },
        { n: loaded.forbiddenPolygons.length, key: "forbidden" },
        { n: loaded.objects.length, key: "pins" },
      ].filter((c) => c.n > 0)
    : [];
  const isEmpty = stage === "confirm" && counts.length === 0;

  const confirm = () => {
    if (loaded && counts.length > 0) onApply(loaded);
    setStage("done");
  };

  return (
    <HudCard side="right" sideOffset={60} onToggleCollapse={onClose} collapseIcon="x" collapsedTag={t('border.tag')} collapsedTitle={t('copyFlow.title')}>
      <HudHeader title={t('copyFlow.title')} subtitle={stage === "pick" ? t('copyFlow.subtitle') : undefined} />
      <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
        {stage === "pick" && (
          sources.length === 0 ? (
            <div style={{ fontSize: 13, color: COLORS.outline, padding: "12px 4px" }}>{t('copyFlow.noSources')}</div>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.outline, margin: "2px 4px 8px" }}>
                {t('copyFlow.chooseSource')}
              </div>
              {sources.map((s) => (
                <button
                  key={s.id}
                  onClick={() => load(s.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "10px 12px", borderRadius: 8, border: "none",
                    background: "transparent", cursor: "pointer", textAlign: "left", marginBottom: 2,
                  }}
                >
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                  <Icon name="chevron-right" size={14} color={COLORS.outline} />
                </button>
              ))}
            </>
          )
        )}

        {stage === "loading" && (
          <div style={{ fontSize: 13, color: COLORS.outline, padding: "12px 4px" }}>{t('copyFlow.loading')}</div>
        )}

        {stage === "error" && (
          <div style={{ fontSize: 13, color: COLORS.error, padding: "12px 4px" }}>{t('copyFlow.loadError')}</div>
        )}

        {stage === "confirm" && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
              {t('copyFlow.summaryTitle', { team: selectedName })}
            </div>
            {isEmpty ? (
              <div style={{ fontSize: 13, color: COLORS.outline }}>{t('copyFlow.empty')}</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
                {counts.map((c) => (
                  <li key={c.key}>{t(`copyFlow.${c.key}`, { count: c.n })}</li>
                ))}
              </ul>
            )}
          </>
        )}

        {stage === "done" && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{t('copyFlow.doneTitle')}</div>
            <div style={{ fontSize: 13, color: COLORS.outline }}>{t('copyFlow.doneBody')}</div>
          </>
        )}
      </div>

      <div style={{ padding: 12, borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surface, display: "flex", gap: 8 }}>
        {stage === "confirm" && (
          <>
            <button className="wh-btn wh-btn-outline" onClick={() => setStage("pick")} style={{ flex: 1 }}>
              {t('copyFlow.back')}
            </button>
            <button className="wh-btn wh-btn-primary" onClick={confirm} disabled={isEmpty} style={{ flex: 1 }}>
              {t('copyFlow.confirm')}
            </button>
          </>
        )}
        {stage === "error" && (
          <>
            <button className="wh-btn wh-btn-outline" onClick={onClose} style={{ flex: 1 }}>
              {t('copyFlow.cancel')}
            </button>
            <button className="wh-btn wh-btn-primary" onClick={() => selectedId && load(selectedId)} style={{ flex: 1 }}>
              {t('copyFlow.retry')}
            </button>
          </>
        )}
        {stage === "done" && (
          <button className="wh-btn wh-btn-primary" onClick={onClose} style={{ width: "100%" }}>
            {t('copyFlow.close')}
          </button>
        )}
        {(stage === "pick" || stage === "loading") && (
          <button className="wh-btn wh-btn-outline" onClick={onClose} style={{ width: "100%" }}>
            {t('copyFlow.cancel')}
          </button>
        )}
      </div>
    </HudCard>
  );
}

type OmaRiistaStage = "input" | "submitting" | "created" | "refreshed" | "not-found" | "error";

/**
 * CLUB-190 — import an Oma Riista hunting area by its public 10-character code.
 * No sign-in: the user types the code, the backend resolves the official
 * boundary via Tanger and writes the hunting ground. Success is reported as
 * created vs refreshed; an unknown code shows a not-found message with a retry.
 */
export function OmaRiistaImportFlow({
  onImport,
  onClose,
}: {
  onImport: (areaCode: string) => Promise<OmaRiistaImportResult>;
  onClose: () => void;
}) {
  const { t } = useTranslation('map');
  const [stage, setStage] = useState<OmaRiistaStage>("input");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [code, setCode] = useState("");
  // Ignore results from a superseded submit (e.g. panel closed mid-request).
  const submitToken = useRef(0);
  const valid = isValidAreaCode(code);

  const submit = () => {
    if (!valid || stage === "submitting") return;
    const normalized = normalizeAreaCode(code);
    setCode(normalized);
    setStage("submitting");
    const token = ++submitToken.current;
    onImport(normalized)
      .then((result) => {
        if (token !== submitToken.current) return;
        if (result.status === "error") {
          setErrorCode(result.code);
          setStage("error");
        } else {
          setStage(result.status);
        }
      })
      .catch(() => {
        if (token !== submitToken.current) return;
        setErrorCode(null);
        setStage("error");
      });
  };

  const isDone = stage === "created" || stage === "refreshed";
  const doneTitle = stage === "created" ? t('omaRiista.createdTitle') : t('omaRiista.refreshedTitle');
  const doneBody = stage === "created" ? t('omaRiista.createdBody') : t('omaRiista.refreshedBody');

  return (
    <HudCard side="right" sideOffset={60} onToggleCollapse={onClose} collapseIcon="x" collapsedTag={t('border.tag')} collapsedTitle={t('omaRiista.title')}>
      <HudHeader title={t('omaRiista.title')} subtitle={stage === "input" ? t('omaRiista.subtitle') : undefined} />
      <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
        {(stage === "input" || stage === "submitting" || stage === "not-found" || stage === "error") && (
          <>
            <label htmlFor="oma-riista-code" style={{ display: "block", fontSize: 12, fontWeight: 700, color: COLORS.outline, margin: "2px 4px 8px" }}>
              {t('omaRiista.codeLabel')}
            </label>
            <input
              id="oma-riista-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder={t('omaRiista.codePlaceholder')}
              maxLength={10}
              autoFocus
              disabled={stage === "submitting"}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${COLORS.surfaceContainerHighest}`,
                background: COLORS.surface,
                color: COLORS.onSurface,
                fontSize: 15,
                letterSpacing: 2,
                fontFamily: "ui-monospace, monospace",
                boxSizing: "border-box",
              }}
            />
            <div style={{ fontSize: 11, color: COLORS.outline, margin: "6px 4px 0" }}>{t('omaRiista.codeHint')}</div>
            {stage === "not-found" && (
              <div style={{ fontSize: 13, color: COLORS.error, margin: "12px 4px 0" }}>{t('omaRiista.notFound')}</div>
            )}
            {stage === "error" && (
              <div style={{ fontSize: 13, color: COLORS.error, margin: "12px 4px 0" }}>
                {t('omaRiista.error')}
                {errorCode && (
                  <div style={{ marginTop: 4, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                    {t('omaRiista.errorCode', { code: errorCode })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {isDone && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{doneTitle}</div>
            <div style={{ fontSize: 13, color: COLORS.outline }}>{doneBody}</div>
          </>
        )}
      </div>

      <div style={{ padding: 12, borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surface, display: "flex", gap: 8 }}>
        {isDone ? (
          <button className="wh-btn wh-btn-primary" onClick={onClose} style={{ width: "100%" }}>
            {t('omaRiista.close')}
          </button>
        ) : (
          <>
            <button className="wh-btn wh-btn-outline" onClick={onClose} style={{ flex: 1 }} disabled={stage === "submitting"}>
              {t('omaRiista.cancel')}
            </button>
            <button className="wh-btn wh-btn-primary" onClick={submit} disabled={!valid || stage === "submitting"} style={{ flex: 1 }}>
              {stage === "submitting" ? t('omaRiista.submitting') : t('omaRiista.submit')}
            </button>
          </>
        )}
      </div>
    </HudCard>
  );
}

type SveaskogStage = "input" | "submitting" | "created" | "refreshed" | "not-found" | "error";

/**
 * CLUB-189 — import a Sveaskog property boundary by its objektnummer.
 *
 * No sign-in: the objektnummer is on the user's lease, and the backend resolves
 * the official boundary through Tanger. Sveaskog is a Swedish-only source, so
 * the Swedish copy is the one that matters most here.
 *
 * The header's close ("x") control stays live during a submit, matching
 * OmaRiistaImportFlow: the fetch chain behind onImport has no request
 * timeout and these panels have no backdrop, click-outside, or Escape
 * handler, so withdrawing the only exit would trap the user behind a hung
 * request with no way out short of reloading the page. onClose is the
 * single close affordance the component ever calls (header and footer
 * alike). submitToken is inherited verbatim from OmaRiistaImportFlow's
 * identical guard, kept for the same reason: submit() early-returns while
 * `submitting`, so within one mounted instance no second submit is ever
 * outstanding, and `token !== submitToken.current` can never actually be
 * true today. What actually keeps a result that arrives after an early
 * close from touching state is that the host unmounts this component when
 * the panel closes — a late `.then`/`.catch` then has no live component to
 * call setState on. The guard costs nothing and would start doing real work
 * the moment a future change lets submit() re-enter while a request is
 * still outstanding.
 */
export function SveaskogImportFlow({
  onImport,
  onClose,
}: {
  onImport: (objektnummer: string) => Promise<SveaskogImportResult>;
  onClose: () => void;
}) {
  const { t } = useTranslation('map');
  const [stage, setStage] = useState<SveaskogStage>("input");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [code, setCode] = useState("");
  // Ignore results from a superseded submit (e.g. panel closed mid-request).
  const submitToken = useRef(0);
  const valid = isValidObjektnummer(code);
  const submitting = stage === "submitting";

  const submit = () => {
    if (!valid || submitting) return;
    const normalized = normalizeObjektnummer(code);
    setCode(normalized);
    setStage("submitting");
    const token = ++submitToken.current;
    onImport(normalized)
      .then((result) => {
        if (token !== submitToken.current) return;
        if (result.status === "error") {
          setErrorCode(result.code);
          setStage("error");
        } else {
          setStage(result.status);
        }
      })
      .catch(() => {
        if (token !== submitToken.current) return;
        setErrorCode(null);
        setStage("error");
      });
  };

  const isDone = stage === "created" || stage === "refreshed";
  const doneTitle = stage === "created" ? t('sveaskog.createdTitle') : t('sveaskog.refreshedTitle');
  const doneBody = stage === "created" ? t('sveaskog.createdBody') : t('sveaskog.refreshedBody');

  return (
    <HudCard
      side="right"
      sideOffset={60}
      onToggleCollapse={onClose}
      collapseIcon="x"
      collapsedTag={t('border.tag')}
      collapsedTitle={t('sveaskog.title')}
    >
      <HudHeader title={t('sveaskog.title')} subtitle={stage === "input" ? t('sveaskog.subtitle') : undefined} />
      <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
        {(stage === "input" || stage === "submitting" || stage === "not-found" || stage === "error") && (
          <>
            <label htmlFor="sveaskog-objektnummer" style={{ display: "block", fontSize: 12, fontWeight: 700, color: COLORS.outline, margin: "2px 4px 8px" }}>
              {t('sveaskog.codeLabel')}
            </label>
            <input
              id="sveaskog-objektnummer"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder={t('sveaskog.codePlaceholder')}
              // inputMode surfaces the numeric keypad on touch devices. The
              // digits-and-length rule (1-10 digits) is enforced entirely by
              // isValidObjektnummer, not by the input itself — deliberately no
              // maxLength here, so an over-long or letter-bearing paste stays
              // fully visible and simply fails validation (showing the hint)
              // rather than being silently truncated into a shorter, different,
              // valid-looking objektnummer.
              inputMode="numeric"
              autoFocus
              disabled={submitting}
              autoComplete="off"
              spellCheck={false}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${COLORS.surfaceContainerHighest}`,
                background: COLORS.surface,
                color: COLORS.onSurface,
                fontSize: 15,
                letterSpacing: 2,
                fontFamily: "ui-monospace, monospace",
                boxSizing: "border-box",
              }}
            />
            <div style={{ fontSize: 11, color: COLORS.outline, margin: "6px 4px 0" }}>{t('sveaskog.codeHint')}</div>
            {stage === "not-found" && (
              <div style={{ fontSize: 13, color: COLORS.error, margin: "12px 4px 0" }}>{t('sveaskog.notFound')}</div>
            )}
            {stage === "error" && (
              <div style={{ fontSize: 13, color: COLORS.error, margin: "12px 4px 0" }}>
                {t('sveaskog.error')}
                {errorCode && (
                  <div style={{ marginTop: 4, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                    {t('sveaskog.errorCode', { code: errorCode })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {isDone && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{doneTitle}</div>
            <div style={{ fontSize: 13, color: COLORS.outline }}>{doneBody}</div>
          </>
        )}
      </div>

      <div style={{ padding: 12, borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surface, display: "flex", gap: 8 }}>
        {isDone ? (
          <button className="wh-btn wh-btn-primary" onClick={onClose} style={{ width: "100%" }}>
            {t('sveaskog.close')}
          </button>
        ) : (
          <>
            <button className="wh-btn wh-btn-outline" onClick={onClose} style={{ flex: 1 }} disabled={submitting}>
              {t('sveaskog.cancel')}
            </button>
            <button className="wh-btn wh-btn-primary" onClick={submit} disabled={!valid || submitting} style={{ flex: 1 }}>
              {submitting ? t('sveaskog.submitting') : t('sveaskog.submit')}
            </button>
          </>
        )}
      </div>
    </HudCard>
  );
}

/**
 * CLUB-576 review: in every layer list, the whole row left of the icon buttons
 * centres the map on that object. It replaces the per-list colour square /
 * line-sample click target — which in the area lists showed the record's stored
 * colour, blue on a shape the canvas draws magenta — and keeps each row to its
 * two icons.
 */
const listRowBodyStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  textAlign: "left",
  cursor: "pointer",
  fontFamily: "inherit",
  color: "inherit",
};

function AreasListCard({
  title,
  singular,
  accent,
  displayItems,
  onNew,
  onSelectProps,
  onEdit,
  onRedraw,
  onRename,
  onRemove,
  onCenterOn,
  collapsed,
  onToggleCollapse,
}: {
  /** Plural, for headings and the list label ("Sub-areas"). */
  title: string;
  /** Singular, for the empty state's invitation ("…your first sub-area"). */
  singular: string;
  accent: string;
  // Pre-grouped by the parent (memoized): each property selection is one item,
  // each manual area its own. Display grouping only — storage/save stay per-parcel.
  displayItems: AreaDisplayItem<Area>[];
  onNew: () => void;
  onSelectProps: () => void;
  onEdit: (a: Area) => void;
  onRedraw: (a: Area) => void;
  /** Renames a row: every member record of a property group, or the one area. */
  onRename: (item: AreaDisplayItem<Area>, name: string) => void;
  onRemove: (item: AreaDisplayItem<Area>) => void;
  onCenterOn: (item: AreaDisplayItem<Area>) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  // The row whose name is open for editing, by display key. Renaming lives on the
  // list rather than in the vertex-edit panel because that panel is closed to most
  // areas: a property selection and a stored or multi-ring outline cannot be
  // reshaped, and they need a name all the same.
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const renameButton = (item: AreaDisplayItem<Area>) => (
    <button className="wh-btn-icon" onClick={() => setRenamingKey(item.key)} title={t('area.rename')}>
      <Icon name="rename" size={20} />
    </button>
  );
  const nameEditor = (item: AreaDisplayItem<Area>) => (
    <InlineNameEditor
      initialName={item.name}
      onCommit={(name) => {
        onRename(item, name);
        setRenamingKey(null);
      }}
      onCancel={() => setRenamingKey(null)}
    />
  );
  // Counts reflect the grouped ROWS the user sees, not the per-parcel array.
  return (
    <HudCard collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={title} collapsedTitle={t('area.collapsedCount', { count: displayItems.length, plural: title.toLowerCase() })}>
      {/* CLUB-576 review: the sub-area and forbidden panels used to list their
          objects first and hang two bare buttons off the bottom, while the
          border and line panels led with their actions. They now follow the
          border panel: header, then the actions, then the objects. */}
      <div style={{ padding: "16px 18px 8px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            // One colour for every panel's layer tag, as in the border and map
            // pin panels: the accent belongs to the shape on the map, not to
            // the label naming the layer.
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 22,
            fontWeight: 400,
            lineHeight: 1.15,
          }}
        >
          {displayItems.length === 0
            ? t('area.headingEmpty', { title: singular.toLowerCase() })
            : t('area.headerCount', { title, count: displayItems.length })}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 6, lineHeight: 1.5 }}>
          {t('area.description', { plural: title.toLowerCase() })}
        </div>
      </div>
      <div style={{ padding: "4px 8px 8px" }}>
        <ActionRow
          icon="edit"
          title={t('area.drawNew')}
          sub={t('area.drawNewSub')}
          onClick={onNew}
        />
        <ActionRow
          icon="map"
          title={t('area.selectProperties')}
          sub={t('area.selectPropertiesSub')}
          onClick={onSelectProps}
        />
      </div>

      {displayItems.length > 0 && (
        <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
          <div
            style={{
              padding: "10px 16px 6px",
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.outline,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t('area.existing', { plural: title.toLowerCase() })}
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {displayItems.map((item) => {
            // Property group: name + parcel-count badge, rename, remove and
            // centre-on (no per-vertex edit — parcels aren't hand-edited).
            if (item.isGroup) {
              return (
                <div
                  key={item.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    // Flush with the "Existing …" label above and with the
                    // border panel's rows — 10px left them short of both.
                    padding: "10px 16px",
                  }}
                >
                  {renamingKey === item.key ? nameEditor(item) : (
                    <>
                      <button
                        onClick={() => onCenterOn(item)}
                        title={t('area.centreOn')}
                        style={{ ...listRowBodyStyle }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.outline }}>
                          {t('group.parcels', { count: item.members.length })}
                        </div>
                      </button>
                      {renameButton(item)}
                    </>
                  )}
                  <button
                    className="wh-btn-icon"
                    onClick={() => onRemove(item)}
                    title={t('area.remove')}
                    style={{ color: COLORS.error }}
                  >
                    <Icon name="trash" size={20} />
                  </button>
                </div>
              );
            }
            const a = item.members[0];
            return (
              <div
                key={item.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 16px",
                }}
              >
                {renamingKey === item.key ? nameEditor(item) : (
                  <>
                    <button
                      onClick={() => onCenterOn(item)}
                      title={t('area.centreOn')}
                      style={{ ...listRowBodyStyle, fontSize: 13, fontWeight: 600 }}
                    >
                      {a.name}
                    </button>
                    {renameButton(item)}
                    {/* No point editing for an outline the editor cannot send back - see
                        geometryEditable. Renaming and deleting stay. */}
                    {geometryEditable(a) && (
                      <button className="wh-btn-icon" onClick={() => onEdit(a)} title={t('area.editVertices')}>
                        <Icon name="edit" size={20} />
                      </button>
                    )}
                  </>
                )}
                <button
                  className="wh-btn-icon"
                  onClick={() => onRemove(item)}
                  title={t('area.remove')}
                  style={{ color: COLORS.error }}
                >
                  <Icon name="trash" size={20} />
                </button>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </HudCard>
  );
}

function AreaDrawHud({
  title,
  accent,
  count,
  name,
  onRename,
  onCancel,
  onFinish,
  collapsed,
  onToggleCollapse,
}: {
  title: string;
  accent: string;
  count: number;
  name: string;
  onRename: (n: string) => void;
  onCancel: () => void;
  onFinish: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  const canFinish = count >= 3;
  return (
    <HudCard collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={title} collapsedTitle={name || t('areaDraw.collapsedNew', { title: title.toLowerCase() })}>
      <HudHeader title={t('areaDraw.heading', { title: title.toLowerCase() })} subtitle={t('areaDraw.subtitle')} accent={accent} />
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <input
          className="wh-input"
          value={name}
          onChange={(e) => onRename(e.target.value)}
          placeholder={t('areaDraw.namePlaceholder', { title })}
        />
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {t('areaDraw.pointsPlaced', { count })}
        </div>
      </div>
      <MapToolActions
        onCancel={onCancel}
        onDone={onFinish}
        doneDisabled={!canFinish}
        doneAccent={accent}
      />
    </HudCard>
  );
}

function VertexEditHud({
  kind,
  target,
  color,
  onRename,
  onRecolor,
  onRedraw,
  onCancel,
  onDelete,
  onDone,
  collapsed,
  onToggleCollapse,
}: {
  kind: "border" | "subarea" | "forbidden";
  target: Border | Area | null;
  color: string;
  onRename: (n: string) => void;
  onRecolor?: (color: string) => void;
  onRedraw: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onDone: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  const [showColors, setShowColors] = useState(false);
  if (!target) return null;
  const kindTag = kind === "border" ? t('vertex.tagBorder') : kind === "subarea" ? t('vertex.tagSubarea') : t('vertex.tagForbidden');
  const labels = {
    border: {
      tag: t('vertex.editingBorder'),
      body: t('vertex.bodyBorder'),
    },
    subarea: {
      tag: t('vertex.editingSubarea'),
      body: t('vertex.bodySubarea'),
    },
    forbidden: {
      tag: t('vertex.editingForbidden'),
      body: t('vertex.bodyForbidden'),
    },
  } as const;
  const label = labels[kind];

  return (
    <HudCard
      width={320}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      collapsedTag={kindTag}
      collapsedTitle={target.name}
      collapsedActions={
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            title={t('toolActions.delete')}
            onClick={onDelete}
            className="wh-btn-icon"
            style={{ color: COLORS.error, height: 44, width: 40, borderRadius: 0, background: "none" }}
          >
            <Icon name="trash" size={20} />
          </button>
          <button
            title={t('toolActions.done')}
            onClick={onDone}
            className="wh-btn-icon"
            style={{ color: COLORS.primary, height: 44, width: 40, borderRadius: 0, background: "none" }}
          >
            <Icon name="check" size={20} />
          </button>
        </div>
      }
    >
      <div style={{ padding: "16px 18px 12px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color,
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {label.tag}
        </div>
        <div className="wh-field" style={{ marginBottom: 12 }}>
          <label>{t('vertex.name')}</label>
          <input className="wh-input" value={target.name} onChange={(e) => onRename(e.target.value)} />
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, lineHeight: 1.5 }}>{label.body}</div>
      </div>
      {onRecolor && (
        <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
          <button
            onClick={() => setShowColors((v) => !v)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              color: COLORS.onSurface,
              textAlign: "left",
            }}
          >
            <span style={{ width: 16, height: 16, borderRadius: "50%", background: color, flexShrink: 0, border: "2px solid rgba(0,0,0,0.12)" }} />
            <Icon name="palette" size={16} color={COLORS.outline} />
            {t('vertex.borderColor')}
            <Icon name={showColors ? "chevron-down" : "chevron-right"} size={16} color={COLORS.outline} style={{ marginLeft: "auto" }} />
          </button>
          {showColors && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "4px 12px 12px" }}>
              {BORDER_COLORS.map((c) => (
                <button
                  key={c}
                  // A colour swatch with no accessible name is a button that reads as
                  // nothing at all. The hex is not display copy, so it needs no
                  // translation, and it is the one label that is unambiguous.
                  aria-label={c}
                  title={c}
                  onClick={() => { onRecolor(c); setShowColors(false); }}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: c,
                    border: color === c ? "3px solid white" : "2px solid rgba(0,0,0,0.12)",
                    boxShadow: color === c ? `0 0 0 2px ${c}` : "none",
                    cursor: "pointer",
                    padding: 0,
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, padding: "8px 8px" }}>
        <ActionRow icon="edit" title={t('vertex.redraw')} onClick={onRedraw} chevron={false} />
      </div>
      {/* CLUB-576: last row of the body, labelled with the type it removes.
           Identical in every editor, forbidden areas included — the plate the
           review asked for there read as a different control, not the same one. */}
      <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, padding: "8px 8px" }}>
        <ActionRow
          icon="trash"
          title={t(DELETE_LABEL_KEY[kind])}
          onClick={onDelete}
          tone="destructive"
          chevron={false}
        />
      </div>
      <MapToolActions onCancel={onCancel} onDone={onDone} />
    </HudCard>
  );
}

function PinsActionsCard({ count, folderCount, onAdd, onBrowse, collapsed, onToggleCollapse }: { count: number; folderCount: number; onAdd: () => void; onBrowse: () => void; collapsed: boolean; onToggleCollapse: () => void }) {
  const { t } = useTranslation('map');
  return (
    <HudCard collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={t('layer.pins')} collapsedTitle={t('pins.collapsed', { count })}>
      {/* CLUB-576 review: the border panel's shape — tag, heading, description,
          then the action as a row.
          CLUB-676: the pins layer now has a list of its own — the collection —
          reached by the second row. Tapping a pin on the map still opens it for
          editing; the list is the way in when you know the pin's name but not
          where on the ground it is. */}
      <div style={{ padding: "16px 18px 8px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {t('layer.pins')}
        </div>
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 22,
            fontWeight: 400,
            lineHeight: 1.15,
          }}
        >
          {t('pins.title', { count })}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 6, lineHeight: 1.5 }}>
          {t('pins.subtitle')} {t('pins.hint')}
        </div>
      </div>
      <div style={{ padding: "4px 8px 8px" }}>
        <ActionRow icon="plus" title={t('pins.add')} sub={t('pins.addSub')} onClick={onAdd} />
        <ActionRow
          icon="folder"
          title={t('pins.browse')}
          sub={t('pins.browseSub', { count, folderCount })}
          onClick={onBrowse}
        />
      </div>
    </HudCard>
  );
}

/**
 * CLUB-676 — the pin collection, open in the editor.
 *
 * The same {@link PinFolderTree} the team's Map tab shows, given the editor's
 * full height and its selection. It is not a `HudCard`: a card sizes to its
 * content, and a 500-pin folder has to scroll inside a panel pinned to the
 * viewport rather than run off the bottom of one.
 *
 * Clicking a row selects that pin on the map — the sprite highlights, and the
 * map's own selection checks the row back — instead of opening the edit card,
 * which would tear down the list mid-read. Editing a pin is still a tap on the
 * map; this is the way in when you know the name and not the place.
 */
function PinCollectionHud({
  folders,
  pins,
  lines,
  selectedId,
  onSelect,
  onFocus,
  onClose,
  ops,
}: {
  folders: readonly PinFolder[];
  pins: readonly MapObject[];
  lines: readonly GeoLine[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onFocus: (pt: GeoPt) => void;
  onClose: () => void;
  /** CLUB-676: the same editing surface the Map tab's panel offers. */
  ops?: PinCollectionOps;
}) {
  const { t } = useTranslation('map');
  return (
    <div
      data-map-panel="true"
      data-map-print="hide"
      style={{
        position: "absolute",
        top: 16,
        right: 16,
        bottom: 16,
        zIndex: 10,
        width: 340,
        display: "flex",
        flexDirection: "column",
        background: COLORS.surfaceContainerLowest,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        title={t('pins.browseClose')}
        aria-label={t('pins.browseClose')}
        className="wh-btn-icon"
        style={{ position: "absolute", top: 10, right: 8, zIndex: 1 }}
      >
        <Icon name="close" size={18} />
      </button>
      <PinFolderTree
        folders={folders}
        pins={pins}
        lines={lines}
        selectedId={selectedId}
        onSelect={onSelect}
        onFocus={onFocus}
        {...ops}
      />
    </div>
  );
}

function PlacementBanner({
  placeMode,
  sessionCount,
  onDone,
  onCancel,
}: {
  placeMode: NonNullable<PlaceMode>;
  sessionCount: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  return (
    <div
      style={{
        position: "absolute",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        background: COLORS.surfaceContainerLowest,
        borderRadius: 999,
        padding: "8px 14px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        zIndex: 4,
        fontSize: 13,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon name="pin" size={16} color={COLORS.tertiary} />
        {t('placement.instruction')} <b>{catalog.label(placeMode.type)}</b>
        {sessionCount > 0 && <span style={{ color: COLORS.outline }}>· {t('placement.placed', { count: sessionCount })}</span>}
      </div>
      <MapToolActions variant="inline" onCancel={onCancel} onDone={onDone} />
    </div>
  );
}

function ComingSoonCard({ tab, collapsed, onToggleCollapse }: { tab: { label: string; icon: IconName }; collapsed: boolean; onToggleCollapse: () => void }) {
  const { t } = useTranslation('map');
  return (
    <HudCard collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={tab.label} collapsedTitle={t('comingSoon.collapsedTitle')}>
      <div style={{ padding: 22, textAlign: "center" }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 999,
            margin: "0 auto 12px",
            background: COLORS.primaryContainer,
            color: COLORS.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name={tab.icon} size={20} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('comingSoon.title', { label: tab.label })}</div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {t('comingSoon.body')}
        </div>
      </div>
    </HudCard>
  );
}

// CLUB-187: panel for the Lines / Arrows layers — list existing annotations of
// the active kind (with delete), start a new draw, and show the draw HUD.
// ─── Lines & arrows (CLUB-187) — app-parity panels ──────────────────────────
// The idle list follows the same conventions as BordersListCard/AreasListCard;
// the draw/edit HUDs carry the app's editable set (name, colour, style,
// thickness — mobile edit_map_line_page).

function LineSample({ color, width, style, w = 26, h = 18 }: { color: string; width: number; style: GeoLineStyle; w?: number; h?: number }) {
  const sw = Math.max(2, Math.min(8, width / 3));
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden style={{ display: "block", flexShrink: 0 }}>
      <line x1={2} y1={h - 3} x2={w - 2} y2={3} stroke={color} strokeWidth={sw} strokeLinecap="round" strokeDasharray={lineDashArray(style, sw)} />
    </svg>
  );
}

/** The design-system line-style glyphs (solid / dashed diagonal), tinted via
 * currentColor — the web twin of the app's mapLineStyleAsset icons. */
function LineStyleIcon({ dashed, size = 24 }: { dashed?: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block", flexShrink: 0 }}>
      <path
        d="M4.16309 18.6088L17.9757 3.66943"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray={dashed ? "4 3" : undefined}
      />
    </svg>
  );
}

// The app's thickness_arrow.svg (white squiggle inside the slider thumb).
const THICKNESS_THUMB_ICON_PATH =
  "M9.69059 16C8.9042 16 8.25264 15.7399 7.73591 15.2198C7.21918 14.6998 6.96082 14.053 6.96082 13.2795C6.96082 12.1478 7.43389 11.1327 8.38002 10.2344C9.326 9.336 10.4991 8.79035 11.8992 8.59741C11.8401 7.91192 11.6824 7.40298 11.4261 7.07059C11.1699 6.7382 10.8109 6.572 10.349 6.572C9.8425 6.572 9.30884 6.77255 8.74805 7.17365C8.18727 7.5749 7.51251 8.24973 6.72377 9.19812C5.6577 10.488 4.84161 11.3465 4.27549 11.7736C3.70922 12.2008 3.11802 12.4144 2.5019 12.4144C1.78073 12.4144 1.18342 12.1511 0.709956 11.6245C0.236652 11.0977 0 10.435 0 9.63624C0 8.86149 0.17065 8.0582 0.511949 7.22635C0.853249 6.39435 1.41466 5.42808 2.19618 4.32753C2.5483 3.84125 2.80243 3.4389 2.95858 3.12047C3.11473 2.80188 3.1928 2.5382 3.1928 2.32941C3.1928 2.17741 3.15816 2.06486 3.08886 1.99177C3.01941 1.91882 2.91837 1.88235 2.78574 1.88235C2.59886 1.88235 2.38478 1.97137 2.14351 2.14941C1.90239 2.32729 1.62803 2.60204 1.32044 2.97365L0 1.64706C0.465465 1.10776 0.931244 0.698431 1.39734 0.419059C1.86343 0.139686 2.32623 0 2.78574 0C3.43463 0 3.97825 0.217804 4.41659 0.653412C4.85493 1.08902 5.07411 1.63804 5.07411 2.30047C5.07411 2.73733 4.96107 3.21514 4.735 3.73388C4.50877 4.25278 4.1213 4.89953 3.57259 5.67412C2.99503 6.50314 2.56876 7.23169 2.29378 7.85977C2.01879 8.48784 1.8813 9.05106 1.8813 9.54941C1.8813 9.85216 1.93798 10.0916 2.05132 10.2678C2.16467 10.4441 2.31486 10.5322 2.5019 10.5322C2.6767 10.5322 2.841 10.4845 2.9948 10.3892C3.14844 10.2938 3.37302 10.0995 3.66854 9.80635C3.87235 9.60478 4.10476 9.3538 4.3658 9.05341C4.62698 8.75302 4.94328 8.38863 5.31468 7.96024C6.30847 6.77169 7.18704 5.92941 7.95038 5.43341C8.71372 4.93757 9.51021 4.68965 10.3399 4.68965C11.3239 4.68965 12.1229 5.03051 12.7366 5.71224C13.3506 6.39396 13.6985 7.33153 13.7805 8.52494H16V10.4073H13.7805C13.6672 12.1097 13.2403 13.4675 12.4998 14.4805C11.7594 15.4935 10.823 16 9.69059 16ZM9.71951 14.1176C10.2875 14.1176 10.765 13.7936 11.1521 13.1456C11.5392 12.4976 11.7835 11.6186 11.8849 10.5085C11.0612 10.6449 10.3481 10.9831 9.74561 11.5231C9.14328 12.063 8.84212 12.6262 8.84212 13.2127C8.84212 13.4866 8.91988 13.7059 9.0754 13.8706C9.23108 14.0353 9.44578 14.1176 9.71951 14.1176Z";

/** The app's line-thickness slider (SettingsSliderWidget + _ThicknessThumb):
 * −/+ steppers, a 4px ticked track, and a primary-coloured thumb that grows
 * with the value (24→44px) carrying the white thickness squiggle.
 *
 * Both of the app's laws apply — a continuous drag and a one-tick stepper; see
 * the thickness-picker block in mapLines.ts for why they differ. */
function LineThicknessSlider({
  value,
  onChange,
  label,
  decreaseLabel,
  increaseLabel,
}: {
  value: number;
  onChange: (v: number) => void;
  label: string;
  decreaseLabel: string;
  increaseLabel: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const TICKS = LINE_WIDTH_TICKS;
  const t = lineWidthFraction(value);
  const thumbSize = 24 + (44 - 24) * t;
  const iconSize = thumbSize * 0.55;
  const step = (ticks: number) => onChange(stepLineWidth(value, ticks));

  const valueFromClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return value;
    const r = el.getBoundingClientRect();
    return lineWidthFromFraction((clientX - r.left) / r.width);
  };

  const stepBtn: React.CSSProperties = {
    background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
    fontSize: 20, lineHeight: 1, color: COLORS.onSurface, padding: "4px 8px", flexShrink: 0,
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 12px 12px" }}>
      <button style={stepBtn} aria-label={decreaseLabel} onClick={() => step(-1)}>
        −
      </button>
      <div
        ref={trackRef}
        role="slider"
        aria-label={label}
        aria-valuemin={LINE_MIN_WIDTH}
        aria-valuemax={LINE_MAX_WIDTH}
        // Drag values are continuous, so the raw number is e.g.
        // 8.866319336120297 — announced digit by digit. `valuetext` carries the
        // same short form the panel shows the user.
        aria-valuenow={Math.round(value * 10) / 10}
        aria-valuetext={formatLineWidth(value)}
        tabIndex={0}
        onKeyDown={(e) => {
          // Keyboard mirrors the −/+ buttons: the app has no keyboard, so its
          // stepper is the only law there is to follow here.
          if (e.key === "ArrowLeft" || e.key === "ArrowDown") step(-1);
          if (e.key === "ArrowRight" || e.key === "ArrowUp") step(1);
        }}
        onPointerDown={(e) => {
          draggingRef.current = true;
          (e.target as Element).setPointerCapture?.(e.pointerId);
          onChange(valueFromClientX(e.clientX));
        }}
        onPointerMove={(e) => {
          if (draggingRef.current) onChange(valueFromClientX(e.clientX));
        }}
        onPointerUp={() => { draggingRef.current = false; }}
        onPointerCancel={() => { draggingRef.current = false; }}
        style={{ position: "relative", flex: 1, height: 48, cursor: "pointer", touchAction: "none" }}
      >
        {/* base track + ticks */}
        <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 4, marginTop: -2, borderRadius: 2, background: COLORS.surfaceContainerHighest }} />
        {Array.from({ length: TICKS }, (_, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${(i / (TICKS - 1)) * 100}%`,
              top: "50%",
              width: 2,
              height: 2,
              margin: "-1px 0 0 -1px",
              borderRadius: 1,
              background: COLORS.outline,
            }}
          />
        ))}
        {/* filled portion */}
        <div style={{ position: "absolute", left: 0, width: `${t * 100}%`, top: "50%", height: 4, marginTop: -2, borderRadius: 2, background: COLORS.primary }} />
        {/* thumb */}
        <div
          style={{
            position: "absolute",
            left: `${t * 100}%`,
            top: "50%",
            width: thumbSize,
            height: thumbSize,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background: COLORS.primary,
            boxShadow: "0 1px 1px 1px rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <svg width={iconSize} height={iconSize} viewBox="0 0 16 16" fill="none" aria-hidden>
            <path d={THICKNESS_THUMB_ICON_PATH} fill="#fff" />
          </svg>
        </div>
      </div>
      <button style={stepBtn} aria-label={increaseLabel} onClick={() => step(1)}>
        +
      </button>
    </div>
  );
}

/** Shared name/colour/style/thickness form used by the draw and edit HUDs —
 * the web twin of the app's edit rows + picker sheets. */
function LineStyleControls({
  noun,
  name,
  color,
  width,
  style,
  onPatch,
}: {
  noun: string;
  name: string;
  color: string;
  width: number;
  style: GeoLineStyle;
  onPatch: (patch: Partial<{ name: string; color: string; width: number; style: GeoLineStyle }>) => void;
}) {
  const { t } = useTranslation('map');
  const [open, setOpen] = useState<null | "color" | "style">(null);
  const rowBtn: React.CSSProperties = {
    width: "100%", display: "flex", alignItems: "center", gap: 10,
    padding: "10px 12px", background: "none", border: "none", cursor: "pointer",
    fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: COLORS.onSurface, textAlign: "left",
  };
  // App parity: the picker only offers solid + dashed (SelectLineStyleSheet);
  // dotted/dash_dot stay render-only for legacy documents.
  const pickableStyles: GeoLineStyle[] = ["solid", "dashed"];
  const styleLabel = (s: GeoLineStyle) => (s === "dashed" ? t('lines.styleDashed') : t('lines.styleSolid'));
  return (
    <>
      <div className="wh-field" style={{ margin: "0 12px 10px" }}>
        <label>{t('lines.nameLabel', { noun })}</label>
        <input
          className="wh-input"
          value={name}
          placeholder={t('lines.namePlaceholder', { noun })}
          onChange={(e) => onPatch({ name: e.target.value })}
        />
      </div>
      <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
        <button onClick={() => setOpen((v) => (v === "color" ? null : "color"))} style={rowBtn}>
          <span style={{ width: 16, height: 16, borderRadius: "50%", background: color, flexShrink: 0, border: "2px solid rgba(0,0,0,0.12)" }} />
          {t('lines.colorTitle', { noun })}
          <Icon name={open === "color" ? "chevron-down" : "chevron-right"} size={16} color={COLORS.outline} style={{ marginLeft: "auto" }} />
        </button>
        {open === "color" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 28px)", gap: 10, padding: "4px 12px 12px", justifyContent: "start" }}>
            {LINE_PICKER_COLORS.map((c) => {
              const isSelected = color.toLowerCase() === c.toLowerCase();
              return (
                <button
                  key={c}
                  aria-label={c}
                  onClick={() => { onPatch({ color: c }); setOpen(null); }}
                  style={{
                    width: 28, height: 28, borderRadius: "50%", background: c,
                    border: isSelected ? "3px solid white" : "2px solid rgba(0,0,0,0.12)",
                    boxShadow: isSelected ? `0 0 0 2px ${c}` : "none",
                    cursor: "pointer", padding: 0,
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
      <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
        <button onClick={() => setOpen((v) => (v === "style" ? null : "style"))} style={rowBtn}>
          <span style={{ color: COLORS.onSurface, display: "inline-flex" }}>
            <LineStyleIcon dashed={style === "dashed"} />
          </span>
          {t('lines.styleTitle', { noun })}
          <span style={{ marginLeft: "auto", fontWeight: 400, color: COLORS.outline }}>{styleLabel(style)}</span>
          <Icon name={open === "style" ? "chevron-down" : "chevron-right"} size={16} color={COLORS.outline} />
        </button>
        {open === "style" && (
          <div style={{ padding: "0 12px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
            {pickableStyles.map((s) => (
              <label key={s} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", cursor: "pointer", fontSize: 13, color: COLORS.onSurface }}>
                <LineStyleIcon dashed={s === "dashed"} />
                {styleLabel(s)}
                <input
                  type="radio"
                  className="wh-radio"
                  name="wh-line-style"
                  checked={style === s}
                  onChange={() => { onPatch({ style: s }); setOpen(null); }}
                  style={{ marginLeft: "auto" }}
                />
              </label>
            ))}
          </div>
        )}
      </div>
      <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
        <div style={{ padding: "10px 12px 0", fontSize: 13, fontWeight: 600, display: "flex" }}>
          {t('lines.thickness')}
          <span style={{ marginLeft: "auto", fontWeight: 400, color: COLORS.outline }}>{formatLineWidth(width)}</span>
        </div>
        <LineThicknessSlider
          value={width}
          onChange={(v) => onPatch({ width: v })}
          label={t('lines.thickness')}
          decreaseLabel={t('lines.thinner')}
          increaseLabel={t('lines.thicker')}
        />
      </div>
    </>
  );
}

function LinesPanel({
  kind,
  lines,
  onAdd,
  onEdit,
  onDelete,
  onCenterOn,
  collapsed,
  onToggleCollapse,
}: {
  kind: GeoLineKind;
  lines: GeoLine[];
  onAdd: () => void;
  onEdit: (l: GeoLine) => void;
  onDelete: (id: string) => void;
  onCenterOn: (l: GeoLine) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');

  const noun = kind === "arrow" ? t('lines.arrowNoun') : kind === "trail" ? t('lines.trailNoun') : t('lines.lineNoun');
  const Noun = kind === "arrow" ? t('lines.arrowNounCap') : kind === "trail" ? t('lines.trailNounCap') : t('lines.lineNounCap');
  const plural = kind === "arrow" ? t('lines.arrowPlural') : kind === "trail" ? t('lines.trailPlural') : t('lines.linePlural');
  const collapsedTitle = t('lines.emptyCollapsed', { count: lines.length, noun: lines.length === 1 ? noun : plural.toLowerCase() });
  return (
    <HudCard width={340} collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={plural} collapsedTitle={collapsedTitle}>
      <div style={{ padding: "16px 18px 8px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.outline, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
          {plural}
        </div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 22, fontWeight: 400, lineHeight: 1.15 }}>
          {lines.length === 0
            ? kind === "trail"
              ? t('lines.headingEmptyTrail')
              : t('lines.headingEmpty', { noun })
            : t('lines.headingCount', { count: lines.length, noun: lines.length === 1 ? noun : plural.toLowerCase() })}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 6, lineHeight: 1.5 }}>
          {lines.length === 0
            ? kind === "trail"
              ? t('lines.emptyTrail')
              : t('lines.empty', { plural: plural.toLowerCase() })
            : kind === "arrow"
            ? t('lines.descriptionArrow')
            : t('lines.descriptionLine')}
        </div>
      </div>
      {/* CLUB-528: trails are recorded (GPX import / a recorded drive), never
          drawn — the app has no "add trail" action either, so offering one
          here would invent a concept the product does not have. */}
      {kind !== "trail" && (
        <div style={{ padding: "4px 8px 8px" }}>
          <ActionRow icon="edit" title={t('lines.addNoun', { noun })} sub={t('lines.addSub')} onClick={onAdd} />
        </div>
      )}
      {lines.length > 0 && (
        <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
          <div style={{ padding: "10px 16px 6px", fontSize: 11, fontWeight: 700, color: COLORS.outline, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {t('lines.existing', { plural })}
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {lines.map((l, i) => {
              const displayName = l.name ?? `${Noun} ${i + 1}`;
              return (
                <div
                  key={l.id}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}
                >
                  <button
                    title={t('lines.centreOn', { noun })}
                    onClick={() => onCenterOn(l)}
                    style={{ ...listRowBodyStyle, display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <span style={{ flexShrink: 0, display: "inline-flex" }}>
                      <LineSample color={l.color ?? LINE_DEFAULT_COLOR} width={l.width ?? LINE_DEFAULT_WIDTH} style={l.style ?? "solid"} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: "block",
                          fontSize: 13, fontWeight: 600, color: COLORS.onSurface,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}
                      >
                        {displayName}
                      </span>
                      <span style={{ display: "block", fontSize: 11, color: COLORS.outline }}>
                        {t('lines.pts', { count: l.points.length })} · {formatLineLength(lineLengthMeters(l.points))}
                      </span>
                    </span>
                  </button>
                  {/* The accessible name is the same localized string as the hover
                      title. An icon-only button carries no text of its own, so a
                      hardcoded aria-label would be the ONLY name a screen reader
                      announces — reading English "edit" to a Finnish or German user
                      while every control around it is translated. */}
                  <button className="wh-btn-icon" onClick={() => onEdit(l)} title={t('lines.editAria', { noun })} aria-label={t('lines.editAria', { noun })}>
                    <Icon name="edit" size={20} />
                  </button>
                  <button
                    className="wh-btn-icon"
                    onClick={() => onDelete(l.id)}
                    title={t('lines.deleteAria', { noun })}
                    style={{ color: COLORS.error }}
                  >
                    <Icon name="trash" size={20} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </HudCard>
  );
}

function LineDrawHud({
  kind,
  draft,
  pointCount,
  lengthMeters,
  onChange,
  onCancel,
  onFinish,
  collapsed,
  onToggleCollapse,
}: {
  kind: GeoLineKind;
  draft: LineDraft;
  pointCount: number;
  lengthMeters: number;
  onChange: (patch: Partial<{ name: string; color: string; width: number; style: GeoLineStyle }>) => void;
  onCancel: () => void;
  onFinish: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  const noun = kind === "arrow" ? t('lines.arrowNoun') : kind === "trail" ? t('lines.trailNoun') : t('lines.lineNoun');
  const plural = kind === "arrow" ? t('lines.arrowPlural') : kind === "trail" ? t('lines.trailPlural') : t('lines.linePlural');
  // The helper walks the app flow: place first point → keep going → done.
  const helper =
    pointCount === 0 ? t('lines.helperFirst') : pointCount < 2 ? t('lines.helperSecond') : t('lines.helperDone', { noun });
  return (
    <HudCard width={340} collapsed={collapsed} onToggleCollapse={onToggleCollapse} collapsedTag={plural} collapsedTitle={t('lines.drawingCollapsed', { noun })}>
      <div style={{ padding: "16px 18px 10px" }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{t('lines.drawingTitle', { noun })}</div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>{helper}</div>
        {pointCount >= 2 && (
          <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{formatLineLength(lengthMeters)}</div>
        )}
      </div>
      <LineStyleControls noun={noun} name={draft.name} color={draft.color} width={draft.width} style={draft.style} onPatch={onChange} />
      <MapToolActions onCancel={onCancel} onDone={onFinish} doneDisabled={pointCount < 2} />
    </HudCard>
  );
}

function LineEditHud({
  kind,
  line,
  lineIndex,
  onPatch,
  onCancel,
  onDelete,
  onDone,
  collapsed,
  onToggleCollapse,
}: {
  kind: GeoLineKind;
  line: GeoLine;
  lineIndex: number;
  onPatch: (patch: Partial<GeoLine>) => void;
  onCancel: () => void;
  onDelete: () => void;
  onDone: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  const noun = kind === "arrow" ? t('lines.arrowNoun') : kind === "trail" ? t('lines.trailNoun') : t('lines.lineNoun');
  const Noun = kind === "arrow" ? t('lines.arrowNounCap') : kind === "trail" ? t('lines.trailNounCap') : t('lines.lineNounCap');
  const plural = kind === "arrow" ? t('lines.arrowPlural') : kind === "trail" ? t('lines.trailPlural') : t('lines.linePlural');
  const displayName = line.name ?? `${Noun} ${lineIndex + 1}`;
  return (
    <HudCard
      width={340}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      collapsedTag={plural}
      collapsedTitle={displayName}
      collapsedActions={
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            title={t('lines.deleteAria', { noun })}
            onClick={onDelete}
            className="wh-btn-icon"
            style={{ color: COLORS.error, height: 44, width: 40, borderRadius: 0, background: "none" }}
          >
            <Icon name="trash" size={20} />
          </button>
          <button
            title={t('toolActions.done')}
            onClick={onDone}
            className="wh-btn-icon"
            style={{ color: COLORS.primary, height: 44, width: 40, borderRadius: 0, background: "none" }}
          >
            <Icon name="check" size={20} />
          </button>
        </div>
      }
    >
      <div style={{ padding: "16px 18px 10px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: line.color ?? LINE_DEFAULT_COLOR, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
          {t('lines.editingTag', { noun })}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, lineHeight: 1.5 }}>{t('lines.editBody', { noun })}</div>
        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>
          {t('lines.pts', { count: line.points.length })} · {formatLineLength(lineLengthMeters(line.points))}
        </div>
      </div>
      <LineStyleControls
        noun={noun}
        name={line.name ?? ""}
        color={line.color ?? LINE_DEFAULT_COLOR}
        width={line.width ?? LINE_DEFAULT_WIDTH}
        style={line.style ?? "solid"}
        onPatch={(patch) => onPatch({ ...patch, name: "name" in patch ? patch.name || undefined : line.name })}
      />
      {/* CLUB-528: free-text note carried on the app's document — mobile has had
          this field all along (EditMapLinePage's description TextField), web gets
          an editor for it here. Mirrors mobile's shape: a hint-only field with no
          separate visible label — the copied-from-mobile "Add a comment" text is
          the placeholder. With no visible label, the aria-label is the field's only
          accessible name, so it is localized like everything else on this card:
          a hardcoded English one would be what a Finnish or German screen-reader
          user hears. */}
      <div className="wh-field" style={{ margin: "0 12px 10px", borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, paddingTop: 10 }}>
        <textarea
          className="wh-input"
          rows={2}
          aria-label={t('lines.descriptionAria')}
          placeholder={t('lines.descriptionLabel')}
          value={line.description ?? ""}
          onChange={(e) => onPatch({ description: e.target.value || undefined })}
        />
      </div>
      {/* CLUB-576: last row of the body, labelled with the type it removes. Line is where the
           old footer gave up and put Delete inline; it now ends its body with
           the same row as every other editor. */}
      <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, padding: "8px 8px" }}>
        <ActionRow
          icon="trash"
          title={t(DELETE_LABEL_KEY[kind])}
          onClick={onDelete}
          tone="destructive"
          chevron={false}
        />
      </div>
      <MapToolActions onCancel={onCancel} onDone={onDone} />
    </HudCard>
  );
}

function PinEditCard({
  obj,
  onChange,
  onCancel,
  onDelete,
  onDone,
  withBackground,
  collapsed,
  onToggleCollapse,
}: {
  obj: MapObject;
  onChange: (patch: Partial<MapObject>) => void;
  onCancel: () => void;
  onDelete: () => void;
  onDone: () => void;
  /** Mirrors `appearance.pinBackground` so the type selector shows the drawing
   *  the map will actually use. */
  withBackground: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  const [colorOpen, setColorOpen] = useState(false);
  const [typePicker, setTypePicker] = useState(false);
  const [symbolDialog, setSymbolDialog] = useState(false);
  const [hoveredPhotoIdx, setHoveredPhotoIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceIdxRef = useRef<number | null>(null);
  const pinColor = obj.color || pinDefaultColor(obj.type);

  const rowStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 10,
    padding: "11px 16px",
    borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
    background: "none", border: "none", width: "100%",
    cursor: "pointer", fontFamily: "inherit", textAlign: "left",
  };


  return (
    <HudCard
      width={320}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      collapsedTag={t('pinEdit.collapsedTag')}
      collapsedTitle={obj.label}
      collapsedActions={
        <div style={{ display: "flex", alignItems: "center" }}>
          <button title={t('toolActions.delete')} onClick={onDelete} className="wh-btn-icon"
            style={{ color: COLORS.error, height: 44, width: 40, borderRadius: 0, background: "none" }}>
            <Icon name="trash" size={20} />
          </button>
          <button title={t('toolActions.done')} onClick={onDone} className="wh-btn-icon"
            style={{ color: COLORS.primary, height: 44, width: 40, borderRadius: 0, background: "none" }}>
            <Icon name="check" size={20} />
          </button>
        </div>
      }
    >
      {/* ── Tag ───────────────────────────────────────────────────── */}
      <div style={{ padding: "12px 16px 8px", fontSize: 11, fontWeight: 700, color: pinColor, textTransform: "uppercase", letterSpacing: 1 }}>
        {t('pinEdit.editing', { type: catalog.label(obj.type).toLowerCase() })}
      </div>

      {/* ── Photos ────────────────────────────────────────────────── */}
      {(() => {
        const photos = obj.photos ?? [];
        const THUMB = 100;

        const triggerAdd = () => {
          replaceIdxRef.current = null;
          fileInputRef.current?.click();
        };
        const triggerReplace = (idx: number) => {
          replaceIdxRef.current = idx;
          fileInputRef.current?.click();
        };
        const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (!file) return;
          // A newly picked file is staged as a NEW image (bytes + a local preview
          // URL); the save uploads it and mints its stored id. Replacing swaps the
          // entry at that index — dropping whatever was there (an existing id or an
          // earlier staged file), so a replaced existing picture is treated as
          // removed on save.
          const staged: MapPinImage = { file, url: URL.createObjectURL(file) };
          const next = [...photos];
          if (replaceIdxRef.current !== null) {
            next[replaceIdxRef.current] = staged;
          } else {
            next.push(staged);
          }
          onChange({ photos: next });
          replaceIdxRef.current = null;
          e.target.value = "";
        };

        const circleBtn: React.CSSProperties = {
          position: "absolute", bottom: 7, width: 30, height: 30,
          borderRadius: "50%", background: "#fff", border: "none",
          cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
        };

        return (
          <div style={{ padding: "0 16px 12px" }}>
            <input ref={fileInputRef} type="file" accept="image/*"
              style={{ display: "none" }} onChange={handleFileChange} />

            {photos.length === 0 ? (
              /* ── no photos yet: full-width add button ── */
              <button
                onClick={triggerAdd}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "11px 0", borderRadius: 8, border: `1.5px solid ${COLORS.primary}`, background: "none", color: COLORS.primary, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
              >
                <Icon name="add_a_photo" size={20} />
                {t('pinEdit.addPicture')}
              </button>
            ) : (
              /* ── photo strip ── */
              <div style={{ overflowX: "auto", margin: "0 -16px", padding: "0 16px 2px" }}>
                <div style={{ display: "flex", gap: 8, width: "max-content", paddingRight: 16 }}>

                  {photos.map((img, i) => (
                    <div
                      key={i}
                      style={{ position: "relative", flexShrink: 0, width: THUMB, height: THUMB, borderRadius: 10, overflow: "hidden" }}
                      onMouseEnter={() => setHoveredPhotoIdx(i)}
                      onMouseLeave={() => setHoveredPhotoIdx(null)}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={img.url} alt={t('pinEdit.pinPhotoAlt', { index: i + 1 })}
                        style={{ width: THUMB, height: THUMB, objectFit: "cover", display: "block" }} />

                      {/* hover overlay + action buttons */}
                      <div style={{
                        position: "absolute", inset: 0,
                        background: hoveredPhotoIdx === i ? "rgba(0,0,0,0.22)" : "transparent",
                        transition: "background 0.12s",
                        pointerEvents: hoveredPhotoIdx === i ? "auto" : "none",
                      }}>
                        <button title={t('pinEdit.replacePhoto')} onClick={() => triggerReplace(i)}
                          style={{ ...circleBtn, left: 7, opacity: hoveredPhotoIdx === i ? 1 : 0, transition: "opacity 0.12s" }}>
                          <Icon name="edit" size={14} color={COLORS.onSurface} />
                        </button>
                        <button title={t('pinEdit.removePhoto')}
                          onClick={() => onChange({ photos: photos.filter((_, j) => j !== i) })}
                          style={{ ...circleBtn, right: 7, opacity: hoveredPhotoIdx === i ? 1 : 0, transition: "opacity 0.12s" }}>
                          <Icon name="trash" size={14} color={COLORS.error} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add picture tile */}
                  <button
                    onClick={triggerAdd}
                    title={t('pinEdit.addPicture')}
                    style={{ flexShrink: 0, width: THUMB, height: THUMB, borderRadius: 10, border: `1.5px dashed ${COLORS.primary}`, background: "none", color: COLORS.primary, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, fontFamily: "inherit", fontWeight: 600, fontSize: 11 }}
                  >
                    <Icon name="add_a_photo" size={22} />
                    {t('pinEdit.addPicture')}
                  </button>

                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Name ──────────────────────────────────────────────────── */}
      <div style={{ padding: "0 16px 12px" }}>
        <div className="wh-field">
          <label>{t('pinEdit.name')}</label>
          <input className="wh-input" value={obj.label} onChange={(e) => onChange({ label: e.target.value })} />
        </div>
      </div>

      {/* ── Color ─────────────────────────────────────────────────── */}
      <button style={rowStyle} onClick={() => { setColorOpen((v) => !v); setTypePicker(false); }}>
        <span style={{ flex: 1, fontSize: 13, color: COLORS.onSurface }}>{t('pinEdit.color')}</span>
        <div style={{ width: 22, height: 22, borderRadius: "50%", background: pinColor, border: "2px solid rgba(0,0,0,0.1)", flexShrink: 0 }} />
        <Icon name="chevron-right" size={16} color={COLORS.outline} style={{ transform: colorOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
      </button>
      {colorOpen && (
        <div style={{ padding: "8px 16px 14px", borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10 }}>
          {PIN_COLORS.map((c) => (
            <button key={c} onClick={() => onChange({ color: c })}
              style={{ width: 32, height: 32, borderRadius: "50%", background: c, border: c.toLowerCase() === pinColor.toLowerCase() ? `3px solid ${COLORS.onSurface}` : "2.5px solid transparent", boxShadow: c.toLowerCase() === pinColor.toLowerCase() ? `0 0 0 1.5px ${c}` : "none", cursor: "pointer", outline: "none" }}
            />
          ))}
        </div>
      )}

      {/* ── Description ───────────────────────────────────────────── */}
      <div style={{ padding: "10px 16px 2px", borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
        <textarea
          placeholder={t('pinEdit.descriptionPlaceholder')}
          maxLength={120}
          rows={3}
          value={obj.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
          style={{ width: "100%", resize: "none", border: `1.5px solid ${COLORS.surfaceContainerHighest}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", color: COLORS.onSurface, background: COLORS.surfaceContainerLowest, outline: "none", boxSizing: "border-box", lineHeight: 1.5 }}
        />
        <div style={{ textAlign: "right", fontSize: 11, color: COLORS.outline, marginBottom: 6 }}>
          {(obj.description || "").length}/120
        </div>
      </div>

      {/* ── Pin type ──────────────────────────────────────────────── */}
      <button style={rowStyle} onClick={() => { setTypePicker((v) => !v); setColorOpen(false); }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={pinSrcFor(obj.type, withBackground)} width={22} height={22} alt="" style={{ objectFit: "contain", flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 13, color: COLORS.onSurface }}>{t('pinEdit.mapPinType')}</span>
        <span style={{ fontSize: 13, color: COLORS.primary, fontWeight: 500 }}>{catalog.label(obj.type)}</span>
        <Icon name="chevron-right" size={16} color={COLORS.outline} style={{ transform: typePicker ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
      </button>
      {typePicker && (
        <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, padding: "10px 16px 12px", maxHeight: 230, overflowY: "auto" }}>
          <PinTypePalette
            withBackground={withBackground}
            tileWidth={52}
            iconSize={24}
            activeType={obj.type}
            onPick={(entry) => {
              if (entry.id === 'custom') { setSymbolDialog(true); return; }
              onChange({ type: entry.id });
              setTypePicker(false);
            }}
          />
        </div>
      )}
      {/* Shown only for a `custom` pin; opens the symbol dialog to change it. */}
      {obj.type === 'custom' && (
        <button style={rowStyle} onClick={() => setSymbolDialog(true)}>
          <span style={{ width: 22, textAlign: 'center', fontSize: 18 }}>{obj.customSymbol ?? DEFAULT_CUSTOM_PIN_SYMBOL}</span>
          <span style={{ flex: 1, fontSize: 13, color: COLORS.onSurface }}>{t('pinEdit.symbol')}</span>
          <Icon name="chevron-right" size={16} color={COLORS.outline} />
        </button>
      )}

      {/* ── Position ──────────────────────────────────────────────── */}
      <div style={{ ...rowStyle, cursor: "default" }}>
        <span style={{ flex: 1, fontSize: 13, color: COLORS.onSurface }}>{t('pinEdit.position')}</span>
        <span style={{ fontSize: 12, color: COLORS.primary, fontFamily: "ui-monospace, Menlo, monospace" }}>
          {obj.lat.toFixed(4)}, {obj.lon.toFixed(4)}
        </span>
      </div>

      {/* ── Footer ────────────────────────────────────────────────── */}
      {/* CLUB-576: last row of the body, labelled with the type it removes. */}
      <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, padding: "8px 8px" }}>
        <ActionRow
          icon="trash"
          title={t('toolActions.deletePin')}
          onClick={onDelete}
          tone="destructive"
          chevron={false}
        />
      </div>
      <MapToolActions onCancel={onCancel} onDone={onDone} />
      {symbolDialog && (
        <PinSymbolDialog
          initial={obj.customSymbol ?? DEFAULT_CUSTOM_PIN_SYMBOL}
          onConfirm={(symbol) => { onChange({ type: 'custom', customSymbol: symbol }); setSymbolDialog(false); setTypePicker(false); }}
          onCancel={() => setSymbolDialog(false)}
        />
      )}
    </HudCard>
  );
}

/**
 * The catalog as one flat grid, every type visible at once: the top-level
 * types first, then each catalog group under its own heading, all in catalog
 * order. Mobile's picker drills into a group on a second page because a phone
 * has no room for forty tiles; a desktop editor does, and a single scan beats
 * two taps. Same set, same names, same order as mobile — different layout.
 * Used by the pin drawer (placement) and the edit card (retyping).
 */
function PinTypePalette({
  withBackground,
  tileWidth,
  iconSize,
  activeType,
  onPick,
}: {
  withBackground: boolean;
  tileWidth: number;
  iconSize: number;
  activeType?: string;
  onPick: (entry: PinTypeEntry) => void;
}) {
  const catalog = usePinTypeCatalog();
  const pickable = (entries: readonly PinTypeEntry[]) =>
    entries.filter((entry) => entry.members.length === 0);
  const sections: { key: string; heading: string | null; entries: PinTypeEntry[] }[] = [
    { key: 'top-level', heading: null, entries: pickable(catalog.entries) },
    ...catalog.entries
      .filter((entry) => entry.members.length > 0)
      .map((group) => ({ key: group.id, heading: catalog.label(group.id), entries: pickable(group.members) })),
  ].filter((section) => section.entries.length > 0);
  return (
    <div>
      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: 12 }}>
          {section.heading && (
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: COLORS.outline, marginBottom: 7 }}>
              {section.heading}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {section.entries.map((entry) => {
              const active = entry.id === activeType;
              return (
                <button
                  key={entry.id}
                  type="button"
                  data-testid={`pin-palette-tile-${entry.id}`}
                  data-type={entry.id}
                  data-section={section.key}
                  title={catalog.label(entry.id)}
                  onClick={() => onPick(entry)}
                  style={{ width: tileWidth, padding: "8px 4px 6px", borderRadius: 8, border: `1.5px solid ${active ? COLORS.primary : COLORS.surfaceContainerHighest}`, background: active ? COLORS.primaryOpacity8 : COLORS.surfaceContainerLowest, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={pinSrcFor(entry.id, withBackground)} width={iconSize} height={iconSize} alt="" style={{ objectFit: "contain" }} />
                  <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.onSurface, textAlign: "center", lineHeight: 1.2, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {catalog.label(entry.id)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Pin drawer (bottom sheet) ──────────────────────────────────────────

function PinDrawer({
  placedSession,
  onPlaceMode,
  onSavePlaced,
  onRemovePlaced,
  withBackground,
  onClose,
}: {
  placedSession: MapObject[];
  onPlaceMode: (pm: PlaceMode) => void;
  onSavePlaced: (id: string, patch: Partial<MapObject>) => void;
  onRemovePlaced: (id: string) => void;
  /** Mirrors `appearance.pinBackground` so the picker shows the drawing the map
   *  will actually use once the pin is placed. */
  withBackground: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  const [multi, setMulti] = useState(true);
  const [pendingCustom, setPendingCustom] = useState(false);
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        background: COLORS.surfaceContainerLowest,
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
        boxShadow: "0 -10px 24px rgba(0,0,0,0.35)",
        zIndex: 5,
        maxHeight: "55%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "12px 18px",
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{t('pinDrawer.title')}</div>
          <div style={{ fontSize: 12, color: COLORS.outline }}>
            {t('pinDrawer.subtitle')}
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: COLORS.outline }}>
          <input
            type="checkbox"
            checked={multi}
            onChange={(e) => setMulti(e.target.checked)}
            className="wh-checkbox"
          />
          {t('pinDrawer.placeMultiple')}
        </label>
        <button className="wh-btn-icon" onClick={onClose}>
          <Icon name="close" size={20} />
        </button>
      </div>

      <div style={{ overflowY: "auto", padding: "12px 16px", flex: 1 }}>
        <PinTypePalette
          withBackground={withBackground}
          tileWidth={64}
          iconSize={28}
          onPick={(entry) => {
            if (entry.id === 'custom') { setPendingCustom(true); return; }
            onPlaceMode({ type: entry.id, label: catalog.label(entry.id), multi });
          }}
        />
      </div>
      {pendingCustom && (
        <PinSymbolDialog
          initial={DEFAULT_CUSTOM_PIN_SYMBOL}
          onConfirm={(symbol) => {
            setPendingCustom(false);
            onPlaceMode({ type: 'custom', label: catalog.label('custom'), multi, customSymbol: symbol });
          }}
          onCancel={() => setPendingCustom(false)}
        />
      )}

      {placedSession.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.outline,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 8,
            }}
          >
            {t('pinDrawer.justPlaced', { count: placedSession.length })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {placedSession.map((o) => {
              return (
                <div
                  key={o.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: `1px solid ${COLORS.surfaceContainerHighest}`,
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={pinSrcFor(o.type, withBackground)} width={24} height={24} alt="" style={{ objectFit: "contain", flexShrink: 0 }} />
                  <input
                    value={o.label}
                    onChange={(e) => onSavePlaced(o.id, { label: e.target.value })}
                    style={{
                      flex: 1,
                      border: "none",
                      background: "transparent",
                      fontSize: 13,
                      fontWeight: 600,
                      outline: "none",
                    }}
                  />
                  <button
                    className="wh-btn-icon"
                    onClick={() => onRemovePlaced(o.id)}
                    title={t('toolActions.delete')}
                    style={{ color: COLORS.error }}
                  >
                    <Icon name="trash" size={20} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
